import React, { useState, useEffect, useRef, useMemo, useImperativeHandle, forwardRef } from "react";
import {
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Snackbar,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ListItemText,
  Checkbox,
  IconButton,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import axios from "axios";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";

// Import common styles
import { inputFieldStyles, commonInlineStyles } from "../../styles";
import { appUrl } from "../../../../../../../utils/config";
import { useLocation } from "react-router-dom";
import { useIsMounted } from "../../../../../hooks/useIsMounted";
import MuiAlert from "@material-ui/lab/Alert";
// This component is a step in a multi-step form wizard for configuring product details
// It allows users to enter information about pharmaceutical products including codes, descriptions, and classifications

// Helper function to generate unique material ID
function generateMaterialId() {
  return `material_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Helper function to ensure material has a unique ID
function ensureMaterialId(material) {
  if (!material._materialId) {
    material._materialId = material.id || generateMaterialId();
  }
  return material;
}

// Helper function to check if product is a file uploaded product
function checkFileUploadedProduct(formData) {
  const hasMaterialProducts = formData?.materialDetails?.productMaterialsList && 
                             formData.materialDetails.productMaterialsList.length > 0;
  const isFgStatusesEmpty = !formData?.fgStatuses || 
                           Object.keys(formData.fgStatuses).length === 0;
  return hasMaterialProducts && isFgStatusesEmpty;
}

// Helper function to convert allocation config trigger values from status names to IDs
function convertAllocationConfigTriggerValues(allocationConfig, poStatuses) {
  if (!allocationConfig || !poStatuses || !Array.isArray(poStatuses)) {
    return allocationConfig;
  }

  const convertStatusNameToId = (statusName) => {
    if (!statusName || statusName === "NA") return statusName;
    const status = poStatuses.find(s => s.status_name === statusName);
    return status ? status.status_id : statusName;
  };

  return {
    ...allocationConfig,
    sponsorTriggerStartValue: (allocationConfig.sponsorTriggerStart === "poStatus" || allocationConfig.sponsorTriggerStart === "PO Status") 
      ? convertStatusNameToId(allocationConfig.sponsorTriggerStartValue) 
      : allocationConfig.sponsorTriggerStartValue,
    sponsorTriggerEndValue: (allocationConfig.sponsorTriggerEnd === "poStatus" || allocationConfig.sponsorTriggerEnd === "PO Status")
      ? convertStatusNameToId(allocationConfig.sponsorTriggerEndValue)
      : allocationConfig.sponsorTriggerEndValue,
    cmoTriggerStartValue: (allocationConfig.cmoTriggerStart === "poStatus" || allocationConfig.cmoTriggerStart === "PO Status")
      ? convertStatusNameToId(allocationConfig.cmoTriggerStartValue)
      : allocationConfig.cmoTriggerStartValue,
    cmoTriggerEndValue: (allocationConfig.cmoTriggerEnd === "poStatus" || allocationConfig.cmoTriggerEnd === "PO Status")
      ? convertStatusNameToId(allocationConfig.cmoTriggerEndValue)
      : allocationConfig.cmoTriggerEndValue
  };
}

// Helper function to check if a CMO material row is additional (not linked to sponsor)
function isAdditionalCmoRow(cmoIndex, sponsorListLength) {
  return cmoIndex >= sponsorListLength;
}

// Helper function to check if a sponsor material code exists in the material details list
function isSponsorMaterialCodeValid(materialCode, materialDetailsList) {
  if (!materialCode || !materialDetailsList || materialDetailsList.length === 0) {
    return false;
  }
  return materialDetailsList.some(m => m.bo_material_code === materialCode);
}

// Error message constants
const ERROR_MESSAGES = {
  SPONSOR_MATERIAL_CODE_DELETED: "This Sponsor Material Code has been deleted. Please select a different one.",
};

const useStyles = makeStyles((theme) => ({
  orangeRadio: {
    color: "#B0B0B0 !important",
    "&.Mui-checked": {
      color: "#EC6535 !important",
    },
  },
}));

const ProductDetails = forwardRef(({
  formData, // Current form data from parent component
  updateStepData, // Function to update parent component's form data
  classes, // CSS classes for styling
  readOnly = false, // If true, renders form in read-only mode
  handleNext, // Function to navigate to next step
  handleBack, // Function to navigate to previous step
  handleSubmit, // Function to submit the entire form (for final step)
  activeStep, // Current active step in the wizard
  stepsLength, // Total number of steps in the wizard
  onValidationChange = () => {},
  onValidationError = () => {}, // Callback to pass validation errors to parent
  skipReplicateDialog,
  setSkipReplicateDialog,
  isProductDetailsEditMode,
  markApiCallSuccessful, // Function to mark API call as successful
  materialDetails: propMaterialDetails, // Material details passed as prop
  originalMaterialCodesRef: propOriginalMaterialCodesRef, // Optional ref from parent to track material code changes across remounts
}, ref) => {
  const location = useLocation();
  const classesLocal = useStyles();
  const theme = useTheme();

  // All useState hooks must come first
  const selectedProductType = location.state?.selectedSupplytype || {};
  const selectedProductDetails = location.state?.productDetails || {};

 
   const base = formData?.processFlowConfiguration || {};
  const [stepData, setStepData] = useState(
    formData?.productDetails
      ? {
          ...formData.productDetails,
          supplyTypeID:
            formData.productDetails.supplyTypeID ??
            selectedProductType?.supplyTypeID ??
            "",
          cmoPlantId:
            formData?.productDetails?.vendorMosymphonyID &&
            formData?.productDetails?.vendorName 
              ? `${formData.productDetails.vendorMosymphonyID}|${formData.productDetails.vendorName}|${formData.productDetails.vendorPlant || ""}`
              : "",
          // sponsorCQAApprovalReq: formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? false,
        }
      : {
          productCode: selectedProductDetails?.productCode ?? "",
          productDescription: selectedProductDetails?.productDescription ?? "",
          brand: selectedProductDetails?.brand ?? "",
          status: selectedProductDetails?.status ?? "",
          supplyTypeID: selectedProductType?.supplyTypeID ?? "",
          productTypeID: selectedProductDetails?.productTypeID ?? "",
          facilityTypeID: selectedProductDetails?.facilityTypeID ?? "",
          drugTypeID: selectedProductDetails?.drugTypeID ?? "",
          vendorName: selectedProductDetails?.vendorName ?? "",
          // sponsorCQAApprovalReq: formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
          //                       location.state?.selectedProductFormData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
          //                       false,
          vendorMosymphonyID: selectedProductDetails?.vendorMosymphonyID ?? "",
          // vendorPlant: selectedProductDetails?.vendorPlant ?? "",
          productModality: selectedProductDetails?.drugTypeName ?? "",
          productTypeName: selectedProductDetails?.productTypeName ?? "",
          manufacturingFacilityType:
            selectedProductDetails?.facilityTypeName ?? "",
          receivingStorageLocationIDsFromSponsorERP: (() => {
            const value =
              selectedProductDetails?.receivingStorageLocationIDsFromSponsorERP;
            if (Array.isArray(value)) return value;
            if (typeof value === "string" && value)
              return value
                .split(",")
                .map((item) => item.trim())
                .filter((item) => item !== "");
            return [];
          })(),
          receivingPlantIDFromSponsorERP:
            selectedProductDetails?.receivingPlantIDFromSponsorERP ?? "",
          sponsorUOM: selectedProductDetails?.sponsorUOM ?? "",
          cmoUOM: selectedProductDetails?.cmoUOM ?? "",
          cmoPlantId:
            (selectedProductDetails?.cmoPlantId ??
              `${selectedProductDetails?.vendorMosymphonyID}|${selectedProductDetails?.vendorName}|${selectedProductDetails?.vendorPlant || ""}`) ||
            "",
          cmoProductCode: selectedProductDetails?.cmoProductCode ?? "",
          cmoProductDescription:
            selectedProductDetails?.cmoProductDescription ?? "",
          manufactureSupplyType:
            selectedProductDetails?.manufactureSupplyType ?? "",
          pendingConfiguration: true,
          collabrixProductID: selectedProductDetails?.collabrixProductID ?? "",
        }
  );


    // const selectedQARequired=location.state?.selectedProductFormData?.processFlowConfiguration?.sponsorCQAApprovalReq || formData?.processFlowConfiguration?.sponsorCQAApprovalReq;




  const [apiErrors, setApiErrors] = useState({});
  const productAddedOnce =
    sessionStorage.getItem("productAddedOnce") === "true";
  const editPendingStatus = location.state?.isPending && location.state?.isEdit;
  
  // State for CMO Product Details List
  const [cmoProductDetailsList, setCmoProductDetailsList] = useState([
    { cmoProductCode: "", cmoProductDescription: "", cmoUOM: "" }
  ]);
  const [cmoProductErrors, setCmoProductErrors] = useState({});
  const [cmoTableError, setCmoTableError] = useState("");
  const [hasValidationAttempted, setHasValidationAttempted] = useState(false);
  
  // State for Material Details List
  const [materialDetailsList, setMaterialDetailsList] = useState([]);
  const [materialDetailsErrors, setMaterialDetailsErrors] = useState({});
  const [materialTableError, setMaterialTableError] = useState("");
  
  // State for CMO Material Details List (separate from sponsor materials)
  const [cmoMaterialDetailsList, setCmoMaterialDetailsList] = useState([]);
  const [cmoMaterialDetailsErrors, setCmoMaterialDetailsErrors] = useState({});
  
  // State to track deleted materials (for marking as is_material_active: 1 in payload)
  const [deletedMaterials, setDeletedMaterials] = useState([]);
  
  // State to track materials with changed codes (for marking old as is_material_active: 0 in payload)
  const [materialsWithChangedCodes, setMaterialsWithChangedCodes] = useState([]);
  
  // State for material dropdown options
  const [materialTypeOptions, setMaterialTypeOptions] = useState([]);
  const [materialBrandOptions, setMaterialBrandOptions] = useState([]);
  const [materialPlantIDOptions, setMaterialPlantIDOptions] = useState([]);
  const [materialStorageLocationIDOptions, setMaterialStorageLocationIDOptions] = useState([]);
  
  // Ref to track if we're currently updating to avoid infinite loops
  const syncTimerRef = useRef(null);
  
  // Ref to store initial material details for change detection
  const initialMaterialDetailsRef = useRef(null);
  
  // Ref to store original material codes for each row to detect changes
  // Use prop ref if provided (for ViewProductEdit), otherwise create local ref (for Stepper)
  const localOriginalMaterialCodesRef = useRef([]);
  const originalMaterialCodesRef = propOriginalMaterialCodesRef || localOriginalMaterialCodesRef;
  
  // Ref to store latest material details immediately (bypasses React state batching)
  // This ensures forceSyncMaterialDetails always reads the LATEST data
  const latestMaterialDetailsListRef = useRef([]);
  const latestCmoMaterialDetailsListRef = useRef([]);
  
  // Move all variables used in useEffect dependencies to the top
  const selectedProduct = location.state?.selectedProduct || {};

  const [updateModeData, setUpdateModeData] = useState({});
  const [plantIDStorageID, setPlantIDStorageID] = useState({});
  const [isInitialSaveSuccess, setIsInitialSaveSuccess] = useState(() => {
    const savedValue = sessionStorage.getItem("addedProductDetails");
    return savedValue === "true";
  });
  const selectedCollabrixID = selectedProductDetails.collabrixProductID;
  const viewOnlyProductDetails = location.state?.viewProductDetails;
  const isNotPending = location.state?.isEdit && location.state?.isNotPending;
  const addNewProductConfig = location.state?.addNewProductConfig;

  // Add state for material details
  const [materialDetails, setMaterialDetails] = useState({
    productMaterialsList: [],
  });

  // Update material details from formData or prop when available
  // Use a ref to track if we've initialized to prevent resetting additional CMO rows
  const hasInitializedMaterialsRef = useRef(false);
  
  // Helper function to add unique values to dropdown options
  const addUniqueToOptions = (currentOptions, newValues) => {
    const uniqueValues = [...new Set([...currentOptions, ...newValues.filter(v => v && v.trim())])];
    return uniqueValues;
  };
  
  // Expose forceSyncMaterialDetails function to parent via ref
  useImperativeHandle(ref, () => ({
    forceSyncMaterialDetails: () => {
      // Clear any pending debounced sync
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
      
      
      // Use refs instead of state to get LATEST data (bypasses React batching)
      // Prefer ref if it has been set (not empty array), otherwise use state
      const currentMaterialList = (latestMaterialDetailsListRef.current && latestMaterialDetailsListRef.current.length > 0)
        ? latestMaterialDetailsListRef.current 
        : materialDetailsList;
      const currentCmoList = (latestCmoMaterialDetailsListRef.current && latestCmoMaterialDetailsListRef.current.length > 0)
        ? latestCmoMaterialDetailsListRef.current 
        : cmoMaterialDetailsList;
      

      
      // Sync current material details to formData immediately
      const mergedList = currentMaterialList.map((sponsorItem, idx) => {
        return {
          ...sponsorItem,
          cmo_product_code: currentCmoList[idx]?.cmo_product_code || "",
          cmo_material_code: currentCmoList[idx]?.cmo_material_code || "",
          cmo_material_description: currentCmoList[idx]?.cmo_material_description || "",
          cmo_material_UOM: currentCmoList[idx]?.cmo_material_UOM || "",
          is_material_active: 0, // Active materials
          material_type_id: sponsorItem.material_type_id, // Explicitly preserve material_type_id
          _materialId: sponsorItem._materialId, // Preserve unique ID
          _isNewMaterial: sponsorItem._isNewMaterial // Preserve new material flag
        };
      });
      
      const additionalCmoRows = currentCmoList.slice(currentMaterialList.length)
        .filter(cmoRow => {
          return cmoRow.bo_material_code?.trim() || 
                 cmoRow.material_description?.trim() || 
                 cmoRow.cmo_product_code?.trim() ||
                 cmoRow.cmo_material_code?.trim() || 
                 cmoRow.cmo_material_description?.trim() || 
                 cmoRow.cmo_material_UOM?.trim();
        })
        .map(cmoRow => ({
          _materialId: cmoRow._materialId || cmoRow.id, // Preserve unique ID
          _isNewMaterial: cmoRow._isNewMaterial, // Preserve new material flag
          _isExistingMaterial: cmoRow._isExistingMaterial, // Preserve existing material flag
          _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
          sponsorSupplyingMaterial: false,
          material_brand: "",
          materialTypeName: null, // Set to null for payload (API requirement)
          material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : null,
          bo_material_code: cmoRow.bo_material_code || "",
          material_description: cmoRow.material_description || "",
          cmo_product_code: cmoRow.cmo_product_code || "",
          cmo_material_code: cmoRow.cmo_material_code || "",
          cmo_material_description: cmoRow.cmo_material_description || "",
          cmo_material_UOM: cmoRow.cmo_material_UOM || "",
          is_material_active: 0, // Active materials
        }));
      
      const allMaterials = [...mergedList, ...additionalCmoRows];
      
      // Call updateStepData to sync with parent
      updateStepData("productDetails", {
        ...stepData,
        productMaterialsList: allMaterials,
      });
      
      return allMaterials;
    }
  }));
  
  useEffect(() => {
    // Skip updates when already initialized AND still in edit mode
    // This prevents overwriting refs with stale formData during editing
    // BUT allow re-initialization when switching to readOnly (to display saved data)
    if (hasInitializedMaterialsRef.current && !readOnly) {
      return; // Skip if already initialized and in edit mode
    }
    
    
    const materialDetailsToUse = propMaterialDetails || formData?.materialDetails;
    if (materialDetailsToUse) {
      // Separate deleted materials (is_material_active: 1) from active materials for state management
      const deletedMaterialsFromAPI = materialDetailsToUse.productMaterialsList 
        ? materialDetailsToUse.productMaterialsList.filter(material => material.is_material_active === 1)
        : [];
      
      // Preserve deleted materials from API in deletedMaterials state
      if (deletedMaterialsFromAPI.length > 0) {
        setDeletedMaterials(deletedMaterialsFromAPI.map(material => ensureMaterialId(material)));
      }
      
      // Filter out deleted materials (is_material_active: 1), ensure unique IDs, and sort - materials with materialTypeName first, then CMO-only
      const sortedMaterialDetails = materialDetailsToUse.productMaterialsList 
        ? {
            ...materialDetailsToUse,
            productMaterialsList: [...materialDetailsToUse.productMaterialsList]
              .filter(material => material.is_material_active !== 1) // Exclude deleted materials
              .map(material => {
                const materialWithId = ensureMaterialId(material);
                // Mark materials from API as existing (not new) by checking is_material_active field presence
                if (!materialWithId._isNewMaterial && material.is_material_active !== undefined) {
                  materialWithId._isExistingMaterial = true;
                }
                return materialWithId;
              }) // Ensure each material has a unique ID and mark as existing
              .sort((a, b) => {
                const aHasType = a.materialTypeName && a.materialTypeName.trim() !== "";
                const bHasType = b.materialTypeName && b.materialTypeName.trim() !== "";
                
                if (aHasType && !bHasType) return -1;
                if (!aHasType && bHasType) return 1;
                return 0;
              })
          }
        : materialDetailsToUse;
      
      setMaterialDetails(sortedMaterialDetails);
      // Store initial material details for change detection if not already set
      if (!initialMaterialDetailsRef.current) {
        initialMaterialDetailsRef.current = JSON.parse(JSON.stringify(sortedMaterialDetails));
      }
      // Initialize material lists for both edit mode and read-only mode to ensure display after save
      if (materialDetailsToUse.productMaterialsList) {
        // Filter out deleted materials, ensure unique IDs, and sort: materials with materialTypeName first, then CMO-only materials
        const activeMaterials = materialDetailsToUse.productMaterialsList
          .filter(material => material.is_material_active !== 1) // Exclude deleted materials
          .map(material => {
            const materialWithId = ensureMaterialId(material);
            // Mark materials from API as existing (not new)
            if (!materialWithId._isNewMaterial && material.is_material_active !== undefined) {
              materialWithId._isExistingMaterial = true;
            }
            return materialWithId;
          }); // Ensure each material has a unique ID and mark as existing
        
        // Track which bo_material_codes have been seen to identify duplicates
        // IMPORTANT LOGIC FOR HANDLING DUPLICATE bo_material_code:
        // - First occurrence with materialTypeName = sponsor material (linked to both sponsor and CMO tables)
        // - Subsequent occurrences with same bo_material_code = CMO-only materials (no link to sponsor table)
        // Example: If bo_material_code "2342" appears twice, only the FIRST record is sponsor-linked,
        //          the second and any additional records are CMO-only materials
        const seenBoMaterialCodes = new Set();
        
        const allMaterials = activeMaterials.length > 0 
          ? [...activeMaterials].map(material => {
              const hasMaterialType = material.materialTypeName && material.materialTypeName.trim() !== "";
              const boCode = material.bo_material_code;
              
              // Check if this bo_material_code has been seen before
              const isFirstOccurrence = !seenBoMaterialCodes.has(boCode);
              
              // Mark material as sponsor-linked only if:
              // 1. It has a materialTypeName AND
              // 2. It's the first occurrence of this bo_material_code
              if (hasMaterialType && boCode && isFirstOccurrence) {
                seenBoMaterialCodes.add(boCode);
                material._isSponsorLinked = true;
              } else {
                material._isSponsorLinked = false;
              }
              
              // Convert materialTypeName to material_type_id using availableMaterialTypes
              // This is critical because API returns materialTypeName (string) but we need material_type_id (numeric ID)
              if (hasMaterialType && availableMaterialTypes && availableMaterialTypes.length > 0) {
                const matchingType = availableMaterialTypes.find(
                  mt => mt.materialTypeName === material.materialTypeName
                );
                if (matchingType) {
                  material.material_type_id = matchingType.materialTypeID;
                }
              }
              
              return material;
            }).sort((a, b) => {
              // Sponsor-linked materials come first, then CMO-only materials
              if (a._isSponsorLinked && !b._isSponsorLinked) return -1;
              if (!a._isSponsorLinked && b._isSponsorLinked) return 1;
              return 0; // Keep original order within each group
            })
          : [{
              _materialId: generateMaterialId(),
              sponsorSupplyingMaterial: true,
              material_brand: "",
              materialTypeName: "",
              material_type_id: "",
              bo_material_code: "",
              material_description: "",
              bo_material_UOM: "",
              materialPlantIDFromSponsorERP: "",
              materialStorageLocationIDsFromSponsorERP: "",
              cmo_product_code: "",
              cmo_material_code: "",
              cmo_material_description: "",
              cmo_material_UOM: "",
              _isSponsorLinked: true
            }];
        
        // Filter materials for Sponsor table - only sponsor-linked materials (first occurrence with materialTypeName)
        const sponsorMaterialsList = allMaterials.filter(item => item._isSponsorLinked === true);
        
        // Set sponsor materials list (only sponsor-linked materials - first occurrence with Material Type)
        setMaterialDetailsList(sponsorMaterialsList.length > 0 ? sponsorMaterialsList : [{
          _materialId: generateMaterialId(),
          sponsorSupplyingMaterial: true,
          material_brand: "",
          materialTypeName: "",
          material_type_id: "",
          bo_material_code: "",
          material_description: "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: "",
          cmo_material_code: "",
          cmo_material_description: "",
          cmo_material_UOM: "",
          _isSponsorLinked: true
        }]);
        
        // Extract unique values from materials to populate dropdown options
        if (allMaterials.length > 0) {
          const brands = allMaterials.map(m => m.material_brand).filter(Boolean);
          const types = allMaterials.map(m => m.materialTypeName).filter(Boolean);
          const plantIDs = allMaterials.map(m => m.materialPlantIDFromSponsorERP).filter(Boolean);
          const storageIDs = allMaterials.flatMap(m => {
            if (typeof m.materialStorageLocationIDsFromSponsorERP === 'string') {
              return m.materialStorageLocationIDsFromSponsorERP.split(',').map(s => s.trim()).filter(Boolean);
            }
            return m.materialStorageLocationIDsFromSponsorERP || [];
          }).filter(Boolean);
          
          setMaterialBrandOptions(addUniqueToOptions(brands, brands));
          setMaterialTypeOptions(addUniqueToOptions(types, types));
          setMaterialPlantIDOptions(addUniqueToOptions(plantIDs, plantIDs));
          setMaterialStorageLocationIDOptions(addUniqueToOptions(storageIDs, storageIDs));
        }
        
        // Initialize CMO materials list from ALL materials (both with and without Material Type)
        // Ensure each item has _materialId and id for tracking
        setCmoMaterialDetailsList(allMaterials.map(item => {
          // Set cmo_product_code to actual value, or default to first product code if empty/missing
          // Handle cases where cmo_product_code might be null, undefined, or empty string
          const productCode = (item.cmo_product_code && item.cmo_product_code.trim() !== "") 
            ? item.cmo_product_code 
            : (cmoProductDetailsList?.[0]?.cmoProductCode || "");
          
          // For CMO-only materials with bo_material_code, look up material_type_id from sponsor material
          let materialTypeId = item.material_type_id;
          if (item._isSponsorLinked === false && item.bo_material_code && !materialTypeId) {
            const sponsorMaterial = sponsorMaterialsList.find(m => m.bo_material_code === item.bo_material_code);
            if (sponsorMaterial && sponsorMaterial.material_type_id) {
              materialTypeId = sponsorMaterial.material_type_id;
            }
          }
          
          return {
            _materialId: item._materialId, // Preserve unique ID from parent material
            _isExistingMaterial: item._isExistingMaterial, // Preserve existing material flag
            _isSponsorLinked: item._isSponsorLinked, // Preserve sponsor-linked flag
            id: item._materialId, // Use same ID for React key stability
            material_type_id: materialTypeId, // Preserve or copy material_type_id from sponsor material
            cmo_product_code: productCode,
            bo_material_code: item.bo_material_code || "",
            material_description: item.material_description || "",
            cmo_material_code: item.cmo_material_code || "",
            cmo_material_description: item.cmo_material_description || "",
            cmo_material_UOM: item.cmo_material_UOM || ""
          };
        }));
        
        // Initialize refs with the data we just set
        latestMaterialDetailsListRef.current = sponsorMaterialsList.length > 0 ? sponsorMaterialsList : [{
          _materialId: generateMaterialId(),
          sponsorSupplyingMaterial: true,
          material_brand: "",
          materialTypeName: "",
          material_type_id: "",
          bo_material_code: "",
          material_description: "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: "",
          cmo_material_code: "",
          cmo_material_description: "",
          cmo_material_UOM: "",
          _isSponsorLinked: true
        }];
        
        latestCmoMaterialDetailsListRef.current = allMaterials.map(item => {
          const productCode = (item.cmo_product_code && item.cmo_product_code.trim() !== "") 
            ? item.cmo_product_code 
            : (cmoProductDetailsList?.[0]?.cmoProductCode || "");
          
          // For CMO-only materials with bo_material_code, look up material_type_id from sponsor material
          let materialTypeId = item.material_type_id;
          if (item._isSponsorLinked === false && item.bo_material_code && !materialTypeId) {
            const sponsorMaterial = sponsorMaterialsList.find(m => m.bo_material_code === item.bo_material_code);
            if (sponsorMaterial && sponsorMaterial.material_type_id) {
              materialTypeId = sponsorMaterial.material_type_id;
            }
          }
          
          return {
            _materialId: item._materialId,
            _isExistingMaterial: item._isExistingMaterial,
            _isSponsorLinked: item._isSponsorLinked,
            id: item._materialId,
            material_type_id: materialTypeId,
            cmo_product_code: productCode,
            bo_material_code: item.bo_material_code || "",
            material_description: item.material_description || "",
            cmo_material_code: item.cmo_material_code || "",
            cmo_material_description: item.cmo_material_description || "",
            cmo_material_UOM: item.cmo_material_UOM || ""
          };
        });
        
        // Store original material codes for change detection
        // IMPORTANT: Only set this ONCE on initial load, never overwrite
        // This ensures we always track against the true original values from the first API load
        if (!originalMaterialCodesRef.current || originalMaterialCodesRef.current.length === 0) {
          originalMaterialCodesRef.current = allMaterials.map(item => ({
            id: item._materialId || item.id, // Keep track of the material ID
            bo_material_code: item.bo_material_code || "",
            cmo_material_code: item.cmo_material_code || "",
            original_material: { ...item } // Store the complete original material object
          }));
        }
        
        // Mark as initialized - prevent useEffect from running again
        // This must be set regardless of readOnly to prevent ref overwrites
        hasInitializedMaterialsRef.current = true;
      }
    } else {
      setMaterialDetails({ productMaterialsList: [] });
      // Store initial empty state
      if (!initialMaterialDetailsRef.current) {
        initialMaterialDetailsRef.current = { productMaterialsList: [] };
      }
      if (!readOnly) {
        const defaultMaterialId = generateMaterialId();
        const defaultMaterial = {
          _materialId: defaultMaterialId,
          _isSponsorLinked: true, // New default row is sponsor-linked
          sponsorSupplyingMaterial: true,
          material_brand: "",
          materialTypeName: "",
          material_type_id: "",
          bo_material_code: "",
          material_description: "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: cmoProductDetailsList?.[0]?.cmoProductCode || "",
          cmo_material_code: "",
          cmo_material_description: "",
          cmo_material_UOM: ""
        };
        setMaterialDetailsList([defaultMaterial]);
        setCmoMaterialDetailsList([{
          _materialId: defaultMaterialId,
          id: defaultMaterialId,
          _isSponsorLinked: true, // Corresponding CMO row is linked to sponsor
          cmo_product_code: cmoProductDetailsList?.[0]?.cmoProductCode || "",
          bo_material_code: "",
          material_description: "",
          cmo_material_code: "",
          cmo_material_description: "",
          cmo_material_UOM: ""
        }]);
        
        // Initialize refs with default materials
        latestMaterialDetailsListRef.current = [defaultMaterial];
        latestCmoMaterialDetailsListRef.current = [{
          _materialId: defaultMaterialId,
          id: defaultMaterialId,
          _isSponsorLinked: true,
          cmo_product_code: cmoProductDetailsList?.[0]?.cmoProductCode || "",
          bo_material_code: "",
          material_description: "",
          cmo_material_code: "",
          cmo_material_description: "",
          cmo_material_UOM: ""
        }];
        
        // Mark as initialized - prevent useEffect from running again
        hasInitializedMaterialsRef.current = true;
      }
    }
  }, [readOnly, formData?.materialDetails, propMaterialDetails]);

  // Initialize CMO Product Details List from stepData
  useEffect(() => {
    if (stepData?.cmoProductDetailsList && stepData.cmoProductDetailsList.length > 0) {
      setCmoProductDetailsList(stepData.cmoProductDetailsList);
    } else if (formData?.productDetails?.cmoProductDetailsList && formData.productDetails.cmoProductDetailsList.length > 0) {
      setCmoProductDetailsList(formData.productDetails.cmoProductDetailsList);
    }
  }, [stepData?.cmoProductDetailsList, formData?.productDetails?.cmoProductDetailsList]);

  // Sync cmoProductDetailsList with stepData
  useEffect(() => {
    if (cmoProductDetailsList.length > 0) {
      const updatedStepData = {
        ...stepData,
        cmoProductDetailsList: cmoProductDetailsList
      };
      setStepData(updatedStepData);
      
      // In edit mode, also update parent formData via updateStepData
      if (isProductDetailsEditMode && updateStepData) {
        
        updateStepData("productDetails", updatedStepData);
      }
    }
  }, [cmoProductDetailsList, isProductDetailsEditMode]);

  // Update cmo_product_code in CMO materials when cmoProductDetailsList becomes available
  useEffect(() => {
    if (cmoProductDetailsList.length > 0 && cmoMaterialDetailsList.length > 0) {
      const defaultProductCode = cmoProductDetailsList[0]?.cmoProductCode || "";
      
      // Check if any materials have empty cmo_product_code
      const needsUpdate = cmoMaterialDetailsList.some(
        item => !item.cmo_product_code || item.cmo_product_code.trim() === ""
      );
      
      if (needsUpdate && defaultProductCode) {
        const updatedList = cmoMaterialDetailsList.map(item => ({
          ...item,
          cmo_product_code: (item.cmo_product_code && item.cmo_product_code.trim() !== "") 
            ? item.cmo_product_code 
            : defaultProductCode
        }));
        
        setCmoMaterialDetailsList(updatedList);
        
        // Sync to parent formData
        const mergedList = materialDetailsList.map((sponsorItem, idx) => ({
          ...sponsorItem,
          cmo_product_code: updatedList[idx]?.cmo_product_code || "",
          cmo_material_code: updatedList[idx]?.cmo_material_code || "",
          cmo_material_description: updatedList[idx]?.cmo_material_description || "",
          cmo_material_UOM: updatedList[idx]?.cmo_material_UOM || ""
        }));
        
        const additionalCmoRows = updatedList.slice(materialDetailsList.length)
          .filter(cmoRow => {
            return cmoRow.bo_material_code?.trim() || 
                   cmoRow.material_description?.trim() || 
                   cmoRow.cmo_product_code?.trim() ||
                   cmoRow.cmo_material_code?.trim() || 
                   cmoRow.cmo_material_description?.trim() || 
                   cmoRow.cmo_material_UOM?.trim();
          })
          .map(cmoRow => ({
            _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
            sponsorSupplyingMaterial: false,
            material_brand: "",
            materialTypeName: "",
            material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
            bo_material_code: cmoRow.bo_material_code || "",
            material_description: cmoRow.material_description || "",
            bo_material_UOM: "",
            materialPlantIDFromSponsorERP: "",
            materialStorageLocationIDsFromSponsorERP: "",
            cmo_product_code: cmoRow.cmo_product_code || "",
            cmo_material_code: cmoRow.cmo_material_code || "",
            cmo_material_description: cmoRow.cmo_material_description || "",
            cmo_material_UOM: cmoRow.cmo_material_UOM || "",
            is_material_active: 0 // Active additional CMO materials
          }));
        
        const completeList = [...mergedList, ...additionalCmoRows, ...deletedMaterials, ...materialsWithChangedCodes];
        
        if (typeof updateStepData === 'function') {
          updateStepData('materialDetails', {
            productMaterialsList: completeList
          });
        }
      }
    }
  }, [cmoProductDetailsList]);

 

  // Move ALL hooks to the top of the component, before any return or early return
  // (No code, not even a return, should come before these hooks)
  // ...
  // (All useState, useEffect, useRef, useCallback, etc. declarations go here)
  // ...
  // Ensure initialCMORef.current is set only once and never overwritten
  useEffect(() => {
    if (
      initialCMORef.current === null &&
      formData.productDetails?.vendorMosymphonyID != null
    ) {
      initialCMORef.current = formData.productDetails.vendorMosymphonyID;
    }
  }, [formData.productDetails?.vendorMosymphonyID]);

  // Ensure cmoPlantId stays in sync with vendorMosymphonyID and vendorName changes
  useEffect(() => {
    const shouldUpdate =
      formData?.productDetails?.vendorMosymphonyID &&
      formData?.productDetails?.vendorName &&
      (!stepData?.cmoPlantId || stepData?.cmoPlantId === "|" || stepData?.cmoPlantId === "||");

    if (shouldUpdate) {
      const newCmoPlantId = `${formData?.productDetails?.vendorMosymphonyID}|${formData?.productDetails?.vendorName}|${formData?.productDetails?.vendorPlant || ""}`;
      setStepData((prevData) => ({
        ...prevData,
        cmoPlantId: newCmoPlantId,
      }));
    }
  }, [
    formData?.productDetails?.vendorMosymphonyID,
    formData?.productDetails?.vendorName,
    formData?.productDetails?.vendorPlant,
    stepData?.cmoPlantId,
  ]);

  // Update local stepData when formData.productDetails changes (e.g., when navigating back to this step)
  // Use a ref to prevent unnecessary updates when only materialDetails changes
  const prevFormDataProductDetailsRef = useRef(null);
  const dropdownAPIsLoadedRef = useRef(false);

  
  useEffect(() => {
    if (formData.productDetails) {
      // Check if productDetails actually changed (not just materialDetails)
      const currentProductDetailsStr = JSON.stringify(formData.productDetails);
      if (prevFormDataProductDetailsRef.current === currentProductDetailsStr) {
        return; // No change, skip update
      }
      prevFormDataProductDetailsRef.current = currentProductDetailsStr;
      
      const vendorMosymphonyID =
        formData.productDetails.vendorMosymphonyID || "";
      const vendorName = formData.productDetails.vendorName || "";
      const vendorPlant = formData.productDetails.vendorPlant || "";
      setStepData((prevStepData) => ({
        ...prevStepData, // Preserve existing stepData
        ...formData.productDetails, // Override with formData.productDetails
        cmoPlantId:
          vendorMosymphonyID && vendorName
            ? `${vendorMosymphonyID}|${vendorName}|${vendorPlant}`
            : prevStepData.cmoPlantId || "",
        // Preserve sponsorCQAApprovalReq from previous state or formData
        // sponsorCQAApprovalReq: prevStepData?.sponsorCQAApprovalReq !== undefined 
        //   ? prevStepData.sponsorCQAApprovalReq 
        //   : formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? false,
      }));
    }
  }, [formData.productDetails]);

  // API base URL - replace with your actual API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);

  // Fetch all reference data from APIs upon component mount
  useEffect(() => {
    // Skip if already loaded or if in read-only mode
    if (dropdownAPIsLoadedRef.current || readOnly) {
      return;
    }
    
    // Set loading states to true when starting to fetch
    setIsLoading((prev) => ({
      ...prev,
      productModalities: true,
      facilityTypes: true,
      productTypes: true,
      brands: true,
    }));
    
    // Helper to handle API errors and loading state
    const handleApiError = (key, message) => {
      setApiErrors((prev) => ({ ...prev, [key]: message }));
      setIsLoading((prev) => ({ ...prev, [key]: false }));
    };
    const fetchAPIData = async () => {
      // Fetch Product Modalities (Drug Types)
      const fetchProductModalities = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/fetchPlantDrugTypes/true`
          );
          setProductModalities(response.data.data.drugTypeList);
          setIsLoading((prev) => ({ ...prev, productModalities: false }));

          // If we have existing productModality data, find and set the corresponding ID
          if (
            stepData?.productModality &&
            !stepData?.drugTypeID &&
            response.data.data.drugTypeList
          ) {
            const matchingModality = response.data.data.drugTypeList.find(
              (modality) => modality.drugTypeName === stepData.productModality
            );

            if (matchingModality) {
              const updatedStepData = {
                ...stepData,
                drugTypeID: matchingModality.drugTypeID,
              };
              setStepData(updatedStepData);
              updateStepData("productDetails", updatedStepData);
            }
          }
        } catch (error) {
          handleApiError(
            "productModalities",
            "Failed to load product modalities"
          );
        }
      };

      // Fetch Facility Types
      const fetchFacilityTypes = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/fetchFacilityTypes/true`
          );
          setFacilityTypes(response.data.data.facilityTypesList);
          setIsLoading((prev) => ({ ...prev, facilityTypes: false }));

          if (
            stepData.manufacturingFacilityType &&
            !stepData.facilityTypeID &&
            response.data.data.facilityTypesList
          ) {
            const matchingFacilityType =
              response.data.data.facilityTypesList.find(
                (facilityType) =>
                  facilityType.facilityTypeName ===
                  stepData.manufacturingFacilityType
              );

            if (matchingFacilityType) {
              const updatedStepData = {
                ...stepData,
                facilityTypeID: matchingFacilityType.facilityTypeID,
              };
              setStepData(updatedStepData);
              updateStepData("productDetails", updatedStepData);
            }
          }
        } catch (error) {
          handleApiError("facilityTypes", "Failed to load facility types");
        }
      };

      // Fetch Product Types
      const fetchProductTypesData = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/fetchProductTypes/true`
          );
          setProductTypes(response.data.data.productTypeList);
          setIsLoading((prev) => ({ ...prev, productTypes: false }));
        } catch (error) {
          handleApiError("productTypes", "Failed to load product types");
        }
      }; // Fetch Brands
      const fetchBrands = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/fetchAllBrands`);
          setBrands(response.data.data.Brands);
          setIsLoading((prev) => ({ ...prev, brands: false }));
        } catch (error) {
          handleApiError("brands", "Failed to load brands");
        }
      }; // Fetch all reference data in parallel
      await Promise.all([
        // fetchPlantSupplyTypes(),
        fetchProductModalities(),
        fetchFacilityTypes(),
        fetchProductTypesData(),
        fetchBrands(),
        // Only fetch vendor plants if we have both drugTypeID and supplyTypeID
        stepData.drugTypeID && stepData.supplyTypeID
          ? fetchVendorPlantsData()
          : Promise.resolve(),
      ]);
      
      // Mark APIs as loaded after successful fetch
      dropdownAPIsLoadedRef.current = true;
    };
    
    fetchAPIData();
    // No cleanup needed as all state updates are guarded by isMounted
  }, [readOnly, isProductDetailsEditMode]);

  useEffect(() => {
    // Get the vendorMosymphonyID from either formData or stepData
    const vendorMosymphonyID =
      formData?.productDetails?.vendorMosymphonyID ||
      stepData?.vendorMosymphonyID;

    // Only call API if vendorMosymphonyID is available
    if (!vendorMosymphonyID) {
      // Clear the data if vendorMosymphonyID is not available
      setPlantIDStorageID({});
      return;
    }

    const fetchPlantIDStorageID = async () => {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/fetchAllReceivingPlantIDsAndStorageLocationIDs/${vendorMosymphonyID}`
        );
        setPlantIDStorageID(response.data);
        // setIsLoading((prev) => ({ ...prev, plantIDStorageID: false }));

        // Uncomment and use this logic only if required:
        /*
      if (
        stepData.manufacturingFacilityType &&
        !stepData.facilityTypeID &&
        response.data.data.facilityTypesList
      ) {
        const matchingFacilityType =
          response.data.data.facilityTypesList.find(
            (facilityType) =>
              facilityType.facilityTypeName === stepData.manufacturingFacilityType
          );

        if (matchingFacilityType) {
          const updatedStepData = {
            ...stepData,
            facilityTypeID: matchingFacilityType.facilityTypeID,
          };
          setStepData(updatedStepData);
          updateStepData("productDetails", updatedStepData);
        }
      }
      */
      } catch (error) {
        console.error(
          "Error fetching plant ID and storage location IDs:",
          error
        );
        // setIsLoading((prev) => ({ ...prev, plantIDStorageID: false }));
      }
    };

    fetchPlantIDStorageID();
  }, [
    formData?.productDetails?.vendorMosymphonyID,
    stepData?.vendorMosymphonyID,
    apiBaseUrl,
  ]);

  const receivingStorageLocationIDs =
    plantIDStorageID?.vendorOrgLevelData?.[0]?.receivingStorageLocationIDs ||
    [];

  const receivingPlantIDs =
    plantIDStorageID?.vendorOrgLevelData?.[0]?.receivingPlantIDs || [];

  // Debug logging to check data types

  useEffect(() => {
    // Helper to handle API errors and loading state
    const handleApiError = (key, message) => {
      setApiErrors((prev) => ({ ...prev, [key]: message }));
      setIsLoading((prev) => ({ ...prev, [key]: false }));
    };
    if (isProductDetailsEditMode) {
      const fetchBrands = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/fetchAllBrands`);
          setBrands(response.data.data.Brands);
          setIsLoading((prev) => ({ ...prev, brands: false }));
        } catch (error) {
          handleApiError("brands", "Failed to load brands");
        }
      };
      fetchBrands();
    }
  }, [isProductDetailsEditMode]);

  // On mount, check if productAddedOnce is set and skip the replica modal if so
  useEffect(() => {
    const shouldSkip =
      productAddedOnce ||
      formData.productDetails?.collabrixProductID ||
      editPendingStatus ||
      isInitialSaveSuccess;
    if (shouldSkip && typeof setSkipReplicateDialog === "function") {
      setSkipReplicateDialog(true);
    }
  }, [
    productAddedOnce,
    formData.productDetails?.collabrixProductID,
    editPendingStatus,
    setSkipReplicateDialog,
    isInitialSaveSuccess,
  ]);

  // Add this useEffect after snackbar state is defined
  useEffect(() => {
    if (apiErrors.saveProductDetails) {
      setSnackbar({
        open: true,
        message: apiErrors.saveProductDetails,
        severity: "error",
        key: Date.now(),
      });
    }
  }, [apiErrors.saveProductDetails]);

  useEffect(() => {
    let isMounted = true;
    const collabrixProductID =
      formData?.productDetails?.collabrixProductID ||
      selectedProductDetails?.collabrixProductID;
    if (
      (editPendingStatus && collabrixProductID) ||
      (viewOnlyProductDetails && collabrixProductID) ||
      isInitialSaveSuccess
    ) {
      const callData = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/fetchProductDetails/${collabrixProductID}`
          );
          if (isMounted && response.data && response.data.status) {
            const apiData = response.data.data;
            if (!isInitialSaveSuccess) {
              // Convert allocation configuration trigger values from status names to IDs
              const poStatuses = apiData?.productFGStatuses?.fgStatusesJSON?.poStatuses || [];
              if (apiData.allocationConfiguration && poStatuses.length > 0) {
                apiData.allocationConfiguration = convertAllocationConfigTriggerValues(
                  apiData.allocationConfiguration,
                  poStatuses
                );
              }
              setUpdateModeData(apiData);
            }
            const productDetailsData = apiData.productDetails || {};

            // Convert string fields that should be arrays back to arrays
            if (
              productDetailsData.receivingStorageLocationIDsFromSponsorERP &&
              typeof productDetailsData.receivingStorageLocationIDsFromSponsorERP ===
                "string"
            ) {
              productDetailsData.receivingStorageLocationIDsFromSponsorERP =
                productDetailsData.receivingStorageLocationIDsFromSponsorERP
                  .split(",")
                  .map((item) => item.trim())
                  .filter((item) => item !== "");
            }

            setStepData((prevData) => {
              const updatedData = { 
                ...prevData, 
                ...productDetailsData,
                // Set sponsorCQAApprovalReq from processFlowConfiguration
                // sponsorCQAApprovalReq: apiData.processFlowConfiguration?.sponsorCQAApprovalReq ?? false
              };
              if (typeof updateStepData === "function") {
                updateStepData("productDetails", updatedData);
              }
              return updatedData;
            });

            // Set material details from API response
            if (apiData.materialDetails) {
              // Reset the initialization flag to allow re-initialization with API data
              hasInitializedMaterialsRef.current = false;
              setMaterialDetails(apiData.materialDetails);
              // Store initial material details for change detection
              if (!initialMaterialDetailsRef.current) {
                initialMaterialDetailsRef.current = JSON.parse(JSON.stringify(apiData.materialDetails));
              }
              // Update parent component with material details
              if (typeof updateStepData === "function") {
                updateStepData("materialDetails", apiData.materialDetails);
              }
            } else {
              // Ensure we clear material details if none exist
              hasInitializedMaterialsRef.current = false;
              setMaterialDetails({ productMaterialsList: [] });
              if (!initialMaterialDetailsRef.current) {
                initialMaterialDetailsRef.current = { productMaterialsList: [] };
              }
              if (typeof updateStepData === "function") {
                updateStepData("materialDetails", null);
              }
            }
          }
        } catch (error) {
          // setError("An error occurred while fetching product details.");
        }
      };
      if (!readOnly || isInitialSaveSuccess) {
        callData();
      }
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const isEditMode = location.state?.isEdit && location.state?.isNotPending;
    if (
      (isEditMode && formData.productDetails && !readOnly) ||
      (isProductDetailsEditMode && !readOnly)
    ) {
      setStepData((prevStepData) => ({
        ...formData.productDetails,
        // Preserve sponsorCQAApprovalReq from previous state or formData
        // sponsorCQAApprovalReq: prevStepData?.sponsorCQAApprovalReq !== undefined 
        //   ? prevStepData.sponsorCQAApprovalReq 
        //   : formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? false,
      }));
      fetchVendorPlantsData(formData.productDetails);
    }
  }, [
    location.state?.isEdit,
    location.state?.isNotPending,
    stepData?.drugTypeID,
    stepData?.facilityTypeID,
    isProductDetailsEditMode,
  ]);


  // Call validation on every stepData change
  useEffect(() => {
    onValidationChange(validate());
  }, [stepData, onValidationChange, isProductDetailsEditMode, cmoProductDetailsList, materialDetailsList, cmoMaterialDetailsList, materialDetailsErrors, cmoMaterialDetailsErrors]);

  // Reset skipReplicateDialog after it is used
  useEffect(() => {
    if (skipReplicateDialog) {
      setSkipReplicateDialog(false);
    }
  }, [skipReplicateDialog, setSkipReplicateDialog]);

  // After all hooks, the rest of the component logic and the return statement
  // Prevent memory leaks on async state updates
  const isMounted = useIsMounted();

  // API data states - store data fetched from backend
  const [productModalities, setProductModalities] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendorPlants, setVendorPlants] = useState([]);
  // Loading states for tracking API fetch status for different data sources
  // Initialize as false if readOnly, true otherwise (editPendingStatus should still fetch dropdown APIs)
  const [isLoading, setIsLoading] = useState({
    plantSupplyTypes: false, // Not fetching this API anymore, so set to false
    productModalities: !readOnly,
    facilityTypes: !readOnly,
    productTypes: !readOnly,
    brands: !readOnly,
    vendorPlants: !readOnly,
    savingData: false, // Tracks when form data is being saved to backend
  });
  // Error handling states
  const [openBrandDialog, setOpenBrandDialog] = useState(false); // Controls visibility of add new brand dialog
  const [newBrand, setNewBrand] = useState(""); // Stores new brand name being added
  const [brandError, setBrandError] = useState(""); // Error message for brand dialog
  const [errors, setErrors] = useState({}); // Field validation errors
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
    key: Date.now(), // Add a key to force re-render
  });
  // Add state for dialog and response
  const [openReplicateDialog, setOpenReplicateDialog] = useState(false);
  const [replicateConfigResponse, setReplicateConfigResponse] = useState(null);
  // Add state for replicate options and selection
  const [replicateOptions, setReplicateOptions] = useState([]);
  const [selectedReplicateOption, setSelectedReplicateOption] = useState(null);
  const [selectedReplicateValue, setSelectedReplicateValue] = useState("");
  const [isReplicateLoading, setIsReplicateLoading] = useState(false);

  // Create Alert component for Snackbar
  const Alert = (props) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  };

  const initialCMORef = useRef(null);

  // Filter material types based on selected product type
  const availableMaterialTypes = useMemo(() => {
    if (!stepData?.productTypeID || !productTypes || productTypes.length === 0) {
      return [];
    }
    
    const selectedProductType = productTypes.find(
      pt => pt.productTypeID === stepData.productTypeID
    );
    
    if (!selectedProductType || !selectedProductType.associatedMaterialTypesList) {
      return [];
    }
    
    return selectedProductType.associatedMaterialTypesList;
  }, [stepData?.productTypeID, productTypes]);

  // Function to fetch vendor plants data - moved out of useEffect for broader access
  const fetchVendorPlantsData = async (data = stepData) => {
    // Check if required IDs are available before making the API call
    if (!data?.drugTypeID || !data?.facilityTypeID) {
      return; // Skip API call if required fields are missing
    }

    try {
      setIsLoading((prev) => ({ ...prev, vendorPlants: true }));
      const payload = {
        drugTypeID: data.drugTypeID,
        supplyTypeID: data.supplyTypeID || selectedProductType?.supplyTypeID,
        facilityTypeID: data.facilityTypeID,
      };
      // Fetch vendor plants from the backend
      const response = await axios.post(
        `${apiBaseUrl}/fetchVendorPlants`,
        payload
      );

      // --- Ensure the selected plant (from product details) is present in the dropdown ---
      // This is needed when coming from an existing product, where the selected plant may not be in the API response
      if (
        data.vendorMosymphonyID &&
        response.data.vendorPlantDetails &&
        !response.data.vendorPlantDetails.some(
          (p) =>
            String(p.vendorMosymphonyID) === String(data.vendorMosymphonyID)
        )
      ) {
        response.data.vendorPlantDetails.push({
          vendorPlant: data.vendorPlant || "",
          vendorName: data.vendorName,
          vendorMosymphonyID: Number(data.vendorMosymphonyID),
        });
      }

      // --- Normalize all vendorPlantDetails to ensure vendorPlant is always a name/code, never an ID ---
      // This prevents issues where the dropdown might display a numeric ID instead of a plant name
      const normalizedVendorPlants = response.data.vendorPlantDetails
        .map((plant) => ({
          ...plant,
          vendorPlant:
            typeof plant.vendorPlant === "number"
              ? String(plant.vendorPlant)
              : plant.vendorPlant || "",
        }))
        .filter((plant) => plant?.vendorMosymphonyID); // Only keep plants with a valid ID
      setVendorPlants(normalizedVendorPlants);
      setIsLoading((prev) => ({ ...prev, vendorPlants: false }));
    } catch (error) {
      setApiErrors((prev) => ({
        ...prev,
        vendorPlants: "Failed to load vendor plants",
      }));
      setIsLoading((prev) => ({ ...prev, vendorPlants: false }));
    }
  };


  // Handle form field changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    
    // Special handling for boolean fields
    let safeValue;
    // if (name === "sponsorCQAApprovalReq") {
    //   safeValue = value; // Keep boolean value as-is
    // } else {
      safeValue = value !== undefined && value !== null ? value : "";
    // }



    // Updated based on requirements
    const productCodeRegex = /^[A-Za-z0-9\-_]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_)
    const productDescriptionRegex = /^[A-Za-z0-9\-_.,() ]+$/; // Alphabets, Spaces, Numbers (0-9), Hyphen(-), Point(.), Comma(,), parentheses(), colon(:)
    const cmoProductCodeRegex = /^[A-Za-z0-9\-_/]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/)
    const cmoProductDescriptionRegex = /^[A-Za-z0-9\-_.,()&/ ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/), Ampersand (&), Parentheses(), Slash (/), Colon (:), Period (.)

    // Reset skipReplicateDialog if key fields change
    if (
      name === "productTypeName" ||
      name === "productTypeID" ||
      name === "vendorMosymphonyID"
    ) {
      setSkipReplicateDialog(false);
    }

    const updatedStepData = {
      ...stepData,
      [name]: safeValue,
    };

    
    // Special case for cmoPlantId composite value
    if (name === "cmoPlantId" && value) {
      const [vendorMosymphonyID, vendorPlant] = value.split("|");
      const selectedVendor = vendorPlants.find(
        (plant) =>
          String(plant?.vendorMosymphonyID) === vendorMosymphonyID &&
          plant.vendorPlant === vendorPlant
      );
      updatedStepData.vendorMosymphonyID = Number(vendorMosymphonyID);
      updatedStepData.vendorPlant = vendorPlant;
      updatedStepData.vendorName = selectedVendor
        ? selectedVendor.vendorName
        : "";
    }
    // Special case handlers for fields that require additional data processing
    if (name === "productModality" && safeValue) {
      const selectedModality = productModalities?.find(
        (modality) => modality.drugTypeName === safeValue
      );
      updatedStepData.drugTypeID = selectedModality
        ? selectedModality.drugTypeID
        : "";
    }
    if (name === "productTypeName" && safeValue) {
      const selectedProductType = productTypes?.find(
        (product) => product.productTypeName === safeValue
      );
      updatedStepData.productTypeID = selectedProductType
        ? selectedProductType.productTypeID
        : "";
    }
    if (name === "manufacturingFacilityType" && safeValue) {
      const selectedFacilityType = facilityTypes?.find(
        (facility) => facility.facilityTypeName === safeValue
      );
      updatedStepData.facilityTypeID = selectedFacilityType
        ? selectedFacilityType.facilityTypeID
        : "";
    }

    // Special case for multi-select receivingStorageLocationIDsFromSponsorERP
    if (name === "receivingStorageLocationIDsFromSponsorERP") {
      // Convert array to comma-separated string for storage
      const arrayValue = Array.isArray(value) ? value : [];
      updatedStepData[name] = arrayValue.join(",");
    }

    setStepData(updatedStepData);

  

    // OnChange validation for productCode, productDescription, cmoProductCode, cmoProductDescription
    const newErrors = { ...errors };

// if (name === "sponsorCQAApprovalReq") {
//   // Valid values are boolean true or false, invalid are null, undefined, or empty string
//   if (safeValue === null || safeValue === undefined || safeValue === "") {
//     newErrors[name] = "QA check required is required";
//   } else {
//     delete newErrors[name];
//   }
// }

    if (name === "productCode") {
      if (!safeValue) {
        newErrors[name] = "Product Code is required";
      } else if (!productCodeRegex.test(safeValue)) {
        newErrors[name] =
          "Product Code can only contain alphabets, numbers, hyphens (-), and underscores (_)";
      } else {
        delete newErrors[name];
      }
      //setErrors([]);
    }

    if (name === "productDescription") {
      if (!safeValue) {
        newErrors[name] = "Product Description is required";
      } else if (!productDescriptionRegex.test(safeValue)) {
        newErrors[name] =
          "Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), and parentheses ()";
      } else {
        delete newErrors[name];
      }
    }
    if (name === "cmoProductCode") {
      if (!safeValue) {
        newErrors[name] = "CMO Product Code is required";
      } else if (!cmoProductCodeRegex.test(safeValue)) {
        newErrors[name] =
          "CMO Product Code can only contain alphabets, numbers, hyphens (-), underscores (_), and slashes (/)";
      } else {
        delete newErrors[name];
      }
    }
    if (name === "cmoProductDescription") {
      if (!safeValue) {
        newErrors[name] = "CMO Product Description is required";
      } else if (!cmoProductDescriptionRegex.test(safeValue)) {
        newErrors[name] =
          "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
      } else {
        delete newErrors[name];
      }
    }
    // Also keep the previous logic for edit mode and other fields
    // if (isProductDetailsEditMode) {
    //   if (!safeValue || (typeof safeValue === 'string' && safeValue.trim() === "")) {
    //     newErrors[name] = `${name === "cmoProductCode" ? "CMO Product Code" : name === "cmoProductDescription" ? "CMO Product Description" : name} is required`;
    //     setErrors(newErrors);
    //   }
    // }

    // === Product Modality Dropdown ===
    if (name === "productModality") {
      if (!safeValue) {
        newErrors[name] = "Product Modality is required";
      } else {
        const selectedModality = productModalities?.find(
          (modality) => modality.drugTypeName === safeValue
        );
        updatedStepData.drugTypeID = selectedModality
          ? selectedModality.drugTypeID
          : "";

        // Clear error if valid
        delete newErrors[name];
      }
    }

    // === Product Type Dropdown ===
    if (name === "productTypeName") {
      if (!safeValue) {
        newErrors[name] = "Product Type is required";
      } else {
        const selectedProductType = productTypes?.find(
          (product) => product.productTypeName === safeValue
        );
        updatedStepData.productTypeID = selectedProductType
          ? selectedProductType.productTypeID
          : "";

        delete newErrors[name];
      }
    }

    // === Manufacturing Facility Type Dropdown ===
    if (name === "manufacturingFacilityType") {
      if (!safeValue) {
        newErrors[name] = "Manufacturing Facility Type is required";
      } else {
        const selectedFacilityType = facilityTypes?.find(
          (facility) => facility.facilityTypeName === safeValue
        );
        updatedStepData.facilityTypeID = selectedFacilityType
          ? selectedFacilityType.facilityTypeID
          : "";

        delete newErrors[name];
      }
    }
    // Generic validation for edit mode - exclude boolean fields like sponsorCQAApprovalReq
    if (
      isProductDetailsEditMode &&
      // name !== "sponsorCQAApprovalReq" && // Exclude boolean fields from generic validation
      (!safeValue ||
        (typeof safeValue === "string" && safeValue.trim() === "") ||
        (Array.isArray(safeValue) && safeValue.length === 0))
    ) {
      if (!newErrors[name]) {
        newErrors[name] = `${
          name === "cmoProductCode"
            ? "CMO Product Code"
            : name === "cmoProductDescription"
            ? "CMO Product Description"
            : //name === "productModality" ? productModality:
              name
        } is required`;
      }
    }

    //for brand //
    if (name === "brand") {
      if (!safeValue) {
        newErrors[name] = "Brand is required";
      } else {
        delete newErrors[name];
        updatedStepData.brand = safeValue; // save brand in your step data
      }
    }
    //for status//
    if (name === "status") {
      if (!safeValue) {
        newErrors[name] = "status is required";
      } else {
        delete newErrors[name];
        updatedStepData.status = safeValue; // save brand in your step data
      }
    }

    // if (name === "cmoPlantId") {
    //   if (!safeValue) {
    //     newErrors[name] = "CMO - Plant ID is required";
    //   } else {
    //     delete newErrors[name];
    //   }
    // }

        if (name === "cmoPlantId") {
      if (!safeValue) {
        newErrors[name] = "CMO is required";
      } else {
        delete newErrors[name];
      }
    }

    if (name === "sponsorUOM") {
  if (!safeValue || safeValue.trim() === "") {
    newErrors[name] = "sponsorUOM is required"; // key is 'sponsorUOM'
  } else {
    delete newErrors[name];
  }
}

    if (name === "cmoUOM") {
  if (!safeValue || safeValue.trim() === "") {
    newErrors[name] = "cmoUOM is required"; // key is 'sponsorUOM'
  } else {
    delete newErrors[name];
  }
}


    setErrors(newErrors);

    // Propagate changes to parent component
    if (!editPendingStatus) {
      if (!isInitialSaveSuccess) {
        // Ensure receivingStorageLocationIDsFromSponsorERP is always a string for consistency
        const formattedStepData = {
          ...updatedStepData,
          receivingStorageLocationIDsFromSponsorERP: 
            Array.isArray(updatedStepData.receivingStorageLocationIDsFromSponsorERP)
              ? updatedStepData.receivingStorageLocationIDsFromSponsorERP.join(",")
              : updatedStepData.receivingStorageLocationIDsFromSponsorERP
        };
        
        updateStepData("productDetails", formattedStepData);
        // Special handling for sponsorCQAApprovalReq - update processFlowConfiguration
        // if (name === "sponsorCQAApprovalReq") {
        //   updateStepData("processFlowConfiguration", {
        //     ...formData?.processFlowConfiguration,
        //     sponsorCQAApprovalReq: safeValue,
        //   });
        // }
      }
    } else if (name === "cmoPlantId") {
      updateStepData("productDetails", {
        ...formData.productDetails,
        cmoPlantId: updatedStepData.cmoPlantId,
        vendorMosymphonyID: updatedStepData.vendorMosymphonyID,
        vendorPlant: updatedStepData.vendorPlant,
        vendorName: updatedStepData.vendorName,
      });
    // } else if (name === "sponsorCQAApprovalReq") {
    //   // Handle sponsorCQAApprovalReq for edit pending status
    //   updateStepData("processFlowConfiguration", {
    //     ...formData?.processFlowConfiguration,
    //     sponsorCQAAprovalReq: safeValue,
    //   });
    }

    // Only call fetchVendorPlantsData when a relevant field changes AND we have both drugTypeID and supplyTypeID
    if (
      (name === "productModality" ||
        name === "manufacturingFacilityType" ||
        name === "manufactureSupplyType" ||
        name === "supplyTypeID") &&
      updatedStepData.drugTypeID &&
      updatedStepData.supplyTypeID
    ) {
      fetchVendorPlantsData(updatedStepData);
    }
  };

  // CMO Product Details List Handlers
  const handleAddCmoProductRow = () => {
    setCmoProductDetailsList([
      ...cmoProductDetailsList,
      { cmoProductCode: "", cmoProductDescription: "", cmoUOM: "" }
    ]);
  };

  const handleDeleteCmoProductRow = (index) => {
    const deletedProductCode = cmoProductDetailsList[index]?.cmoProductCode;
    
    if (cmoProductDetailsList.length > 1) {
      // If more than 1 row, delete the row
      const updatedList = cmoProductDetailsList.filter((_, idx) => idx !== index);
      setCmoProductDetailsList(updatedList);
      // Clear errors for deleted row
      const newErrors = { ...cmoProductErrors };
      delete newErrors[`cmoProductCode-${index}`];
      delete newErrors[`cmoProductDescription-${index}`];
      delete newErrors[`cmoUOM-${index}`];
      setCmoProductErrors(newErrors);
    } else {
      // If only 1 row, clear the row data
      const updatedList = [...cmoProductDetailsList];
      updatedList[index] = { cmoProductCode: "", cmoProductDescription: "", cmoUOM: "" };
      setCmoProductDetailsList(updatedList);
      // Clear errors for this row
      const newErrors = { ...cmoProductErrors };
      delete newErrors[`cmoProductCode-${index}`];
      delete newErrors[`cmoProductDescription-${index}`];
      delete newErrors[`cmoUOM-${index}`];
      setCmoProductErrors(newErrors);
    }
    
    // Validate CMO Material Details for invalid product code references
    if (deletedProductCode && deletedProductCode.trim() !== "") {
      const materialsWithDeletedProduct = cmoMaterialDetailsList.filter(
        material => material.cmo_product_code === deletedProductCode
      );
      
      if (materialsWithDeletedProduct.length > 0) {
        // Set errors for materials referencing the deleted product
        const newMaterialErrors = { ...cmoMaterialDetailsErrors };
        cmoMaterialDetailsList.forEach((material, idx) => {
          if (material.cmo_product_code === deletedProductCode) {
            newMaterialErrors[`cmo_product_code-${idx}`] = "This CMO Product Code no longer exists. Please select a valid one.";
          }
        });
        setCmoMaterialDetailsErrors(newMaterialErrors);
      }
    }
  };

  // Check if any row is empty (all fields empty)
  const hasEmptyRow = () => {
    return cmoProductDetailsList.some(row => 
      !row.cmoProductCode?.trim() && !row.cmoProductDescription?.trim() && !row.cmoUOM?.trim()
    );
  };

  // Material Details List Handlers
  const handleAddMaterialRow = () => {
    // Store the current length BEFORE adding the new sponsor row
    const currentSponsorLength = materialDetailsList.length;
    
    const newMaterialId = generateMaterialId();
    const updatedList = [
      ...materialDetailsList,
      {
        _materialId: newMaterialId,
        _isNewMaterial: true, // Track that this is a new material
        _isSponsorLinked: true, // New sponsor row is always linked to both sponsor and CMO
        sponsorSupplyingMaterial: true,
        material_brand: "",
        materialTypeName: null,
        material_type_id: null,
        bo_material_code: "",
        material_description: "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: cmoProductDetailsList?.[0]?.cmoProductCode || "",
        cmo_material_code: "",
        cmo_material_description: "",
        cmo_material_UOM: ""
      }
    ];
    setMaterialDetailsList(updatedList);
    latestMaterialDetailsListRef.current = updatedList; // Update ref
    
    // Add corresponding CMO material row at the position matching the new sponsor row
    // Preserve any additional CMO rows (with their unique IDs) that exist beyond the sponsor list
    const newCmoRow = {
      _materialId: newMaterialId, // Use same ID as sponsor row for linking
      id: newMaterialId, // For React key stability
      cmo_product_code: cmoProductDetailsList?.[0]?.cmoProductCode || "",
      bo_material_code: "",
      material_description: "",
      cmo_material_code: "",
      cmo_material_description: "",
      cmo_material_UOM: ""
    };
    
    // Split CMO list: linked rows (first N) and additional rows (rest, with unique IDs)
    const linkedRows = cmoMaterialDetailsList.slice(0, currentSponsorLength);
    const additionalRows = cmoMaterialDetailsList.slice(currentSponsorLength);
    
    // Insert new linked row, preserving all additional rows WITH their IDs
    const updatedCmoList = [...linkedRows, newCmoRow, ...additionalRows];
    
    setCmoMaterialDetailsList(updatedCmoList);
    
    // Merge sponsor and CMO data for parent update (linked rows)
    const mergedList = updatedList.map((sponsorItem, idx) => ({
      ...sponsorItem,
      cmo_product_code: updatedCmoList[idx]?.cmo_product_code || "",
      cmo_material_code: updatedCmoList[idx]?.cmo_material_code || "",
      cmo_material_description: updatedCmoList[idx]?.cmo_material_description || "",
      cmo_material_UOM: updatedCmoList[idx]?.cmo_material_UOM || ""
    }));
    
    // Get additional CMO rows (those beyond the sponsor materials count)
    const additionalCmoRows = updatedCmoList.slice(updatedList.length).map(cmoRow => ({
      _materialId: cmoRow._materialId || cmoRow.id, // Preserve unique ID
      _isNewMaterial: cmoRow._isNewMaterial, // Preserve new material flag
      _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
      sponsorSupplyingMaterial: false, // Additional rows are CMO-only
      material_brand: "",
      materialTypeName: "",
      material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
      bo_material_code: cmoRow.bo_material_code || "",
      material_description: cmoRow.material_description || "",
      bo_material_UOM: "",
      materialPlantIDFromSponsorERP: "",
      materialStorageLocationIDsFromSponsorERP: "",
      cmo_product_code: cmoRow.cmo_product_code || "",
      cmo_material_code: cmoRow.cmo_material_code || "",
      cmo_material_description: cmoRow.cmo_material_description || "",
      cmo_material_UOM: cmoRow.cmo_material_UOM || "",
      is_material_active: 0 // Active additional CMO materials
    }));
    
    // Combine linked rows, additional CMO rows, deleted materials, and materials with changed codes
    const completeList = [...mergedList, ...additionalCmoRows, ...deletedMaterials, ...materialsWithChangedCodes];
    
    // Update parent formData with material details
    if (typeof updateStepData === 'function') {
      updateStepData('materialDetails', {
        productMaterialsList: completeList
      });
    }
  };

  const handleDeleteMaterialRow = (index) => {
    // Store the material being deleted to track for payload
    const materialToDelete = materialDetailsList[index];
    const cmoMaterialToDelete = cmoMaterialDetailsList[index];
    
    // Get original CMO data from ref if available (to track original values, not edited values)
    const originalCmoData = originalMaterialCodesRef.current?.[index]?.original_material || cmoMaterialToDelete;
    
    // Only add to deleted list if this is an existing material from the API
    // New materials that were never saved don't need to be marked as deleted
    // Check if material has material_type_id from database OR if _isExistingMaterial flag is set
    const isExistingMaterial = (materialToDelete?.material_type_id !== undefined && 
                                materialToDelete?.material_type_id !== null && 
                                materialToDelete?.material_type_id !== '') || 
                               materialToDelete?._isExistingMaterial === true;
    
    // Track the updated deleted materials list for immediate use
    let updatedDeletedMaterials = [...deletedMaterials];
    
    if (isExistingMaterial && materialToDelete && 
        (materialToDelete.bo_material_code?.trim() || 
         materialToDelete.material_description?.trim() || 
         originalCmoData?.cmo_material_code?.trim())) {
      // Check if this is a CMO-only material (not sponsor-linked)
      // A material is CMO-only if it's either:
      // 1. Not explicitly marked as sponsor-linked (_isSponsorLinked === false), OR
      // 2. Has no materialTypeName (legacy check for backwards compatibility)
      const isCmoOnly = materialToDelete._isSponsorLinked === false || 
                       (!materialToDelete.materialTypeName || materialToDelete.materialTypeName.trim() === "");
      
      const deletedRecord = isCmoOnly ? {
        // For CMO-only materials, exclude sponsor-specific fields
        // Use ORIGINAL CMO data, not current edited state
        _materialId: materialToDelete._materialId,
        _isNewMaterial: materialToDelete._isNewMaterial,
        sponsorSupplyingMaterial: false,
        material_brand: "",
        materialTypeName: null, // Set to null for payload (API requirement)
        material_type_id: materialToDelete.material_type_id !== undefined ? materialToDelete.material_type_id : "",
        bo_material_code: materialToDelete.bo_material_code || "",
        material_description: materialToDelete.material_description || "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: originalCmoData?.cmo_product_code || "",
        cmo_material_code: originalCmoData?.cmo_material_code || "",
        cmo_material_description: originalCmoData?.cmo_material_description || "",
        cmo_material_UOM: originalCmoData?.cmo_material_UOM || "",
        is_material_active: 1
      } : {
        // For sponsor materials, include all fields
        // Use ORIGINAL CMO data, not current edited state
        ...materialToDelete,
        _materialId: materialToDelete._materialId,
        _isNewMaterial: materialToDelete._isNewMaterial,
        cmo_product_code: originalCmoData?.cmo_product_code || "",
        cmo_material_code: originalCmoData?.cmo_material_code || "",
        cmo_material_description: originalCmoData?.cmo_material_description || "",
        cmo_material_UOM: originalCmoData?.cmo_material_UOM || "",
        is_material_active: 1
      };
      // Ensure deleted record has unique ID
      ensureMaterialId(deletedRecord);
      
      // Check if this material is already in deleted list using unique combination of material codes
      // NOTE: material_type_id is NOT unique - it represents the category (e.g., "Bulk DP")
      // Multiple materials can have the same material_type_id, so we must use material codes
      const uniqueKey = [
        deletedRecord.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
        deletedRecord.bo_material_code || '',
        deletedRecord.cmo_material_code || '',
        deletedRecord.material_type_id || 'null'
      ].filter(Boolean).join('|');
      
      // Check if already exists with same unique key
      const exists = updatedDeletedMaterials.some(m => {
        const existingKey = [
          m.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
          m.bo_material_code || '',
          m.cmo_material_code || '',
          m.material_type_id || 'null'
        ].filter(Boolean).join('|');
        return existingKey === uniqueKey;
      });
      
      if (!exists) {
        updatedDeletedMaterials = [...updatedDeletedMaterials, deletedRecord];
      }
      
      // Update state with the new deleted materials list
      setDeletedMaterials(updatedDeletedMaterials);
    }
    
    // Store the current sponsor length BEFORE deletion
    const currentSponsorLength = materialDetailsList.length;
    let updatedList;
    let updatedCmoList;
    
    if (materialDetailsList.length > 1) {
      updatedList = materialDetailsList.filter((_, idx) => idx !== index);
      setMaterialDetailsList(updatedList);
      latestMaterialDetailsListRef.current = updatedList; // Update ref
      
      // Remove the corresponding linked CMO row, but preserve additional rows
      const linkedRows = cmoMaterialDetailsList.slice(0, currentSponsorLength);
      const additionalRows = cmoMaterialDetailsList.slice(currentSponsorLength);
      
      // Remove from linked rows only
      const updatedLinkedRows = linkedRows.filter((_, idx) => idx !== index);
      updatedCmoList = [...updatedLinkedRows, ...additionalRows];
      
      setCmoMaterialDetailsList(updatedCmoList);
      
      // Clear errors for deleted row
      const newErrors = { ...materialDetailsErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.endsWith(`-${index}`)) {
          delete newErrors[key];
        }
      });
      setMaterialDetailsErrors(newErrors);
      
      // Check if any additional CMO materials reference this deleted sponsor material code
      const deletedMaterialCode = materialToDelete?.bo_material_code;
      if (deletedMaterialCode && deletedMaterialCode.trim() !== "") {
        const newCmoErrors = { ...cmoMaterialDetailsErrors };
        
        // Find and track CMO-only materials that reference this deleted sponsor code
        updatedCmoList.forEach((cmoRow, cmoIdx) => {
          // Only check additional CMO rows (beyond sponsor list length)
          if (isAdditionalCmoRow(cmoIdx, updatedList.length) && cmoRow.bo_material_code === deletedMaterialCode) {
            newCmoErrors[`bo_material_code-${cmoIdx}`] = ERROR_MESSAGES.SPONSOR_MATERIAL_CODE_DELETED;
            
            // If this CMO-only material was loaded from API (is existing), add it to deleted materials
            const isCmoExisting = (cmoRow.material_type_id !== undefined && 
                                  cmoRow.material_type_id !== null && 
                                  cmoRow.material_type_id !== '') || 
                                 cmoRow._isExistingMaterial === true;
            if (isCmoExisting) {
              const cmoMaterialId = cmoRow._materialId || generateMaterialId();
              
              // Check if this CMO material is already in deleted list
              const alreadyTracked = updatedDeletedMaterials.some(m => m._materialId === cmoMaterialId);
              
              if (!alreadyTracked) {
                const deletedCmoMaterial = {
                  _materialId: cmoMaterialId,
                  _isExistingMaterial: true,
                  sponsorSupplyingMaterial: false,
                  material_brand: "",
                  materialTypeName: null,
                  material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
                  bo_material_code: cmoRow.bo_material_code || "",
                  material_description: cmoRow.material_description || "",
                  bo_material_UOM: "",
                  materialPlantIDFromSponsorERP: "",
                  materialStorageLocationIDsFromSponsorERP: "",
                  cmo_product_code: cmoRow.cmo_product_code || "",
                  cmo_material_code: cmoRow.cmo_material_code || "",
                  cmo_material_description: cmoRow.cmo_material_description || "",
                  cmo_material_UOM: cmoRow.cmo_material_UOM || "",
                  is_material_active: 1
                };
                
                updatedDeletedMaterials = [...updatedDeletedMaterials, deletedCmoMaterial];
              }
            }
          }
        });
        setCmoMaterialDetailsErrors(newCmoErrors);
        
        // Update deleted materials state if CMO materials were added
        setDeletedMaterials(updatedDeletedMaterials);
      }
    } else {
      // If only 1 row, clear the row data
      updatedList = [...materialDetailsList];
      updatedList[index] = {
        sponsorSupplyingMaterial: true,
        material_brand: "",
        materialTypeName: "",
        material_type_id: "",
        bo_material_code: "",
        material_description: "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_material_code: "",
        cmo_material_description: "",
        cmo_material_UOM: ""
      };
      setMaterialDetailsList(updatedList);
      latestMaterialDetailsListRef.current = updatedList; // Update ref
      
      // Also clear corresponding CMO material row (only the linked one)
      if (index < currentSponsorLength) {
        updatedCmoList = [...cmoMaterialDetailsList];
        updatedCmoList[index] = {
          cmo_product_code: "",
          bo_material_code: "",
          material_description: "",
          cmo_material_code: "",
          cmo_material_description: "",
          cmo_material_UOM: ""
        };
        setCmoMaterialDetailsList(updatedCmoList);
      }
      
      // Clear errors for this row
      const newErrors = { ...materialDetailsErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.endsWith(`-${index}`)) {
          delete newErrors[key];
        }
      });
      setMaterialDetailsErrors(newErrors);
      
      // Check if any additional CMO materials reference this cleared sponsor material code
      const clearedMaterialCode = materialToDelete?.bo_material_code;
      if (clearedMaterialCode && clearedMaterialCode.trim() !== "") {
        const cmoListToCheck = updatedCmoList || cmoMaterialDetailsList;
        const newCmoErrors = { ...cmoMaterialDetailsErrors };
        
        // Find and track CMO-only materials that reference this cleared sponsor code
        cmoListToCheck.forEach((cmoRow, cmoIdx) => {
          // Only check additional CMO rows (beyond sponsor list length)
          if (isAdditionalCmoRow(cmoIdx, updatedList.length) && cmoRow.bo_material_code === clearedMaterialCode) {
            newCmoErrors[`bo_material_code-${cmoIdx}`] = ERROR_MESSAGES.SPONSOR_MATERIAL_CODE_DELETED;
            
            // If this CMO-only material was loaded from API (is existing), add it to deleted materials
            const isCmoExisting = (cmoRow.material_type_id !== undefined && 
                                  cmoRow.material_type_id !== null && 
                                  cmoRow.material_type_id !== '') || 
                                 cmoRow._isExistingMaterial === true;
            if (isCmoExisting) {
              const cmoMaterialId = cmoRow._materialId || generateMaterialId();
              
              // Check if this CMO material is already in deleted list
              const alreadyTracked = updatedDeletedMaterials.some(m => m._materialId === cmoMaterialId);
              
              if (!alreadyTracked) {
                const deletedCmoMaterial = {
                  _materialId: cmoMaterialId,
                  _isExistingMaterial: true,
                  sponsorSupplyingMaterial: false,
                  material_brand: "",
                  materialTypeName: null,
                  material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
                  bo_material_code: cmoRow.bo_material_code || "",
                  material_description: cmoRow.material_description || "",
                  bo_material_UOM: "",
                  materialPlantIDFromSponsorERP: "",
                  materialStorageLocationIDsFromSponsorERP: "",
                  cmo_product_code: cmoRow.cmo_product_code || "",
                  cmo_material_code: cmoRow.cmo_material_code || "",
                  cmo_material_description: cmoRow.cmo_material_description || "",
                  cmo_material_UOM: cmoRow.cmo_material_UOM || "",
                  is_material_active: 1
                };
                
                updatedDeletedMaterials = [...updatedDeletedMaterials, deletedCmoMaterial];
              }
            }
          }
        });
        setCmoMaterialDetailsErrors(newCmoErrors);
        
        // Update deleted materials state if CMO materials were added
        setDeletedMaterials(updatedDeletedMaterials);
      }
    }
    
    // Merge sponsor and CMO data for parent update (linked rows)
    const cmoListToUse = updatedCmoList || cmoMaterialDetailsList;
    const mergedList = updatedList.map((sponsorItem, idx) => ({
      ...sponsorItem,
      cmo_product_code: cmoListToUse[idx]?.cmo_product_code || "",
      cmo_material_code: cmoListToUse[idx]?.cmo_material_code || "",
      cmo_material_description: cmoListToUse[idx]?.cmo_material_description || "",
      cmo_material_UOM: cmoListToUse[idx]?.cmo_material_UOM || "",
      is_material_active: 0 // Active materials
    }));
    
    // Get additional CMO rows (those beyond the sponsor materials count)
    const additionalCmoRows = cmoListToUse.slice(updatedList.length)
      .filter(cmoRow => {
        // Only include rows that have at least one non-empty field
        return cmoRow.bo_material_code?.trim() || 
               cmoRow.material_description?.trim() || 
               cmoRow.cmo_product_code?.trim() ||
               cmoRow.cmo_material_code?.trim() || 
               cmoRow.cmo_material_description?.trim() || 
               cmoRow.cmo_material_UOM?.trim();
      })
      .map(cmoRow => ({
        _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
        sponsorSupplyingMaterial: false, // Additional rows are CMO-only
        material_brand: "",
        materialTypeName: null,
        material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
        bo_material_code: cmoRow.bo_material_code || "",
        material_description: cmoRow.material_description || "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: cmoRow.cmo_product_code || "",
        cmo_material_code: cmoRow.cmo_material_code || "",
        cmo_material_description: cmoRow.cmo_material_description || "",
        cmo_material_UOM: cmoRow.cmo_material_UOM || "",
        is_material_active: 0 // Active additional CMO materials
      }));
    
    // Combine linked rows, additional CMO rows, deleted materials, and materials with changed codes
    // Use updatedDeletedMaterials to get the current deletion state
    const completeList = [...mergedList, ...additionalCmoRows, ...updatedDeletedMaterials, ...materialsWithChangedCodes];
    
    // Update parent formData with material details
    if (typeof updateStepData === 'function') {
      updateStepData('materialDetails', {
        productMaterialsList: completeList
      });
    }
  };

  // Check if any material row has all empty fields
  const hasMaterialEmptyRow = () => {
    return materialDetailsList.some(row => 
      !row.bo_material_code?.trim() && 
      !row.material_description?.trim() && 
      !row.material_brand?.trim() &&
      !row.bo_material_UOM?.trim()
    );
  };

  const handleMaterialDetailsChange = (index, field, value) => {
    // Use ref for most recent data (bypasses React state batching)
    // This prevents reading stale state when user adds material + selects type immediately
    const currentList = latestMaterialDetailsListRef.current || materialDetailsList;
    const updatedList = [...currentList];

    // Define regex patterns for material field validation
    const materialCodeRegex = /^[A-Za-z0-9\-_]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_)
    const materialDescriptionRegex = /^[A-Za-z0-9\-_.,() ]+$/; // Alphabets, Spaces, Numbers (0-9), Hyphen(-), Point(.), Comma(,), parentheses()
    const materialUOMRegex = /^[A-Za-z0-9\-_. ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), Period (.), Spaces

    // Initialize originalMaterialCodesRef for this index if it doesn't exist (for dynamically added rows)
    if (!originalMaterialCodesRef.current[index]) {
      originalMaterialCodesRef.current[index] = {
        id: updatedList[index]?.id || `material-${index}`,
        bo_material_code: updatedList[index]?.bo_material_code || "",
        cmo_material_code: cmoMaterialDetailsList[index]?.cmo_material_code || "",
        original_material: { ...updatedList[index] }
      };
    }
    
    // Check if bo_material_code is being changed and if it had a previous value
    if (field === 'bo_material_code' && originalMaterialCodesRef.current[index]) {
      const originalCode = originalMaterialCodesRef.current[index].bo_material_code;
      const currentCode = updatedList[index].bo_material_code;
      
      // Only track code changes for existing materials from API (not new rows)
      const originalMaterial = originalMaterialCodesRef.current[index].original_material;
      const isExistingMaterial = originalMaterial?.material_type_id !== undefined && 
                                originalMaterial?.material_type_id !== null && 
                                originalMaterial?.material_type_id !== '';
      
      // If the code is changing and had a non-empty original value AND is an existing material
      if (isExistingMaterial && originalCode && originalCode.trim() !== "" && originalCode !== value) {
        // Build the complete old material object (merged sponsor data + CMO data)
        const oldCmoData = cmoMaterialDetailsList[index];
        const materialId = updatedList[index]._materialId || updatedList[index].id || `material-${index}`;
        
        // Check if this is a CMO-only material
        const originalMaterial = originalMaterialCodesRef.current[index].original_material;
        // A material is CMO-only if it's either:
        // 1. Not explicitly marked as sponsor-linked (_isSponsorLinked === false), OR
        // 2. Has no materialTypeName (legacy check for backwards compatibility)
        const isCmoOnly = originalMaterial._isSponsorLinked === false || 
                         (!originalMaterial.materialTypeName || originalMaterial.materialTypeName.trim() === "");
        
        const oldMaterial = isCmoOnly ? {
          // For CMO-only materials, exclude sponsor-specific fields
          _materialId: materialId,
          _isNewMaterial: originalMaterial._isNewMaterial,
          sponsorSupplyingMaterial: false,
          material_brand: "",
          materialTypeName: null, // Set to null for payload (API requirement)
          material_type_id: originalMaterial.material_type_id !== undefined ? originalMaterial.material_type_id : "",
          bo_material_code: originalMaterial.bo_material_code || "",
          material_description: originalMaterial.material_description || "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: oldCmoData?.cmo_product_code || "",
          cmo_material_code: oldCmoData?.cmo_material_code || "",
          cmo_material_description: oldCmoData?.cmo_material_description || "",
          cmo_material_UOM: oldCmoData?.cmo_material_UOM || "",
          is_material_active: 1
        } : {
          // For sponsor materials, include all fields
          ...originalMaterialCodesRef.current[index].original_material,
          _materialId: materialId,
          cmo_product_code: oldCmoData?.cmo_product_code || "",
          cmo_material_code: oldCmoData?.cmo_material_code || "",
          cmo_material_description: oldCmoData?.cmo_material_description || "",
          cmo_material_UOM: oldCmoData?.cmo_material_UOM || "",
          is_material_active: 1
        };
        
        // Update or add to materialsWithChangedCodes based on _materialId
        setMaterialsWithChangedCodes(prev => {
          // Remove any existing entry for this material
          const filtered = prev.filter(m => m._materialId !== materialId);
          // Add the new old material entry
          return [...filtered, oldMaterial];
        });
        
        // Also track any CMO-only materials (additional rows) that reference this old material code
        // These materials need their old versions added because they're being updated with the new code
        const additionalCmoRowsToTrack = cmoMaterialDetailsList
          .slice(materialDetailsList.length) // Get only additional CMO rows
          .map((cmoRow, cmoIdx) => ({
            cmoRow,
            actualIndex: materialDetailsList.length + cmoIdx
          }))
          .filter(({ cmoRow }) => cmoRow.bo_material_code === originalCode); // Find rows referencing the old code
        
        if (additionalCmoRowsToTrack.length > 0) {
          setMaterialsWithChangedCodes(prev => {
            const newEntries = [];
            
            additionalCmoRowsToTrack.forEach(({ cmoRow, actualIndex }) => {
              const cmoMaterialId = cmoRow._materialId || cmoRow.id || `cmo-material-${actualIndex}`;
              
              // Check if this CMO material is already in the changed materials list
              // If it is, don't add it again - we already have its ORIGINAL version
              const alreadyTracked = prev.some(m => m._materialId === cmoMaterialId);
              
              if (alreadyTracked) {
                return; // Skip this material, it's already tracked with its original version
              }
              
              // Check if this CMO row is an existing material from API
              // Check if material has material_type_id from database OR if _isExistingMaterial flag is set
              const cmoOriginalMaterial = originalMaterialCodesRef.current[actualIndex]?.original_material;
              const isCmoExisting = (cmoOriginalMaterial?.material_type_id !== undefined && 
                                    cmoOriginalMaterial?.material_type_id !== null && 
                                    cmoOriginalMaterial?.material_type_id !== '') ||
                                   cmoOriginalMaterial?._isExistingMaterial === true || 
                                   (cmoRow.material_type_id !== undefined && 
                                    cmoRow.material_type_id !== null && 
                                    cmoRow.material_type_id !== '') ||
                                   cmoRow._isExistingMaterial === true;
              
              if (isCmoExisting) {
                // Build old CMO material with ORIGINAL sponsor material code (from originalMaterialCodesRef)
                const oldCmoMaterial = {
                  _materialId: cmoMaterialId,
                  _isNewMaterial: cmoOriginalMaterial?._isNewMaterial || false,
                  _isExistingMaterial: true,
                  sponsorSupplyingMaterial: false,
                  material_brand: "",
                  materialTypeName: null, // Set to null for payload (API requirement)
                  material_type_id: cmoOriginalMaterial?.material_type_id !== undefined ? cmoOriginalMaterial.material_type_id : "",
                  bo_material_code: cmoOriginalMaterial?.bo_material_code || "",
                  material_description: cmoOriginalMaterial?.material_description || "",
                  bo_material_UOM: "",
                  materialPlantIDFromSponsorERP: "",
                  materialStorageLocationIDsFromSponsorERP: "",
                  cmo_product_code: cmoOriginalMaterial?.cmo_product_code || cmoRow.cmo_product_code || "",
                  cmo_material_code: cmoOriginalMaterial?.cmo_material_code || cmoRow.cmo_material_code || "",
                  cmo_material_description: cmoOriginalMaterial?.cmo_material_description || cmoRow.cmo_material_description || "",
                  cmo_material_UOM: cmoOriginalMaterial?.cmo_material_UOM || cmoRow.cmo_material_UOM || "",
                  is_material_active: 1
                };
                
                newEntries.push(oldCmoMaterial);
              }
            });
            
            if (newEntries.length > 0) {
              // Add new entries without filtering - we already checked they don't exist
              return [...prev, ...newEntries];
            }
            
            return prev;
          });
        }
        
        // DO NOT update the original code reference - keep the original value
        // This ensures we always track against the initial loaded value
      }
    }
    
    // Special handling for material type - value is materialTypeID
    if (field === 'materialTypeName') {
      const selectedMaterialType = availableMaterialTypes.find(mt => mt.materialTypeID === value);
      
      if (selectedMaterialType) {
        updatedList[index] = {
          ...updatedList[index],
          materialTypeName: selectedMaterialType.materialTypeName,
          material_type_id: selectedMaterialType.materialTypeID
        };
      } else {
        // Fallback: If material type not found in availableMaterialTypes,
        // still set the material_type_id (important for update flow)
        updatedList[index] = {
          ...updatedList[index],
          material_type_id: value || null,
          materialTypeName: null // Will be resolved by backend or on next render
        };
      }
    } else {
      updatedList[index] = {
        ...updatedList[index],
        [field]: value
      };
    }
    
    
    setMaterialDetailsList(updatedList);
    // Update ref immediately (bypasses React state batching)
    latestMaterialDetailsListRef.current = updatedList;
    


    // Clear table-level error when user starts filling in data
    if (materialTableError && value && value.toString().trim()) {
      setMaterialTableError("");
    }

    // Add value to dropdown options if it doesn't exist
    if (field === 'material_brand' && value && value.trim() && !materialBrandOptions.includes(value)) {
      setMaterialBrandOptions(prev => addUniqueToOptions(prev, [value]));
    }
    if (field === 'materialTypeName' && value) {
      const selectedMaterialType = availableMaterialTypes.find(mt => mt.materialTypeID === value);
      if (selectedMaterialType && selectedMaterialType.materialTypeName && selectedMaterialType.materialTypeName.trim() && !materialTypeOptions.includes(selectedMaterialType.materialTypeName)) {
        setMaterialTypeOptions(prev => addUniqueToOptions(prev, [selectedMaterialType.materialTypeName]));
      }
    }
    if (field === 'materialPlantIDFromSponsorERP' && value && value.trim() && !materialPlantIDOptions.includes(value)) {
      setMaterialPlantIDOptions(prev => addUniqueToOptions(prev, [value]));
    }
    if (field === 'materialStorageLocationIDsFromSponsorERP') {
      // Handle both string and array values
      const storageIDs = typeof value === 'string' 
        ? value.split(',').map(s => s.trim()).filter(Boolean) 
        : (Array.isArray(value) ? value : []);
      
      // Add any new storage IDs to options
      const newIDs = storageIDs.filter(id => !materialStorageLocationIDOptions.includes(id));
      if (newIDs.length > 0) {
        setMaterialStorageLocationIDOptions(prev => addUniqueToOptions(prev, newIDs));
      }
    }

    // Validation
    const newErrors = { ...materialDetailsErrors };
    const errorKey = `${field}-${index}`;
    
    // Validation for bo_material_code
    if (field === 'bo_material_code') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = 'Material Code is required';
      } else if (!materialCodeRegex.test(value)) {
        newErrors[errorKey] = 'Material Code can only contain alphabets, numbers, hyphens (-), and underscores (_)';
      } else {
        delete newErrors[errorKey];
      }
    }
    
    // Validation for material_description
    if (field === 'material_description') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = 'Material Description is required';
      } else if (!materialDescriptionRegex.test(value)) {
        newErrors[errorKey] = 'Material Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), and parentheses ()';
      } else {
        delete newErrors[errorKey];
      }
    }
    
    // Validation for bo_material_UOM
    if (field === 'bo_material_UOM') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = 'Material UOM is required';
      } else if (!materialUOMRegex.test(value)) {
        newErrors[errorKey] = 'Material UOM can only contain alphabets, numbers, hyphens (-), underscores (_), periods (.), and spaces';
      } else {
        delete newErrors[errorKey];
      }
    }
    
    // Validation for material_brand
    if (field === 'material_brand') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = 'Material Brand is required';
      } else {
        delete newErrors[errorKey];
      }
    }
    
    // Validation for materialTypeName
    if (field === 'materialTypeName') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = 'Material Type is required';
      } else {
        delete newErrors[errorKey];
      }
    }
    
    // Validation for materialPlantIDFromSponsorERP
    if (field === 'materialPlantIDFromSponsorERP') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = 'Material Plant ID From Sponsor ERP is required';
      } else {
        delete newErrors[errorKey];
      }
    }
    
    // Validation for materialStorageLocationIDsFromSponsorERP - No longer mandatory
    if (field === 'materialStorageLocationIDsFromSponsorERP') {
      // Remove any existing error for this field
      delete newErrors[errorKey];
    }
    
    setMaterialDetailsErrors(newErrors);
    
    // Update CMO materials list when sponsor material code or description changes
    if (field === 'bo_material_code' || field === 'material_description') {
      const updatedCmoList = [...cmoMaterialDetailsList];
      if (updatedCmoList[index]) {
        updatedCmoList[index] = {
          ...updatedCmoList[index],
          bo_material_code: field === 'bo_material_code' ? value : updatedList[index].bo_material_code,
          material_description: field === 'material_description' ? value : updatedList[index].material_description
        };
        setCmoMaterialDetailsList(updatedCmoList);
        
        // Clear the corresponding CMO material error when sponsor material code is filled
        if (field === 'bo_material_code' && value && value.trim() !== "") {
          const newCmoErrors = { ...cmoMaterialDetailsErrors };
          delete newCmoErrors[`bo_material_code-${index}`];
          setCmoMaterialDetailsErrors(newCmoErrors);
        }
      }
    }
    
    // Merge sponsor and CMO data for parent update (linked rows)
    const mergedList = updatedList.map((sponsorItem, idx) => ({
      ...sponsorItem,
      cmo_product_code: cmoMaterialDetailsList[idx]?.cmo_product_code || "",
      cmo_material_code: cmoMaterialDetailsList[idx]?.cmo_material_code || "",
      cmo_material_description: cmoMaterialDetailsList[idx]?.cmo_material_description || "",
      cmo_material_UOM: cmoMaterialDetailsList[idx]?.cmo_material_UOM || "",
      is_material_active: 0, // Active materials
      _materialId: sponsorItem._materialId, // Preserve unique ID
      _isNewMaterial: sponsorItem._isNewMaterial // Preserve new material flag
    }));
    
    // Get additional CMO rows (those beyond the sponsor materials count)
    const additionalCmoRows = cmoMaterialDetailsList.slice(updatedList.length)
      .filter(cmoRow => {
        // Only include rows that have at least one non-empty field
        return cmoRow.bo_material_code?.trim() || 
               cmoRow.material_description?.trim() || 
               cmoRow.cmo_product_code?.trim() ||
               cmoRow.cmo_material_code?.trim() || 
               cmoRow.cmo_material_description?.trim() || 
               cmoRow.cmo_material_UOM?.trim();
      })
      .map(cmoRow => ({
        _materialId: cmoRow._materialId || cmoRow.id, // Preserve unique ID
        _isNewMaterial: cmoRow._isNewMaterial, // Preserve new material flag
        _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
        sponsorSupplyingMaterial: false, // Additional rows are CMO-only
        material_brand: "",
        materialTypeName: null,
        material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
        bo_material_code: cmoRow.bo_material_code || "",
        material_description: cmoRow.material_description || "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: cmoRow.cmo_product_code || "",
        cmo_material_code: cmoRow.cmo_material_code || "",
        cmo_material_description: cmoRow.cmo_material_description || "",
        cmo_material_UOM: cmoRow.cmo_material_UOM || "",
        is_material_active: 0 // Active additional CMO materials
      }));
    
    // Combine all materials and deduplicate in a single pass
    // Priority: Active materials from mergedList/additionalCmoRows, then deleted materials
    const allMaterials = [...mergedList, ...additionalCmoRows, ...deletedMaterials, ...materialsWithChangedCodes];
    const uniqueMaterialsMap = new Map();
    
    allMaterials.forEach(material => {
      ensureMaterialId(material);
      
      // Use material codes as unique key (material_type_id is NOT unique - it's the type name)
      const keyParts = [
        material.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
        material.bo_material_code || '',
        material.cmo_material_code || ''
      ].filter(Boolean);
      const baseKey = keyParts.join('|');
      
      // Use different keys for active vs deleted to allow both versions
      const finalKey = material.is_material_active === 1 ? `${baseKey}_deleted` : `${baseKey}_active`;
      
      // Only add if not already present (first occurrence wins)
      if (!uniqueMaterialsMap.has(finalKey)) {
        uniqueMaterialsMap.set(finalKey, material);
      } else {
        // If duplicate exists for deleted materials, prefer one WITH material_type_id
        const existing = uniqueMaterialsMap.get(finalKey);
        const existingHasTypeId = existing.material_type_id !== undefined && existing.material_type_id !== null && existing.material_type_id !== '';
        const materialHasTypeId = material.material_type_id !== undefined && material.material_type_id !== null && material.material_type_id !== '';
        
        // Replace if: it's a deleted material AND new one has type ID but existing doesn't
        if (material.is_material_active === 1 && !existingHasTypeId && materialHasTypeId) {
          uniqueMaterialsMap.set(finalKey, material);
        }
      }
    });
    
    const completeList = Array.from(uniqueMaterialsMap.values())
      .map(material => {
        const { _trackingId, _materialId, id, _isNewMaterial, _isSponsorLinked, _isExistingMaterial, ...cleanMaterial } = material;
        
        // For new materials (without material_type_id from DB), omit material_type_id field
        if (_isNewMaterial || (!material.material_type_id && material.material_type_id !== 0)) {
          const { material_type_id, ...withoutTypeId } = cleanMaterial;
          return withoutTypeId;
        }
        
        // For existing materials, keep material_type_id even if null
        return cleanMaterial;
      });
    
    
 
    
    // Update parent formData with material details
    if (typeof updateStepData === 'function') {
      updateStepData('materialDetails', {
        productMaterialsList: completeList
      });
    }
  };

  // CMO Material Details handlers
  const handleAddCmoMaterialRow = () => {
    const newMaterialId = generateMaterialId();
    const newRow = {
      _materialId: newMaterialId,
      _isNewMaterial: true, // Track that this is a new material
      _isExistingMaterial: false, // Not from API
      _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
      id: newMaterialId, // Unique ID for React key stability
      cmo_product_code: cmoProductDetailsList?.[0]?.cmoProductCode || "",
      bo_material_code: "",
      material_description: "",
      cmo_material_code: "",
      cmo_material_description: "",
      cmo_material_UOM: ""
    };
    
    const updatedList = [...cmoMaterialDetailsList, newRow];
    setCmoMaterialDetailsList(updatedList);
    
    // Initialize originalMaterialCodesRef for this new row
    const newIndex = updatedList.length - 1;
    if (!originalMaterialCodesRef.current[newIndex]) {
      originalMaterialCodesRef.current[newIndex] = {
        id: newMaterialId,
        bo_material_code: "",
        cmo_material_code: "",
        original_material: { ...newRow }
      };
    }
    
    // IMPORTANT: Sync the new row to parent formData immediately
    // Without this, empty/partial CMO rows can get added to the payload
    // Merge sponsor and CMO data for parent update (linked rows)
    const mergedList = materialDetailsList.map((sponsorItem, idx) => ({
      ...sponsorItem,
      cmo_product_code: updatedList[idx]?.cmo_product_code || "",
      cmo_material_code: updatedList[idx]?.cmo_material_code || "",
      cmo_material_description: updatedList[idx]?.cmo_material_description || "",
      cmo_material_UOM: updatedList[idx]?.cmo_material_UOM || "",
      is_material_active: 0, // Active materials
      _materialId: sponsorItem._materialId, // Preserve unique ID
      _isNewMaterial: sponsorItem._isNewMaterial // Preserve new material flag
    }));
    
    // Get additional CMO rows (those beyond the sponsor materials count)
    // FILTER OUT EMPTY ROWS - this is the key fix to prevent partial/empty rows in payload
    const additionalCmoRows = updatedList.slice(materialDetailsList.length)
      .filter(cmoRow => {
        // Only include rows that have at least one non-empty field
        return cmoRow.bo_material_code?.trim() || 
               cmoRow.material_description?.trim() || 
               cmoRow.cmo_product_code?.trim() ||
               cmoRow.cmo_material_code?.trim() || 
               cmoRow.cmo_material_description?.trim() || 
               cmoRow.cmo_material_UOM?.trim();
      })
      .map(cmoRow => ({
        _materialId: cmoRow._materialId || cmoRow.id, // Preserve unique ID
        _isNewMaterial: cmoRow._isNewMaterial, // Preserve new material flag
        _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
        sponsorSupplyingMaterial: false, // Additional rows are CMO-only
        material_brand: "",
        materialTypeName: null,
        material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
        bo_material_code: cmoRow.bo_material_code || "",
        material_description: cmoRow.material_description || "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: cmoRow.cmo_product_code || "",
        cmo_material_code: cmoRow.cmo_material_code || "",
        cmo_material_description: cmoRow.cmo_material_description || "",
        cmo_material_UOM: cmoRow.cmo_material_UOM || "",
        is_material_active: 0 // Active additional CMO materials
      }));
    
    // Combine linked rows, additional CMO rows, deleted materials, and materials with changed codes
    const completeList = [...mergedList, ...additionalCmoRows, ...deletedMaterials, ...materialsWithChangedCodes];
    
    // Update parent formData with material details
    if (typeof updateStepData === 'function') {
      updateStepData('materialDetails', {
        productMaterialsList: completeList
      });
    }
    
    // Note: Only updating CMO materials list, not adding to sponsor materials list
    // This allows adding additional CMO materials without affecting sponsor materials
  };

  const handleDeleteCmoMaterialRow = (index) => {
    // Track the updated deleted materials list for immediate use
    let updatedDeletedMaterials = [...deletedMaterials];
    
    // Store the material being deleted to track for payload (only for additional CMO rows)
    if (index >= materialDetailsList.length) {
      const cmoMaterialToDelete = cmoMaterialDetailsList[index];
      
      // Get original CMO data from ref if available (to track original values, not edited values)
      const originalCmoData = originalMaterialCodesRef.current?.[index]?.original_material || cmoMaterialToDelete;
      
      // Only add to deleted list if this is an existing material from the API
      // Check if material has material_type_id from database OR if _isExistingMaterial flag is set
      // Also check if is_material_active is defined (0 or 1) - this indicates material came from API
      const isExistingMaterial = (originalCmoData?.material_type_id !== undefined && 
                                  originalCmoData?.material_type_id !== null && 
                                  originalCmoData?.material_type_id !== '') || 
                                 originalCmoData?._isExistingMaterial === true ||
                                 originalCmoData?.is_material_active !== undefined;
      
      // If this is an existing additional CMO material, add to deleted list
      if (isExistingMaterial && originalCmoData && 
          (originalCmoData.cmo_material_code?.trim() || 
           originalCmoData.bo_material_code?.trim() || 
           originalCmoData.material_description?.trim())) {
        const deletedRecord = {
          _materialId: originalCmoData._materialId || originalCmoData.id || cmoMaterialToDelete._materialId || cmoMaterialToDelete.id,
          _isNewMaterial: originalCmoData._isNewMaterial, // Preserve new material flag
          sponsorSupplyingMaterial: false,
          material_brand: "",
          materialTypeName: null, // Set to null for payload (API requirement)
          material_type_id: originalCmoData.material_type_id !== undefined ? originalCmoData.material_type_id : "",
          bo_material_code: originalCmoData.bo_material_code || "",
          material_description: originalCmoData.material_description || "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: originalCmoData.cmo_product_code || "",
          cmo_material_code: originalCmoData.cmo_material_code || "",
          cmo_material_description: originalCmoData.cmo_material_description || "",
          cmo_material_UOM: originalCmoData.cmo_material_UOM || "",
          is_material_active: 1
        };
        // Ensure deleted record has unique ID
        ensureMaterialId(deletedRecord);
        
        // Check if this material is already in deleted list using unique combination of material codes
        // NOTE: material_type_id is NOT unique - it represents the category (e.g., "Bulk DP")
        // Multiple materials can have the same material_type_id, so we must use material codes
        const uniqueKey = [
          deletedRecord.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
          deletedRecord.bo_material_code || '',
          deletedRecord.cmo_material_code || '',
          deletedRecord.material_type_id || 'null'
        ].filter(Boolean).join('|');
        
        // Check if already exists with same unique key
        const exists = updatedDeletedMaterials.some(m => {
          const existingKey = [
            m.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
            m.bo_material_code || '',
            m.cmo_material_code || '',
            m.material_type_id || 'null'
          ].filter(Boolean).join('|');
          return existingKey === uniqueKey;
        });
        
        if (!exists) {
          updatedDeletedMaterials = [...updatedDeletedMaterials, deletedRecord];
        }
        
        // Update state with the new deleted materials list
        setDeletedMaterials(updatedDeletedMaterials);
      }
    }
    
    let updatedList;
    
    if (cmoMaterialDetailsList.length > materialDetailsList.length) {
      // Only allow deletion if there are more CMO rows than sponsor rows
      updatedList = cmoMaterialDetailsList.filter((_, idx) => idx !== index);
      setCmoMaterialDetailsList(updatedList);
      // Clear errors for deleted row AND reindex errors for rows that shifted up
      const newErrors = {};
      Object.keys(cmoMaterialDetailsErrors).forEach(key => {
        const [fieldName, rowIndex] = key.split('-');
        const rowIdx = parseInt(rowIndex, 10);
        
        if (rowIdx === index) {
          // Delete error for the deleted row
          return;
        } else if (rowIdx > index) {
          // Reindex errors for rows after the deleted row (they shift up by 1)
          newErrors[`${fieldName}-${rowIdx - 1}`] = cmoMaterialDetailsErrors[key];
        } else {
          // Keep errors for rows before the deleted row
          newErrors[key] = cmoMaterialDetailsErrors[key];
        }
      });
      setCmoMaterialDetailsErrors(newErrors);
    } else if (index >= materialDetailsList.length) {
      // Clear the row if it's an additional CMO row
      updatedList = [...cmoMaterialDetailsList];
      updatedList[index] = {
        cmo_product_code: "",
        bo_material_code: "",
        material_description: "",
        cmo_material_code: "",
        cmo_material_description: "",
        cmo_material_UOM: ""
      };
      setCmoMaterialDetailsList(updatedList);
      // Clear errors
      const newErrors = { ...cmoMaterialDetailsErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.endsWith(`-${index}`)) {
          delete newErrors[key];
        }
      });
      setCmoMaterialDetailsErrors(newErrors);
    }
    
    // Sync changes to parent formData
    if (updatedList) {
      // Merge sponsor and CMO data for parent update (linked rows)
      const mergedList = materialDetailsList.map((sponsorItem, idx) => ({
        ...sponsorItem,
        cmo_product_code: updatedList[idx]?.cmo_product_code || "",
        cmo_material_code: updatedList[idx]?.cmo_material_code || "",
        cmo_material_description: updatedList[idx]?.cmo_material_description || "",
        cmo_material_UOM: updatedList[idx]?.cmo_material_UOM || "",
        is_material_active: 0 // Active materials
      }));
      
      // Get additional CMO rows (those beyond the sponsor materials count)
      const additionalCmoRows = updatedList.slice(materialDetailsList.length)
        .filter(cmoRow => {
          // Only include rows that have at least one non-empty field
          return cmoRow.bo_material_code?.trim() || 
                 cmoRow.material_description?.trim() || 
                 cmoRow.cmo_product_code?.trim() ||
                 cmoRow.cmo_material_code?.trim() || 
                 cmoRow.cmo_material_description?.trim() || 
                 cmoRow.cmo_material_UOM?.trim();
        })
        .map(cmoRow => ({
          _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
          sponsorSupplyingMaterial: false, // Additional rows are CMO-only
          material_brand: "",
          materialTypeName: null,
          material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
          bo_material_code: cmoRow.bo_material_code || "",
          material_description: cmoRow.material_description || "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: cmoRow.cmo_product_code || "",
          cmo_material_code: cmoRow.cmo_material_code || "",
          cmo_material_description: cmoRow.cmo_material_description || "",
          cmo_material_UOM: cmoRow.cmo_material_UOM || "",
          is_material_active: 0 // Active additional CMO materials
        }));
      
      // Combine linked rows, additional CMO rows, deleted materials, and materials with changed codes
      // Use updatedDeletedMaterials to get the current deletion state
      const completeList = [...mergedList, ...additionalCmoRows, ...updatedDeletedMaterials, ...materialsWithChangedCodes];
      
      // Update parent formData with material details
      if (typeof updateStepData === 'function') {
        updateStepData('materialDetails', {
          productMaterialsList: completeList
        });
      }
    }
  };

  const handleCmoMaterialDetailsChange = (index, field, value) => {
    const isAdditionalRow = isAdditionalCmoRow(index, materialDetailsList.length);
    
    // Define regex patterns for CMO material field validation
    const cmoMaterialCodeRegex = /^[A-Za-z0-9\-_/]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/)
    const cmoMaterialDescriptionRegex = /^[A-Za-z0-9\-_.,()&/ ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/), Ampersand (&), Parentheses(), Period (.), Comma (,), Spaces
    const cmoMaterialUOMRegex = /^[A-Za-z0-9\-_. ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), Period (.), Spaces
    
    // Initialize originalMaterialCodesRef for this index if it doesn't exist (for dynamically added rows)
    if (!originalMaterialCodesRef.current[index]) {
      const currentCmoData = cmoMaterialDetailsList[index];
      originalMaterialCodesRef.current[index] = {
        id: currentCmoData?.id || `material-${index}`,
        bo_material_code: currentCmoData?.bo_material_code || "",
        cmo_material_code: currentCmoData?.cmo_material_code || "",
        original_material: { ...currentCmoData }
      };
    }
    
    // Check if cmo_material_code is being changed and if it had a previous value
    if (field === 'cmo_material_code' && originalMaterialCodesRef.current[index]) {
      const originalCode = originalMaterialCodesRef.current[index].cmo_material_code;
      
      // Only track code changes for existing materials from API (not new rows)
      const originalMaterial = originalMaterialCodesRef.current[index].original_material;
      // Check if material has material_type_id from database OR if _isExistingMaterial flag is set
      // This handles both sponsor materials (with material_type_id) and CMO-only materials (with _isExistingMaterial flag)
      // Also check if is_material_active is defined (0 or 1) - this indicates material came from API
      const isExistingMaterial = (originalMaterial?.material_type_id !== undefined && 
                                 originalMaterial?.material_type_id !== null && 
                                 originalMaterial?.material_type_id !== '') || 
                                originalMaterial?._isExistingMaterial === true ||
                                originalMaterial?.is_material_active !== undefined;
      
      // If the code is changing and had a non-empty original value AND is an existing material
      if (isExistingMaterial && originalCode && originalCode.trim() !== "" && originalCode !== value) {
        // Build the complete old material object (merged sponsor + old CMO data)
        const sponsorData = !isAdditionalRow ? materialDetailsList[index] : {};
        const oldCmoData = cmoMaterialDetailsList[index];
        const materialId = oldCmoData?._materialId || oldCmoData?.id || originalMaterialCodesRef.current[index].id || `cmo-material-${index}`;
        
        // Check if this is a CMO-only material - use original material from ref
        const originalMaterial = originalMaterialCodesRef.current[index].original_material;
        // A material is CMO-only if it's either:
        // 1. Not explicitly marked as sponsor-linked (_isSponsorLinked === false), OR
        // 2. Has no materialTypeName (legacy check for backwards compatibility)
        const isCmoOnly = originalMaterial?._isSponsorLinked === false || 
                         (!originalMaterial?.materialTypeName || originalMaterial.materialTypeName.trim() === "");
        
        const oldMaterial = isCmoOnly ? {
          // For CMO-only materials, exclude sponsor-specific fields
          // USE ORIGINAL MATERIAL DATA ENTIRELY - do not fall back to current state
          _materialId: materialId,
          _isNewMaterial: originalMaterial?._isNewMaterial,
          sponsorSupplyingMaterial: false,
          material_brand: "",
          materialTypeName: null,
          material_type_id: originalMaterial?.material_type_id !== undefined ? originalMaterial.material_type_id : "",
          bo_material_code: originalMaterial?.bo_material_code || "",
          material_description: originalMaterial?.material_description || "",
          bo_material_UOM: "",
          materialPlantIDFromSponsorERP: "",
          materialStorageLocationIDsFromSponsorERP: "",
          cmo_product_code: originalMaterial?.cmo_product_code || "",
          cmo_material_code: originalCode,
          cmo_material_description: originalMaterial?.cmo_material_description || "",
          cmo_material_UOM: originalMaterial?.cmo_material_UOM || "",
          is_material_active: 1
        } : {
          // For sponsor materials, include all fields from original material
          // DO NOT use current state data - always use original_material
          ...sponsorData,
          ...originalMaterialCodesRef.current[index].original_material,
          _materialId: materialId,
          cmo_product_code: originalMaterial?.cmo_product_code || "",
          cmo_material_code: originalCode,
          cmo_material_description: originalMaterial?.cmo_material_description || "",
          cmo_material_UOM: originalMaterial?.cmo_material_UOM || "",
          is_material_active: 1
        };
        
        // Update or add to materialsWithChangedCodes based on _materialId
        setMaterialsWithChangedCodes(prev => {
          // Remove any existing entry for this material
          const filtered = prev.filter(m => m._materialId !== materialId);
          // Add the new old material entry
          return [...filtered, oldMaterial];
        });
        
        // DO NOT update the original code reference - keep the original value
        // This ensures we always track against the initial loaded value
      }
    }
    
    setCmoMaterialDetailsList(prevList => {
      const updatedList = [...prevList];
      
      // If changing material code dropdown in additional row, auto-populate description and material_type_id
      if (field === 'bo_material_code' && isAdditionalRow) {
        const selectedMaterial = materialDetailsList.find(m => m.bo_material_code === value);
        updatedList[index] = {
          ...updatedList[index],
          bo_material_code: value,
          material_description: selectedMaterial?.material_description || "",
          material_type_id: selectedMaterial?.material_type_id !== undefined ? selectedMaterial.material_type_id : ""
        };
      } else {
        // Update the field value while preserving all other fields including id
        updatedList[index] = {
          ...updatedList[index],
          [field]: value
        };
      }
      
      // Sync to parent for both linked and additional rows
      // Debounce parent updates to avoid excessive re-renders
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
      
      // Validation for CMO material fields
      const newErrors = { ...cmoMaterialDetailsErrors };
      const errorKey = `${field}-${index}`;
      
      // Validation for cmo_material_code
      if (field === 'cmo_material_code') {
        if (!value || (typeof value === 'string' && value.trim() === "")) {
          newErrors[errorKey] = 'CMO Material Code is required';
        } else if (!cmoMaterialCodeRegex.test(value)) {
          newErrors[errorKey] = 'CMO Material Code can only contain alphabets, numbers, hyphens (-), underscores (_), and slashes (/)';
        } else {
          delete newErrors[errorKey];
        }
      }
      
      // Validation for cmo_material_description
      if (field === 'cmo_material_description') {
        if (!value || (typeof value === 'string' && value.trim() === "")) {
          newErrors[errorKey] = 'CMO Material Description is required';
        } else if (!cmoMaterialDescriptionRegex.test(value)) {
          newErrors[errorKey] = 'CMO Material Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)';
        } else {
          delete newErrors[errorKey];
        }
      }
      
      // Validation for cmo_material_UOM
      if (field === 'cmo_material_UOM') {
        if (!value || (typeof value === 'string' && value.trim() === "")) {
          newErrors[errorKey] = 'CMO Material UOM is required';
        } else if (!cmoMaterialUOMRegex.test(value)) {
          newErrors[errorKey] = 'CMO Material UOM can only contain alphabets, numbers, hyphens (-), underscores (_), periods (.), and spaces';
        } else {
          delete newErrors[errorKey];
        }
      }
      
      setCmoMaterialDetailsErrors(newErrors);

      syncTimerRef.current = setTimeout(() => {
        // Merge sponsor and CMO data for parent update (linked rows)
        const mergedList = materialDetailsList.map((sponsorItem, idx) => ({
          ...sponsorItem,
          cmo_product_code: updatedList[idx]?.cmo_product_code || "",
          cmo_material_code: updatedList[idx]?.cmo_material_code || "",
          cmo_material_description: updatedList[idx]?.cmo_material_description || "",
          cmo_material_UOM: updatedList[idx]?.cmo_material_UOM || "",
          is_material_active: 0, // Active materials
          _materialId: sponsorItem._materialId, // Preserve unique ID
          _isNewMaterial: sponsorItem._isNewMaterial // Preserve new material flag
        }));
        
        // Get additional CMO rows (those beyond the sponsor materials count)
        const additionalCmoRows = updatedList.slice(materialDetailsList.length)
          .filter(cmoRow => {
            // Only include rows that have at least one non-empty field
            return cmoRow.bo_material_code?.trim() || 
                   cmoRow.material_description?.trim() || 
                   cmoRow.cmo_product_code?.trim() ||
                   cmoRow.cmo_material_code?.trim() || 
                   cmoRow.cmo_material_description?.trim() || 
                   cmoRow.cmo_material_UOM?.trim();
          })
          .map(cmoRow => ({
            _materialId: cmoRow._materialId || cmoRow.id, // Preserve unique ID
            _isNewMaterial: cmoRow._isNewMaterial, // Preserve new material flag
            _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
            sponsorSupplyingMaterial: false, // Additional rows are CMO-only
            material_brand: "",
            materialTypeName: null,
            material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : "",
            bo_material_code: cmoRow.bo_material_code || "",
            material_description: cmoRow.material_description || "",
            bo_material_UOM: "",
            materialPlantIDFromSponsorERP: "",
            materialStorageLocationIDsFromSponsorERP: "",
            cmo_product_code: cmoRow.cmo_product_code || "",
            cmo_material_code: cmoRow.cmo_material_code || "",
            cmo_material_description: cmoRow.cmo_material_description || "",
            cmo_material_UOM: cmoRow.cmo_material_UOM || "",
            is_material_active: 0 // Active additional CMO materials
          }));
        
        // Combine all materials and deduplicate in a single pass
        const allMaterials = [...mergedList, ...additionalCmoRows, ...deletedMaterials, ...materialsWithChangedCodes];
        const uniqueMaterialsMap = new Map();
        
        allMaterials.forEach(material => {
          ensureMaterialId(material);
          
          // Use material codes as unique key (material_type_id is NOT unique - it's the type name)
          const keyParts = [
            material.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
            material.bo_material_code || '',
            material.cmo_material_code || ''
          ].filter(Boolean);
          const baseKey = keyParts.join('|');
          
          // Use different keys for active vs deleted to allow both versions
          const finalKey = material.is_material_active === 1 ? `${baseKey}_deleted` : `${baseKey}_active`;
          
          // Only add if not already present (first occurrence wins)
          if (!uniqueMaterialsMap.has(finalKey)) {
            uniqueMaterialsMap.set(finalKey, material);
          } else {
            // If duplicate exists for deleted materials, prefer one WITH material_type_id
            const existing = uniqueMaterialsMap.get(finalKey);
            const existingHasTypeId = existing.material_type_id !== undefined && existing.material_type_id !== null && existing.material_type_id !== '';
            const materialHasTypeId = material.material_type_id !== undefined && material.material_type_id !== null && material.material_type_id !== '';
            
            // Replace if: it's a deleted material AND new one has type ID but existing doesn't
            if (material.is_material_active === 1 && !existingHasTypeId && materialHasTypeId) {
              uniqueMaterialsMap.set(finalKey, material);
            }
          }
        });
        
        const completeList = Array.from(uniqueMaterialsMap.values())
          .map(material => {
            // Remove internal tracking fields before sending to API
            const { _trackingId, _materialId, id, _isNewMaterial, _isSponsorLinked, _isExistingMaterial, ...cleanMaterial } = material;
            
            // For new materials (without material_type_id from DB), omit material_type_id field
            if (_isNewMaterial || (!material.material_type_id && material.material_type_id !== 0)) {
              const { material_type_id, ...withoutTypeId } = cleanMaterial;
              return withoutTypeId;
            }
            
            // For existing materials, keep material_type_id even if null
            return cleanMaterial;
          });
        
        // Update parent formData
        if (typeof updateStepData === 'function') {
          updateStepData('materialDetails', {
            productMaterialsList: completeList
          });
        }
      }, 500);
      
      // Update ref immediately (bypasses React state batching)
      latestCmoMaterialDetailsListRef.current = updatedList;
      
      return updatedList;
    });
    
    // Validation
    const newErrors = { ...cmoMaterialDetailsErrors };
    const errorKey = `${field}-${index}`;
    
    if (field === 'cmo_product_code' || field === 'bo_material_code' || field === 'cmo_material_code' || 
        field === 'cmo_material_description' || field === 'cmo_material_UOM') {
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        newErrors[errorKey] = `${field === 'cmo_product_code' ? 'CMO Product Code' : 
                                 field === 'bo_material_code' ? 'Material Code' : 
                                 field === 'cmo_material_code' ? 'CMO Material Code' : 
                                 field === 'cmo_material_description' ? 'CMO Material Description' : 
                                 'CMO Material UOM'} is required`;
      } else {
        delete newErrors[errorKey];
        
        // Additional validation for bo_material_code in additional rows - check if it exists
        if (field === 'bo_material_code' && isAdditionalCmoRow(index, materialDetailsList.length)) {
          if (!isSponsorMaterialCodeValid(value, materialDetailsList)) {
            newErrors[errorKey] = ERROR_MESSAGES.SPONSOR_MATERIAL_CODE_DELETED;
          }
        }
      }
    }
    
    setCmoMaterialDetailsErrors(newErrors);
  };

  const handleCmoProductChange = (index, field, value) => {
    const updatedList = [...cmoProductDetailsList];
    updatedList[index] = {
      ...updatedList[index],
      [field]: value
    };
    setCmoProductDetailsList(updatedList);

    // Clear table-level error when user starts filling in data
    if (cmoTableError && value.trim()) {
      setCmoTableError("");
    }

    // Validation regex
    const cmoProductCodeRegex = /^[A-Za-z0-9\-_/ ]+$/;
    const cmoProductDescriptionRegex = /^[A-Za-z0-9\-_.,()&/ ]+$/;

    // Validate on change
    const newErrors = { ...cmoProductErrors };
    const errorKey = `${field}-${index}`;
    
    if (!value || value.trim() === "") {
      newErrors[errorKey] = `${field === 'cmoProductCode' ? 'CMO Product Code' : field === 'cmoProductDescription' ? 'CMO Product Description' : 'CMO UOM'} is required`;
    } else if (field === 'cmoProductCode' && !cmoProductCodeRegex.test(value)) {
      newErrors[errorKey] = "CMO Product Code can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), and slashes (/)";
    } else if (field === 'cmoProductDescription' && !cmoProductDescriptionRegex.test(value)) {
      newErrors[errorKey] = "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
    } else {
      delete newErrors[errorKey];
    }
    
    setCmoProductErrors(newErrors);
  };

  // Opens the dialog for adding a new brand
  const handleOpenBrandDialog = (event) => {
    event.preventDefault();
    setOpenBrandDialog(true);
  };

  // Closes the brand dialog and resets its state
  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setNewBrand("");
    setBrandError("");
  };

  // Adds a new brand to the brands list and selects it
  const handleAddNewBrand = () => {
    // Validate that brand name is not empty
    if (!newBrand.trim()) {
      setBrandError("Brand name cannot be empty");
      return;
    }

    // Validate brand name format (Alphabets, digits, space, Hyphens (-), Ampersand (&), Underscore (_), Apostrophe ('), Period (.))
    const brandNameRegex = /^[A-Za-z0-9 \-&_'\.]+$/;
    if (!brandNameRegex.test(newBrand.trim())) {
      setBrandError(
        "Brand name can only contain alphabets, digits, spaces, hyphens (-), ampersands (&), underscores (_), apostrophes ('), and periods (.)"
      );
      return;
    }

    // Check if this brand already exists in the list to avoid duplicates
    let brandExists = false;

    // Safety check: Make sure brands is an array
    if (Array.isArray(brands)) {
      // Check each brand item for a match
      for (const brand of brands) {
        if (typeof brand === "object" && brand !== null && brand.name) {
          if (brand.name.toLowerCase() === newBrand.trim().toLowerCase()) {
            brandExists = true;
            break;
          }
        } else if (typeof brand === "string") {
          if (brand.toLowerCase() === newBrand.trim().toLowerCase()) {
            brandExists = true;
            break;
          }
        }
      }
    }

    if (brandExists) {
      setBrandError("This brand already exists in the list");
      return;
    }

    try {
      // For simplicity, just use the brand name as a string (matching the format in the Select)
      const newBrandName = newBrand.trim();

      // Update the brands list
      const updatedBrands = Array.isArray(brands)
        ? [...brands, newBrandName]
        : [newBrandName];
      setBrands(updatedBrands);

      // Set the new brand as the selected value
      handleChange({
        target: {
          name: "brand",
          value: newBrandName,
        },
      });
      // Close the dialog
      handleCloseBrandDialog();
    } catch (error) {
      setBrandError("Failed to add new brand. Please try again.");
    }
  };

  // Function to validate mandatory fields before form submission
  const validateForm = () => {
    setHasValidationAttempted(true);
    // Field-to-label mapping for better error messages in UI
    const fieldLabels = {
      productCode: "Product Code",
      productDescription: "Product Description",
      productModality: "Product Modality",
      productTypeName: "Product Type",
      manufacturingFacilityType: "Manufacturing Facility Type",
      receivingPlantIDFromSponsorERP: "Receiving Plant ID From Sponsor ERP",
      cmoUOM:"cmoUOM",
      sponsorUOM:"sponsorUOM",
      // receivingStorageLocationIDsFromSponsorERP:
      //   "Receiving Storage Location IDs From Sponsor ERP",
      brand: "Brand",
      cmoPlantId: "CMO - Plant ID",
      cmoProductCode: "CMO Product Code",
      cmoProductDescription: "CMO Product Description",
      status: "Product Status",
      manufactureSupplyType: "Manufacturing Supply Type",
      // sponsorCQAApprovalReq:"QA check required",
    };

    // List of fields that are required to be filled before proceeding
    const mandatoryFields = [
      "productCode",
      "productDescription",
      "productModality",
      "productTypeName",
      "manufacturingFacilityType",
      // "manufactureSupplyType",
      "brand",
      "cmoPlantId",
      // "cmoProductCode", // Removed - now using cmoProductDetailsList array
      // "cmoProductDescription", // Removed - now using cmoProductDetailsList array
      "status",
      // "receivingStorageLocationIDsFromSponsorERP", 
      "receivingPlantIDFromSponsorERP",
      // "cmoUOM",
      "sponsorUOM",
      // "sponsorCQAApprovalReq",
    ];
    const newErrors = {};
    let isValid = true; // Validation regex
    // Updated based on requirements
    const productCodeRegex = /^[A-Za-z0-9\-_]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_)
    const productDescriptionRegex = /^[A-Za-z0-9\-_.,() ]+$/; // Alphabets, Spaces, Numbers (0-9), Hyphen(-), Point(.), Comma(,), parentheses(), colon(:)
    const cmoProductCodeRegex = /^[A-Za-z0-9\-_/ ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/), Spaces
    const cmoProductDescriptionRegex = /^[A-Za-z0-9\-_.,()&/ ]+$/; // Alphabets, Numbers (0-9), Hyphens (-), Underscore (_), slash (/), Ampersand (&), Parentheses(), Slash (/), Colon (:), Period (.)

    // Check each mandatory field for empty values
    mandatoryFields.forEach((field) => {
      // Skip validation if field is in read-only mode
      if (!shouldShowEditableField(field, readOnly, selectedProduct)) {
        return;
      }

      // if (
      //   !stepData[field] ||
      //   (typeof stepData[field] === "string" &&
      //     stepData[field].trim() === "") ||
      //   (Array.isArray(stepData[field]) && stepData[field].length === 0)
      // ) {
      //   newErrors[field] = `${fieldLabels[field]} is required`;
      //   isValid = false;
      // }

  const value = stepData[field];
  
  // Special handling for boolean fields like sponsorCQAApprovalReq
  // if (field === "sponsorCQAApprovalReq") {
  //   // For boolean fields, null and undefined are invalid, but false is valid
  //   const isEmpty = value === null || value === undefined;
  //   if (isEmpty) {
  //     newErrors[field] = `${fieldLabels[field]} is required`;
  //     isValid = false;
  //   }
  // } else {
    // For other fields, use the original validation logic
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0);

    if (isEmpty) {
      newErrors[field] = `${fieldLabels[field]} is required`;
      isValid = false;
    } else {
        // Custom validation for Product Code
        if (
          field === "productCode" &&
          !productCodeRegex.test(stepData[field])
        ) {
          newErrors[field] =
            "Product Code can only contain alphabets, numbers, hyphens (-), and underscores (_)";
          isValid = false;
        }
        // Custom validation for Product Description
        if (
          field === "productDescription" &&
          !productDescriptionRegex.test(stepData[field])
        ) {
          newErrors[field] =
            "Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), and parentheses ()";
          isValid = false;
        }
        // Custom validation for CMO Product Code
        if (
          field === "cmoProductCode" &&
          !cmoProductCodeRegex.test(stepData[field])
        ) {
          newErrors[field] =
            "CMO Product Code can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), and slashes (/)";
          isValid = false;
        }
        // Custom validation for CMO Product Description
        if (
          field === "cmoProductDescription" &&
          !cmoProductDescriptionRegex.test(stepData[field])
        ) {
          newErrors[field] =
            "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
          isValid = false;
        }
      }
  // }
    });

    // Validate CMO Product Details List
    const cmoErrors = {};
    cmoProductDetailsList.forEach((row, index) => {
      if (!row.cmoProductCode || row.cmoProductCode.trim() === "") {
        cmoErrors[`cmoProductCode-${index}`] = "CMO Product Code is required";
        isValid = false;
      } else if (!cmoProductCodeRegex.test(row.cmoProductCode)) {
        cmoErrors[`cmoProductCode-${index}`] = "CMO Product Code can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), and slashes (/)";
        isValid = false;
      }
      
      if (!row.cmoProductDescription || row.cmoProductDescription.trim() === "") {
        cmoErrors[`cmoProductDescription-${index}`] = "CMO Product Description is required";
        isValid = false;
      } else if (!cmoProductDescriptionRegex.test(row.cmoProductDescription)) {
        cmoErrors[`cmoProductDescription-${index}`] = "CMO Product Description can only contain alphabets, numbers, spaces, hyphens (-), underscores (_), periods (.), commas (,), parentheses (), ampersands (&), and slashes (/)";
        isValid = false;
      }
      
      if (!row.cmoUOM || row.cmoUOM.trim() === "") {
        cmoErrors[`cmoUOM-${index}`] = "CMO UOM is required";
        isValid = false;
      }
    });
    setCmoProductErrors(cmoErrors);

    // Validate Material Details (Sponsor) List
    const materialErrors = {};
    materialDetailsList.forEach((row, index) => {
      // Validate Material Brand
      if (!row.material_brand || row.material_brand.trim() === "") {
        materialErrors[`material_brand-${index}`] = "Material Brand is required";
        isValid = false;
      }
      
      // Validate Material Type
      if (!row.materialTypeName || row.materialTypeName.trim() === "") {
        materialErrors[`materialTypeName-${index}`] = "Material Type is required";
        isValid = false;
      }
      
      // Validate Material Code
      if (!row.bo_material_code || row.bo_material_code.trim() === "") {
        materialErrors[`bo_material_code-${index}`] = "Material Code is required";
        isValid = false;
      }
      
      // Validate Material Description
      if (!row.material_description || row.material_description.trim() === "") {
        materialErrors[`material_description-${index}`] = "Material Description is required";
        isValid = false;
      }
      
      // Validate Material UOM
      if (!row.bo_material_UOM || row.bo_material_UOM.trim() === "") {
        materialErrors[`bo_material_UOM-${index}`] = "Material UOM is required";
        isValid = false;
      }
      
      // Validate Material Plant ID From Sponsor ERP
      if (!row.materialPlantIDFromSponsorERP || row.materialPlantIDFromSponsorERP.trim() === "") {
        materialErrors[`materialPlantIDFromSponsorERP-${index}`] = "Material Plant ID From Sponsor ERP is required";
        isValid = false;
      }
      
      // Material Storage Location IDs From Sponsor ERP - No longer mandatory
    });
    setMaterialDetailsErrors(materialErrors);

    // Validate Material Details (CMO) List
    const cmoMaterialErrors = {};
    
    // Get list of valid CMO Product Codes
    const validCmoProductCodes = cmoProductDetailsList
      .filter(product => product.cmoProductCode && product.cmoProductCode.trim() !== "")
      .map(product => product.cmoProductCode);
    
    cmoMaterialDetailsList.forEach((row, index) => {
      // Check if this row has any data (not completely empty)
      const hasAnyData = row.cmo_product_code?.trim() || 
                         row.bo_material_code?.trim() || 
                         row.cmo_material_code?.trim() || 
                         row.cmo_material_description?.trim() || 
                         row.cmo_material_UOM?.trim();
      
      // Skip validation for completely empty rows
      if (!hasAnyData) {
        return;
      }
      
      const isLinkedRow = !isAdditionalCmoRow(index, materialDetailsList.length);
      
      // Validate CMO Product Code - mandatory for all CMO material rows with data
      if (!row.cmo_product_code || row.cmo_product_code.trim() === "") {
        cmoMaterialErrors[`cmo_product_code-${index}`] = "CMO Product Code is required";
        isValid = false;
      } else if (!validCmoProductCodes.includes(row.cmo_product_code)) {
        // Check if the CMO Product Code exists in the valid list
        cmoMaterialErrors[`cmo_product_code-${index}`] = "This CMO Product Code no longer exists. Please select a valid one.";
        isValid = false;
      }
      
      // Validate Sponsor Material Code (bo_material_code)
      // Mandatory for all rows that have any CMO data
      if (!row.bo_material_code || row.bo_material_code.trim() === "") {
        if (isLinkedRow) {
          cmoMaterialErrors[`bo_material_code-${index}`] = "Sponsor Material Code is required for linked materials";
        } else {
          cmoMaterialErrors[`bo_material_code-${index}`] = "Sponsor Material Code is required";
        }
        isValid = false;
      } else if (isAdditionalCmoRow(index, materialDetailsList.length)) {
        // For additional CMO rows, validate that the sponsor material code exists
        if (!isSponsorMaterialCodeValid(row.bo_material_code, materialDetailsList)) {
          cmoMaterialErrors[`bo_material_code-${index}`] = ERROR_MESSAGES.SPONSOR_MATERIAL_CODE_DELETED;
          isValid = false;
        }
      }
      
      // Validate CMO Material Code
      if (!row.cmo_material_code || row.cmo_material_code.trim() === "") {
        cmoMaterialErrors[`cmo_material_code-${index}`] = "CMO Material Code is required";
        isValid = false;
      }
      
      // Validate CMO Material Description
      if (!row.cmo_material_description || row.cmo_material_description.trim() === "") {
        cmoMaterialErrors[`cmo_material_description-${index}`] = "CMO Material Description is required";
        isValid = false;
      }
      
      // Validate CMO Material UOM
      if (!row.cmo_material_UOM || row.cmo_material_UOM.trim() === "") {
        cmoMaterialErrors[`cmo_material_UOM-${index}`] = "CMO Material UOM is required";
        isValid = false;
      }
    });
    setCmoMaterialDetailsErrors(cmoMaterialErrors);

    // Update the errors state with any validation errors found
    setErrors(newErrors);
    return isValid;
  };


  // Custom next button handler with validation
  const handleNextWithValidation = async (forceSkipReplicate = false) => {
    // Force immediate sync of material details before validation
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }
    
    // Sync current material details to formData immediately
    const mergedList = materialDetailsList.map((sponsorItem, idx) => {
    
      return {
        ...sponsorItem,
        cmo_product_code: cmoMaterialDetailsList[idx]?.cmo_product_code || "",
        cmo_material_code: cmoMaterialDetailsList[idx]?.cmo_material_code || "",
        cmo_material_description: cmoMaterialDetailsList[idx]?.cmo_material_description || "",
        cmo_material_UOM: cmoMaterialDetailsList[idx]?.cmo_material_UOM || "",
        is_material_active: 0, // Active materials
        _materialId: sponsorItem._materialId, // Preserve unique ID
        _isNewMaterial: sponsorItem._isNewMaterial // Preserve new material flag
      };
    });
    
    const additionalCmoRows = cmoMaterialDetailsList.slice(materialDetailsList.length)
      .filter(cmoRow => {
        return cmoRow.bo_material_code?.trim() || 
               cmoRow.material_description?.trim() || 
               cmoRow.cmo_product_code?.trim() ||
               cmoRow.cmo_material_code?.trim() || 
               cmoRow.cmo_material_description?.trim() || 
               cmoRow.cmo_material_UOM?.trim();
      })
      .map(cmoRow => ({
        _materialId: cmoRow._materialId || cmoRow.id, // Preserve unique ID
        _isNewMaterial: cmoRow._isNewMaterial, // Preserve new material flag
        _isExistingMaterial: cmoRow._isExistingMaterial, // Preserve existing material flag
        _isSponsorLinked: false, // CMO-only material, not linked to sponsor table
        sponsorSupplyingMaterial: false,
        material_brand: "",
        materialTypeName: null, // Set to null for payload (API requirement)
        material_type_id: cmoRow.material_type_id !== undefined ? cmoRow.material_type_id : null,
        bo_material_code: cmoRow.bo_material_code || "",
        material_description: cmoRow.material_description || "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: cmoRow.cmo_product_code || "",
        cmo_material_code: cmoRow.cmo_material_code || "",
        cmo_material_description: cmoRow.cmo_material_description || "",
        cmo_material_UOM: cmoRow.cmo_material_UOM || "",
        is_material_active: 0
      }));
    
    // Combine all materials and deduplicate in a single pass
    // Only include deletedMaterials and materialsWithChangedCodes if they have actual data
    const validDeletedMaterials = (deletedMaterials && deletedMaterials.length > 0) ? deletedMaterials : [];
    const validChangedCodeMaterials = (materialsWithChangedCodes && materialsWithChangedCodes.length > 0) ? materialsWithChangedCodes : [];
    
    const allMaterials = [...mergedList, ...additionalCmoRows, ...validDeletedMaterials, ...validChangedCodeMaterials];
    const uniqueMaterialsMap = new Map();
    
    allMaterials.forEach(material => {
      // Ensure material has _materialId
      ensureMaterialId(material);
      
      // Use material codes as unique key (material_type_id is NOT unique - it's the type name)
      // Build key based on material type and codes
      const keyParts = [
        material.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
        material.bo_material_code || '',
        material.cmo_material_code || ''
      ].filter(Boolean);
      const baseKey = keyParts.join('|');
      
      // Check if we already have an active version of this material
      const activeKey = `${baseKey}_active`;
      const deletedKey = `${baseKey}_deleted`;
      
      // If this is an active material (is_material_active === 0)
      if (material.is_material_active === 0) {
        // Remove any deleted version if it exists (check all possible deleted keys)
        // Check both sponsor and cmo variants with same material codes
        const boCode = material.bo_material_code || '';
        const cmoCode = material.cmo_material_code || '';
        
        // Remove all deleted versions with same material codes (regardless of sponsor/cmo flag)
        const keysToDelete = [];
        uniqueMaterialsMap.forEach((value, key) => {
          if (key.endsWith('_deleted')) {
            const keyBoCode = value.bo_material_code || '';
            const keyCmoCode = value.cmo_material_code || '';
            // Match if both codes are the same
            if (keyBoCode === boCode && keyCmoCode === cmoCode) {
              keysToDelete.push(key);
            }
          }
        });
        keysToDelete.forEach(key => uniqueMaterialsMap.delete(key));
        
        // Add or update the active version
        if (!uniqueMaterialsMap.has(activeKey)) {
          uniqueMaterialsMap.set(activeKey, material);
        }
      } else {
        // This is a deleted material (is_material_active === 1)
        // Skip if it has empty CMO fields and is sponsor material - likely a duplicate tracking
        const hasEmptyCmoFields = !material.cmo_material_code?.trim() && 
                                  !material.cmo_material_description?.trim() && 
                                  !material.cmo_material_UOM?.trim();
        
        if (material.sponsorSupplyingMaterial && hasEmptyCmoFields && material.bo_material_code) {
          // Check if there's already a deleted version with CMO details
          let hasDeletedWithCmoDetails = false;
          
          uniqueMaterialsMap.forEach((value, key) => {
            if (key.startsWith(`sponsor|${material.bo_material_code}|`) && 
                key.endsWith('_deleted') && 
                value.cmo_material_code?.trim()) {
              hasDeletedWithCmoDetails = true;
            }
          });
          
          // Skip this material if there's already a deleted version with CMO details
          if (hasDeletedWithCmoDetails) {
            return;
          }
        }
        
        // Check if there's an active version with same material codes (regardless of sponsor/cmo flag)
        const boCode = material.bo_material_code || '';
        const cmoCode = material.cmo_material_code || '';
        let hasActiveVersion = false;
        
        uniqueMaterialsMap.forEach((value, key) => {
          if (key.endsWith('_active')) {
            const keyBoCode = value.bo_material_code || '';
            const keyCmoCode = value.cmo_material_code || '';
            // Match if both codes are the same
            if (keyBoCode === boCode && keyCmoCode === cmoCode) {
              hasActiveVersion = true;
            }
          }
        });
        
        // Only add deleted material if there's NO active version with same codes
        if (!hasActiveVersion) {
          if (!uniqueMaterialsMap.has(deletedKey)) {
            uniqueMaterialsMap.set(deletedKey, material);
          } else {
            // If duplicate deleted exists, prefer one WITH complete data
            const existing = uniqueMaterialsMap.get(deletedKey);
            const existingHasTypeId = existing.material_type_id !== undefined && existing.material_type_id !== null && existing.material_type_id !== '';
            const materialHasTypeId = material.material_type_id !== undefined && material.material_type_id !== null && material.material_type_id !== '';
            const existingHasCmoCode = existing.cmo_material_code?.trim();
            const materialHasCmoCode = material.cmo_material_code?.trim();
            
            // Prefer material with CMO code, or if both/neither have it, prefer one with type ID
            if ((materialHasCmoCode && !existingHasCmoCode) || 
                (materialHasCmoCode === existingHasCmoCode && !existingHasTypeId && materialHasTypeId)) {
              uniqueMaterialsMap.set(deletedKey, material);
            }
          }
        }
      }
    });
    
    const completeList = Array.from(uniqueMaterialsMap.values())
      .map(material => {
        const { _trackingId, _materialId, id, _isNewMaterial, ...cleanMaterial } = material;
        
        // Explicitly ensure material_type_id is present (even if null)
        // This is required for the API payload structure
        if (!('material_type_id' in cleanMaterial)) {
          cleanMaterial.material_type_id = null;
        }
        
        return cleanMaterial;
      });
    
    if (typeof updateStepData === 'function') {
      updateStepData('materialDetails', {
        productMaterialsList: completeList
      });
    }
    
    // Store the latest material details for use in payload construction
    // This ensures we use the most recent data instead of potentially stale formData
    const latestMaterialDetails = {
      productMaterialsList: completeList
    };
    
    const isValid = validateForm();

    if (isValid) {
      let callFetchProductsOfSameConfig = false;

      // Check for the file uploaded product: materialDetails has products AND productFGStatuses is empty
      const isFileUploadedProduct = checkFileUploadedProduct(formData);

      if (forceSkipReplicate) {
        callFetchProductsOfSameConfig = false;
      } else if (isFileUploadedProduct) {
        // File uploaded product: always trigger replica flow for existing product with materials but no FG statuses
        callFetchProductsOfSameConfig = true;
      } else if (editPendingStatus) {
        callFetchProductsOfSameConfig = false;
      } else if (selectedCollabrixID && !isInitialSaveSuccess) {
        callFetchProductsOfSameConfig = true;
      } else if (addNewProductConfig && !isInitialSaveSuccess) {
        callFetchProductsOfSameConfig = true;
      }
      // else if (addNewProductConfig && !productAddedOnce) {
      //    callFetchProductsOfSameConfig = true;

      // }
      else {
        callFetchProductsOfSameConfig = false;
      }

      // If addNewProductConfig is true, NOT skipping, and product has not been added once, call handleFetchProductsOfSameConfig
      if (callFetchProductsOfSameConfig) {
        await handleFetchProductsOfSameConfig();
        return;
      }

      // If forceSkipReplicate is true, proceed directly to next step
      // if (forceSkipReplicate ) {
      try {
        setIsLoading((prev) => ({ ...prev, savingData: true }));
        let response;

        // Helper to omit cmoPlantId from productDetails
        const omitCmoPlantId = (obj) => {
          const { cmoPlantId, ...rest } = obj || {};
          return rest;
        };

        // Helper to format receivingStorageLocationIDsFromSponsorERP as comma-separated string
        const formatPayloadData = (data) => {
          const formattedData = { ...data };
          if (
            formattedData.receivingStorageLocationIDsFromSponsorERP &&
            Array.isArray(
              formattedData.receivingStorageLocationIDsFromSponsorERP
            )
          ) {
            formattedData.receivingStorageLocationIDsFromSponsorERP =
              formattedData.receivingStorageLocationIDsFromSponsorERP.join(",");
          }
          return formattedData;
        };

        // Check for the file uploaded product again for API selection
        const isFileUploadedProduct = checkFileUploadedProduct(formData);

        // Helper function to ensure all materials have cmo_product_code
        const ensureCmoProductCode = (materialDetails) => {
          if (!materialDetails || !materialDetails.productMaterialsList) {
            return materialDetails;
          }
          
          const defaultProductCode = stepData.cmoProductDetailsList?.[0]?.cmoProductCode || 
                                    cmoProductDetailsList?.[0]?.cmoProductCode || "";
          
          return {
            ...materialDetails,
            productMaterialsList: materialDetails.productMaterialsList.map(item => ({
              ...item,
              cmo_product_code: (item.cmo_product_code && item.cmo_product_code.trim() !== "") 
                ? item.cmo_product_code 
                : defaultProductCode
            }))
          };
        };

        // Always compare against the original value captured on mount
          const originalCMOValue = initialCMORef.current;
          const currentCMOValue = stepData.vendorMosymphonyID;
          const hasCMOChanged = originalCMOValue !== currentCMOValue;

          // Check if any other fields have changed
          const hasOtherFieldsChanged = Object.keys(stepData).some((key) => {
            // Skip comparing certain fields that might be different but don't matter
            const fieldsToIgnore = ["collabrixProductID", "cmoPlantId", "replicatedFromProductID"];
            if (fieldsToIgnore.includes(key)) return false;
            
            // Only compare fields that exist in formData.productDetails
            if (!formData.productDetails || !(key in formData.productDetails)) return false;
            
            // Get both values for comparison
            const formValue = formData.productDetails[key];
            const stepValue = stepData[key];
            
            // Handle null/undefined comparison
            if (formValue === null && stepValue === undefined) return false;
            if (formValue === undefined && stepValue === null) return false;
            
            // Compare the values
            const hasChanged = formValue !== stepValue;
            if (hasChanged) {
              // console.log(`Field ${key} changed:`, { formValue, stepValue });
            }
            return hasChanged;
          });
          
          // Check if material details have changed
          const hasMaterialDetailsChanged = (() => {
            const currentMaterials = formData?.materialDetails?.productMaterialsList || [];
            const originalMaterials = initialMaterialDetailsRef.current?.productMaterialsList || [];
            
            // Compare lengths
            if (currentMaterials.length !== originalMaterials.length) {
              return true;
            }
            
            // Compare each material's properties
            const hasChanges = currentMaterials.some((currentMaterial, index) => {
              const originalMaterial = originalMaterials[index];
              if (!originalMaterial) return true;
              
              // Compare key fields
              const fieldsToCompare = [
                'material_brand', 'materialTypeName', 'bo_material_code', 
                'material_description', 'bo_material_UOM', 
                'materialPlantIDFromSponsorERP', 'materialStorageLocationIDsFromSponsorERP',
                'cmo_product_code', 'cmo_material_code', 'cmo_material_description', 'cmo_material_UOM',
                'sponsorSupplyingMaterial'
              ];
              
              return fieldsToCompare.some(field => {
                const currentValue = currentMaterial[field];
                const originalValue = originalMaterial[field];
                
                // Handle null/undefined/empty string comparison
                const currentNormalized = currentValue ?? '';
                const originalNormalized = originalValue ?? '';
                
                if (currentNormalized !== originalNormalized) {
                  return true;
                }
                return false;
              });
            });
            
            return hasChanges;
          })();
   

          const selectionForNewProduct = sessionStorage.getItem('selectionForNewProduct');

        // Check if we already have a collabrixProductID (returning to this step) OR is file uploaded product
        if (isInitialSaveSuccess || editPendingStatus || (isFileUploadedProduct && !selectionForNewProduct)) {
          

          // Only proceed with API call if there are actual changes OR is file uploaded product
          if (hasCMOChanged || hasOtherFieldsChanged || hasMaterialDetailsChanged || (isFileUploadedProduct)) {
            // Use the latest material details from sync instead of formData (which may be stale)
            // Ensure all materials have cmo_product_code before building payload
            let materialDetailsWithDefaults = ensureCmoProductCode(latestMaterialDetails);
            
            // Include deleted materials and apply deduplication
            if (materialDetailsWithDefaults) {
              const currentMaterials = materialDetailsWithDefaults.productMaterialsList || [];
              
              // Only include deletedMaterials and materialsWithChangedCodes if they actually have data
              const validDeletedMaterials = (deletedMaterials && deletedMaterials.length > 0) ? deletedMaterials : [];
              const validChangedCodeMaterials = (materialsWithChangedCodes && materialsWithChangedCodes.length > 0) ? materialsWithChangedCodes : [];
              
              const allMaterialsForPayload = [...currentMaterials, ...validDeletedMaterials, ...validChangedCodeMaterials];
              
              // Deduplicate materials using the same logic as in other places
              const uniqueMaterialsMap = new Map();
              
              allMaterialsForPayload.forEach(material => {
                // Determine unique key using material codes (bo_material_code + cmo_material_code)
                // material_type_id is NOT unique - it represents material TYPE, not individual material
                const keyParts = [
                  material.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
                  material.bo_material_code || '',
                  material.cmo_material_code || ''
                ].filter(Boolean);
                const baseKey = keyParts.join('|');
                
                // Check if we already have an active version of this material
                const activeKey = `${baseKey}_active`;
                const deletedKey = `${baseKey}_deleted`;
                
                // If this is an active material (is_material_active === 0)
                if (material.is_material_active === 0) {
                  // Remove any deleted version if it exists (check all possible deleted keys)
                  // Check both sponsor and cmo variants with same material codes
                  const boCode = material.bo_material_code || '';
                  const cmoCode = material.cmo_material_code || '';
                  
                  // Remove all deleted versions with same material codes (regardless of sponsor/cmo flag)
                  const keysToDelete = [];
                  uniqueMaterialsMap.forEach((value, key) => {
                    if (key.endsWith('_deleted')) {
                      const keyBoCode = value.bo_material_code || '';
                      const keyCmoCode = value.cmo_material_code || '';
                      // Match if both codes are the same
                      if (keyBoCode === boCode && keyCmoCode === cmoCode) {
                        keysToDelete.push(key);
                      }
                    }
                  });
                  keysToDelete.forEach(key => uniqueMaterialsMap.delete(key));
                  
                  // Add or update the active version
                  if (!uniqueMaterialsMap.has(activeKey)) {
                    uniqueMaterialsMap.set(activeKey, material);
                  }
                } else {
                  // This is a deleted material (is_material_active === 1)
                  // Skip if it has empty CMO fields and is sponsor material - likely a duplicate tracking
                  const hasEmptyCmoFields = !material.cmo_material_code?.trim() && 
                                            !material.cmo_material_description?.trim() && 
                                            !material.cmo_material_UOM?.trim();
                  
                  if (material.sponsorSupplyingMaterial && hasEmptyCmoFields && material.bo_material_code) {
                    // Check if there's already a deleted version with CMO details
                    let hasDeletedWithCmoDetails = false;
                    
                    uniqueMaterialsMap.forEach((value, key) => {
                      if (key.startsWith(`sponsor|${material.bo_material_code}|`) && 
                          key.endsWith('_deleted') && 
                          value.cmo_material_code?.trim()) {
                        hasDeletedWithCmoDetails = true;
                      }
                    });
                    
                    // Skip this material if there's already a deleted version with CMO details
                    if (hasDeletedWithCmoDetails) {
                      return;
                    }
                  }
                  
                  // Check if there's an active version with same material codes (regardless of sponsor/cmo flag)
                  const boCode = material.bo_material_code || '';
                  const cmoCode = material.cmo_material_code || '';
                  let hasActiveVersion = false;
                  
                  uniqueMaterialsMap.forEach((value, key) => {
                    if (key.endsWith('_active')) {
                      const keyBoCode = value.bo_material_code || '';
                      const keyCmoCode = value.cmo_material_code || '';
                      // Match if both codes are the same
                      if (keyBoCode === boCode && keyCmoCode === cmoCode) {
                        hasActiveVersion = true;
                      }
                    }
                  });
                  
                  // Only add deleted material if there's NO active version with same codes
                  if (!hasActiveVersion) {
                    if (!uniqueMaterialsMap.has(deletedKey)) {
                      uniqueMaterialsMap.set(deletedKey, material);
                    } else {
                      // If duplicate deleted exists, prefer one WITH complete data
                      const existing = uniqueMaterialsMap.get(deletedKey);
                      const existingHasTypeId = existing.material_type_id !== undefined && existing.material_type_id !== null && existing.material_type_id !== '';
                      const materialHasTypeId = material.material_type_id !== undefined && material.material_type_id !== null && material.material_type_id !== '';
                      const existingHasCmoCode = existing.cmo_material_code?.trim();
                      const materialHasCmoCode = material.cmo_material_code?.trim();
                      
                      // Prefer material with CMO code, or if both/neither have it, prefer one with type ID
                      if ((materialHasCmoCode && !existingHasCmoCode) || 
                          (materialHasCmoCode === existingHasCmoCode && !existingHasTypeId && materialHasTypeId)) {
                        uniqueMaterialsMap.set(deletedKey, material);
                      }
                    }
                  }
                }
              });
              
              // Clean up materials - remove internal tracking fields
              const deduplicatedMaterials = Array.from(uniqueMaterialsMap.values())
                .map(material => {
                  const { _trackingId, _materialId, id, _isNewMaterial, _isSponsorLinked, _isExistingMaterial, ...cleanMaterial } = material;
                  
                  // Set materialTypeName to null for payload (API requirement)
                  cleanMaterial.materialTypeName = null;
                  
                  // Explicitly ensure material_type_id is present (even if null)
                  if (!('material_type_id' in cleanMaterial)) {
                    cleanMaterial.material_type_id = null;
                  }
                  
                  return cleanMaterial;
                });
              
              materialDetailsWithDefaults = {
                ...materialDetailsWithDefaults,
                productMaterialsList: deduplicatedMaterials
              };
            }
            
            // Prepare payload, omitting cmoPlantId and formatting receivingStorageLocationIDsFromSponsorERP
            const payload = {
              updateProductDetailsFlag: true,
              productDetails: {
                ...formatPayloadData(omitCmoPlantId(stepData)),
                drugTypeID: stepData.drugTypeID,
                drugTypeName: stepData.productModality,
                // cmoProductCode: stepData.cmoProductCode, // OLD - COMMENTED OUT
                // cmoProductDescription: stepData.cmoProductDescription, // OLD - COMMENTED OUT
                cmoProductDetailsList: stepData.cmoProductDetailsList || [], // NEW FORMAT
              },
              // Always include materialDetails when updateProductDetailsFlag is true
              materialDetails: (materialDetailsWithDefaults && materialDetailsWithDefaults.productMaterialsList && materialDetailsWithDefaults.productMaterialsList.length > 0) ? materialDetailsWithDefaults : null,
              processFlowConfiguration: {
                sponsorCQAApprovalReq: stepData.sponsorCQAApprovalReq === true ? true : false, // true if selected YES, false if NO
              },
            };

            // Only add these parameters if hasCMOChanged is true and they have data
            if (hasCMOChanged) {
              if (
                updateModeData?.productFGStatuses &&
                Object.keys(updateModeData.productFGStatuses).length > 0
              ) {
                payload.productFGStatuses = updateModeData.productFGStatuses;
              } else {
                payload.productFGStatuses = {
                  fgStatusesJSON: {},
                };
              }
              if (
                updateModeData?.documentDetailsList &&
                updateModeData.documentDetailsList.length > 0
              ) {
                payload.documentDetailsList =
                  updateModeData.documentDetailsList;
              } else {
                payload.documentDetailsList = [];
              }
              if (
                updateModeData?.allocationConfiguration &&
                Object.keys(updateModeData.allocationConfiguration).length > 0
              ) {
                payload.allocationConfiguration =
                  updateModeData.allocationConfiguration;
              } else {
                payload.allocationConfiguration = {
                  allowLotAllocationBy: null,
                  allowOverAllocation: null,
                  holdTimeBtnCreatedAndSWCMO: null,
                  cmoTriggerEnd: null,
                  allowMultipleLotAllocation: false,
                  sponsorTriggerStart: null,
                  cmoTriggerStart: null,
                  sponsorTriggerStartValue: null,
                  cmoTriggerEndValue: null,
                  sponsorTriggerEndValue: null,
                  defaultAllocationReq: false,
                  cmoTriggerStartValue: null,
                  showExpiredLots: false,
                  qtyVarianceAcceptanceLimit: 0,
                  sponsorTriggerEnd: null,
                  overAllocationLimit: null,
                };
              }
              if (
                updateModeData?.processFlowConfiguration &&
                Object.keys(updateModeData.processFlowConfiguration).length > 0
              ) {
                payload.processFlowConfiguration =
                  updateModeData.processFlowConfiguration;
              } else {
                payload.processFlowConfiguration = {
                  sponsorCQAApprovalReq: false,
                  stockCheckReq: null,
                };
              }
              if (
                updateModeData?.otherConfiguration &&
                Object.keys(updateModeData.otherConfiguration).length > 0
              ) {
                payload.otherConfiguration = updateModeData.otherConfiguration;
              } else {
                payload.otherConfiguration = {
                  poAcknowledgementAvailable: null,
                  cmoReadReceipts: false,
                  mroDateFieldCalculation: 0,
                  dispatchDuration: 0,
                };
              }
              payload.oldCollabrixProductID = editPendingStatus
                ? selectedProductDetails.collabrixProductID
                : formData.productDetails?.collabrixProductID;
              payload.oldVendorMosymphonyID = originalCMOValue;
              payload.updateCMOFlag = true;
            } // Update existing product details

          

            response = await axios.post(
              `${apiBaseUrl}/updateProductDetails`,
              payload
            );

            // Store the product ID in the form data for future reference
            if (response.data && response.data?.status) {
              const updatedStepData = {
                ...stepData,
                collabrixProductID:
                  response.data?.collabrixProductID ||
                  stepData?.collabrixProductID,
              };
              setStepData(updatedStepData);
              updateStepData("productDetails", updatedStepData);
              // Also update the processFlowConfiguration in parent formData
              updateStepData("processFlowConfiguration", {
                ...formData?.processFlowConfiguration,
                sponsorCQAApprovalReq: stepData.sponsorCQAApprovalReq ?? false,
              });
              
              // Update initialMaterialDetailsRef to the new saved state for future change detection
              if (formData?.materialDetails) {
                initialMaterialDetailsRef.current = JSON.parse(JSON.stringify(formData.materialDetails));
              }
              
              // Clear deleted materials and materials with changed codes after successful save
              // They've been processed and saved, so reset tracking for future changes
              setDeletedMaterials([]);
              setMaterialsWithChangedCodes([]);
            }

            // Mark API call as successful
            if (
              typeof markApiCallSuccessful === "function" &&
              response.data &&
              response.data.status
            ) {
              markApiCallSuccessful();
              return; // Exit early since markApiCallSuccessful handles navigation
            }
          }
        } else {

          // Prepare materialDetails for ADD flow - clean up and set materialTypeName to null
          let cleanedMaterialDetails = null;
          if (latestMaterialDetails && latestMaterialDetails.productMaterialsList && latestMaterialDetails.productMaterialsList.length > 0) {
            const cleanedMaterials = latestMaterialDetails.productMaterialsList.map(material => {
              const { _trackingId, _materialId, id, _isNewMaterial, _isSponsorLinked, _isExistingMaterial, ...cleanMaterial } = material;
              
              // Set materialTypeName to null for payload (API requirement)
              cleanMaterial.materialTypeName = null;
              
              // Explicitly ensure material_type_id is present (even if null)
              if (!('material_type_id' in cleanMaterial)) {
                cleanMaterial.material_type_id = null;
              }
              
              return cleanMaterial;
            });
            
            cleanedMaterialDetails = {
              productMaterialsList: cleanedMaterials
            };
          }

   

          response = await axios.post(`${apiBaseUrl}/addProductDetails`, {
            productDetails: {
              ...formatPayloadData(omitCmoPlantId(stepData)),
              drugTypeID: stepData.drugTypeID,
              drugTypeName: stepData.productModality,
              pendingConfiguration: true,
              // cmoProductCode: stepData.cmoProductCode, // OLD - COMMENTED OUT
              // cmoProductDescription: stepData.cmoProductDescription, // OLD - COMMENTED OUT
              cmoProductDetailsList: stepData.cmoProductDetailsList || [], // NEW FORMAT
            },
            // Use cleaned material details with materialTypeName set to null
            materialDetails: cleanedMaterialDetails,
            processFlowConfiguration: {
              sponsorCQAApprovalReq: stepData.sponsorCQAApprovalReq ?? false, // true if selected YES, false if NO
            },
            
          }); // Store the product ID in the form data for future reference
          if (response.data && response.data.collabrixProductID) {
            const updatedStepData = {
              ...stepData,
              collabrixProductID: response.data.collabrixProductID,
            };

            sessionStorage.setItem(`addedProductDetails`, "true");
            sessionStorage.removeItem('selectionForNewProduct');
            setIsInitialSaveSuccess(true);
            setStepData(updatedStepData);
            updateStepData("productDetails", updatedStepData);
            // Also update the processFlowConfiguration in parent formData
            updateStepData("processFlowConfiguration", {
              sponsorCQAApprovalReq: stepData.sponsorCQAApprovalReq ?? false,
            });
            
            // Update initialMaterialDetailsRef to the new saved state for future change detection
            if (formData?.materialDetails) {
              initialMaterialDetailsRef.current = JSON.parse(JSON.stringify(formData.materialDetails));
            }
            
            // Clear deleted materials and materials with changed codes after successful save
            // They've been processed and saved, so reset tracking for future changes
            setDeletedMaterials([]);
            setMaterialsWithChangedCodes([]);
          }

          // Mark API call as successful
          if (
            typeof markApiCallSuccessful === "function" &&
            response.data &&
            response.data.status
          ) {
            markApiCallSuccessful();
            return; // Exit early since markApiCallSuccessful handles navigation
          }
        }
        // If API call is successful or no changes were made, proceed to next step
        handleNext();
      } catch (error) {
        console.error(error, "error-productdetails");
        // Show error notification or handle the error appropriately
        setApiErrors((prev) => ({
          ...prev,
          saveProductDetails:
            "Failed to save product details. Please try again.",
        }));
      } finally {
        // Hide loading state
        setIsLoading((prev) => ({ ...prev, savingData: false }));
      }
      // return;
      // }
    }
  };

  // Check if initial required API data is loaded  // Exclude vendorPlants loading from the main loading indicator
  const isAllDataLoaded = !Object.entries(isLoading)
    .filter(([key]) => key !== "vendorPlants") // Exclude vendorPlants loading
    .some(([_, loading]) => loading === true);

  // Add this helper function after the component declaration but before the return statement
  const shouldShowEditableField = (fieldName, readOnly, selectedProduct) => {
    // If readOnly is true, no fields should be editable
    if (readOnly) return false;

    // If we have a selectedProduct, only allow editing of specific fields
    if (selectedProduct && Object.keys(selectedProduct).length > 0) {
      const editableFields = [
        // "cmoPlantId",
        "cmoProductCode",
        "cmoProductDescription",
        "brand",
        "status",
        "receivingStorageLocationIDsFromSponsorERP",
        "receivingPlantIDFromSponsorERP",
        "cmoUOM",
        "sponsorUOM",
        "sponsorCQAApprovalReq",
      ];
      return editableFields.includes(fieldName);
    }

    // If no selectedProduct, all fields are editable
    return true;
  };

  // Handle closing snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Validation logic (read-only, no state updates)
  const validate = () => {
    
    let isValid = true;
    const errorMessages = [];
    
    if (stepData?.productCode === "" || stepData?.productCode == null) {
      isValid = false;
      errorMessages.push("Product Code is required");
    }
    if (
      stepData?.productDescription === "" ||
      stepData?.productDescription == null
    ) {
      isValid = false;
      errorMessages.push("Product Description is required");
    }
    if (stepData?.brand === "" || stepData?.brand == null) {
      isValid = false;
      errorMessages.push("Brand is required");
    }
    
    // Validate CMO Product Details List (new array-based structure) - NO STATE UPDATES
    let cmoTableValid = true;
    if (stepData?.cmoProductDetailsList && Array.isArray(stepData.cmoProductDetailsList)) {
      if (stepData.cmoProductDetailsList.length === 0) {
        cmoTableValid = false;
      } else {
        // Check if all rows are completely empty
        const allRowsEmpty = stepData.cmoProductDetailsList.every((row) => 
          (!row.cmoProductCode || row.cmoProductCode.trim() === "") &&
          (!row.cmoProductDescription || row.cmoProductDescription.trim() === "") &&
          (!row.cmoUOM || row.cmoUOM.trim() === "")
        );
        
        if (allRowsEmpty) {
          cmoTableValid = false;
        } else {
          // Check if all rows have required fields filled
          let hasIncompleteRow = false;
          stepData.cmoProductDetailsList.forEach((row, index) => {
            if (!row.cmoProductCode || row.cmoProductCode.trim() === "") {
              hasIncompleteRow = true;
              errorMessages.push(`CMO Product Code is required for row ${index + 1}`);
            }
            if (!row.cmoProductDescription || row.cmoProductDescription.trim() === "") {
              hasIncompleteRow = true;
              errorMessages.push(`CMO Product Description is required for row ${index + 1}`);
            }
            if (!row.cmoUOM || row.cmoUOM.trim() === "") {
              hasIncompleteRow = true;
              errorMessages.push(`CMO UOM is required for row ${index + 1}`);
            }
          });
          
          if (hasIncompleteRow) {
            cmoTableValid = false;
          }
        }
      }
    } else {
      cmoTableValid = false;
    }
    
    if (!cmoTableValid) {
      isValid = false;
      if (errorMessages.length === 0) {
        errorMessages.push("CMO Product Details are required");
      }
    }

    // Validate Material Details tables - just check validity, no state updates
    // Material Details (Sponsor)
    // Check both local state and formData from parent (for edit mode)
    const materialsToValidate = materialDetailsList && materialDetailsList.length > 0 
      ? materialDetailsList 
      : (formData?.materialDetails?.productMaterialsList || []);
    
    if (!materialsToValidate || materialsToValidate.length === 0) {
      isValid = false;
      errorMessages.push("At least one Sponsor Material is required");
    } else {
      const allRowsEmpty = materialsToValidate.every((row) => 
        (!row.material_brand || row.material_brand.trim() === "") &&
        (!row.bo_material_code || row.bo_material_code.trim() === "") &&
        (!row.material_description || row.material_description.trim() === "") &&
        (!row.bo_material_UOM || row.bo_material_UOM.trim() === "")
      );
      
      
      if (!allRowsEmpty) {
        // Check for incomplete rows
        materialsToValidate.forEach((row, index) => {
          const isRowEmpty = (!row.material_brand || row.material_brand.trim() === "") &&
                            (!row.bo_material_code || row.bo_material_code.trim() === "") &&
                            (!row.material_description || row.material_description.trim() === "") &&
                            (!row.bo_material_UOM || row.bo_material_UOM.trim() === "");
          
          if (!isRowEmpty) {
            if (!row.material_brand || row.material_brand.trim() === "") {
              isValid = false;
              errorMessages.push(`Sponsor Material Brand is required for material row ${index + 1}`);
            }
            if (!row.materialTypeName || row.materialTypeName.trim() === "") {
              isValid = false;
              errorMessages.push(`Sponsor Material Type is required for material row ${index + 1}`);
            }
            if (!row.bo_material_code || row.bo_material_code.trim() === "") {
              isValid = false;
              errorMessages.push(`Sponsor Material Code is required for material row ${index + 1}`);
            }
            if (!row.material_description || row.material_description.trim() === "") {
              isValid = false;
              errorMessages.push(`Sponsor Material Description is required for material row ${index + 1}`);
            }
            if (!row.bo_material_UOM || row.bo_material_UOM.trim() === "") {
              isValid = false;
              errorMessages.push(`Sponsor Material UOM is required for material row ${index + 1}`);
            }
            if (!row.materialPlantIDFromSponsorERP || row.materialPlantIDFromSponsorERP.trim() === "") {
              isValid = false;
              errorMessages.push(`Material Plant ID From Sponsor ERP is required for material row ${index + 1}`);
            }
            // Material Storage Location IDs From Sponsor ERP - No longer mandatory
          }
        });
      } else {
        isValid = false;
        errorMessages.push("At least one Sponsor Material is required");
      }
    }
    
    // Material Details (CMO) - validate linked rows
    if (cmoMaterialDetailsList && materialDetailsList) {
     
      // Get list of valid CMO Product Codes
      const validCmoProductCodes = cmoProductDetailsList
        .filter(product => product.cmoProductCode && product.cmoProductCode.trim() !== "")
        .map(product => product.cmoProductCode);
      
      for (let index = 0; index < materialDetailsList.length; index++) {
        const row = cmoMaterialDetailsList[index];
      
        
        if (row) {
          // Check all required fields including Sponsor Material Code
          if (!row.bo_material_code || row.bo_material_code.trim() === "") {
            isValid = false;
            errorMessages.push(`Sponsor Material Code is required for CMO material row ${index + 1}`);
          }
          
          // Validate CMO Product Code exists in the valid list
          if (!row.cmo_product_code || row.cmo_product_code.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Product Code is required for CMO material row ${index + 1}`);
          } else if (!validCmoProductCodes.includes(row.cmo_product_code)) {
            isValid = false;
            errorMessages.push(`CMO Product Code "${row.cmo_product_code}" in row ${index + 1} does not exist. Please select a valid CMO Product Code.`);
          }
          
          if (!row.cmo_material_code || row.cmo_material_code.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Material Code is required for CMO material row ${index + 1}`);
          }
          if (!row.cmo_material_description || row.cmo_material_description.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Material Description is required for CMO material row ${index + 1}`);
          }
          if (!row.cmo_material_UOM || row.cmo_material_UOM.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Material UOM is required for CMO material row ${index + 1}`);
          }
        }
      }
      
      // Validate additional CMO rows if they have data
      for (let index = materialDetailsList.length; index < cmoMaterialDetailsList.length; index++) {
        const row = cmoMaterialDetailsList[index];
       
        
        const isRowEmpty = (!row.bo_material_code || row.bo_material_code.trim() === "") &&
                          (!row.material_description || row.material_description.trim() === "") &&
                          (!row.cmo_material_code || row.cmo_material_code.trim() === "") &&
                          (!row.cmo_material_description || row.cmo_material_description.trim() === "") &&
                          (!row.cmo_material_UOM || row.cmo_material_UOM.trim() === "");
        
        
        if (!isRowEmpty) {
          if (!row.bo_material_code || row.bo_material_code.trim() === "") {
            isValid = false;
            errorMessages.push(`Sponsor Material Code is required for additional row ${index + 1}`);
          } else {
            // Check if the sponsor material code exists in materialDetailsList
            if (!isSponsorMaterialCodeValid(row.bo_material_code, materialDetailsList)) {
              isValid = false;
              errorMessages.push(`Sponsor Material Code "${row.bo_material_code}" in additional row ${index + 1} does not exist. Please select a valid Sponsor Material Code.`);
            }
          }
          if (!row.material_description || row.material_description.trim() === "") {
            isValid = false;
            errorMessages.push(`Sponsor Material Description is required for additional row ${index + 1}`);
          }
          
          // Validate CMO Product Code exists in the valid list for additional rows
          if (!row.cmo_product_code || row.cmo_product_code.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Product Code is required for additional row ${index + 1}`);
          } else if (!validCmoProductCodes.includes(row.cmo_product_code)) {
            isValid = false;
            errorMessages.push(`CMO Product Code "${row.cmo_product_code}" in additional row ${index + 1} does not exist. Please select a valid CMO Product Code.`);
          }
          
          if (!row.cmo_material_code || row.cmo_material_code.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Material Code is required for additional row ${index + 1}`);
          }
          if (!row.cmo_material_description || row.cmo_material_description.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Material Description is required for additional row ${index + 1}`);
          }
          if (!row.cmo_material_UOM || row.cmo_material_UOM.trim() === "") {
            isValid = false;
            errorMessages.push(`CMO Material UOM is required for additional row ${index + 1}`);
          }
        }
      }
      
    }

    // Check for inline validation errors in material tables
    const hasMaterialDetailsErrors = Object.keys(materialDetailsErrors).length > 0;
    const hasCmoMaterialDetailsErrors = Object.keys(cmoMaterialDetailsErrors).length > 0;
    
    if (hasMaterialDetailsErrors) {
      isValid = false;
      // Add inline error messages to the error list
      Object.values(materialDetailsErrors).forEach(error => {
        if (error && !errorMessages.includes(error)) {
          errorMessages.push(error);
        }
      });
    }
    
    if (hasCmoMaterialDetailsErrors) {
      isValid = false;
      // Add inline error messages to the error list
      Object.values(cmoMaterialDetailsErrors).forEach(error => {
        if (error && !errorMessages.includes(error)) {
          errorMessages.push(error);
        }
      });
    }

    // Call onValidationError callback if provided
    if (onValidationError && typeof onValidationError === 'function') {
      onValidationError(errorMessages);
    }

    // ...add more as needed...
    return isValid;
  };

  // Update handleFetchProductsOfSameConfig to parse options
  const handleFetchProductsOfSameConfig = async () => {
    setIsReplicateLoading(true);
    setOpenReplicateDialog(true);
    try {
      const payload = {
        productType: stepData.productTypeID,
        vendorMosymphonyID: stepData.vendorMosymphonyID,
      };
      const response = await axios.post(
        `${apiBaseUrl}/fetchProductsOfSameConfig`,
        payload
      );
      setReplicateConfigResponse(response.data); // Parse options for radio, store the whole group object
      const options =
        response.data?.data?.sameConfigProducts?.map((group) => ({
          ...group,
          label:
            group.productsList.length <= 2
              ? group.productsList
                  .map((p) => `${p.productCode} / ${p.productDescription}`)
                  .join(", ")
              : group.productsList
                  .slice(0, 2)
                  .map((p) => `${p.productCode} / ${p.productDescription}`)
                  .join(", ") + ", ...",
          collabrixProductID: group.productsList[0]?.collabrixProductID || null,
        })) || [];
      setReplicateOptions(options);
      if (options.length === 0) {
        // No replicate options, proceed with normal add flow
        setOpenReplicateDialog(false);
        setIsReplicateLoading(false);
        handleNextWithValidation(true);
        return;
      }

      setSelectedReplicateOption(null);
      setSelectedReplicateValue("");
    } catch (error) {
      setReplicateConfigResponse({ error: "Failed to fetch products." });
    } finally {
      setIsReplicateLoading(false);
    }
  };

  const handleReplicateProductConfigurations = async () => {
    if (selectedReplicateValue && selectedReplicateValue !== "none") {
      // Call fetchProductDetails API for replicating configurations
      try {
        setIsReplicateLoading(true);
        const response = await axios.get(
          `${apiBaseUrl}/fetchProductDetails/${selectedReplicateOption.replicatedFromProductID}`
        );
        if (response.data?.status) {
          const replicatedData = response.data.data;
          
          const originalCollabrixProductID = formData?.productDetails?.collabrixProductID || stepData.collabrixProductID;
          
          const updatedProductDetails = {
            ...stepData,
            collabrixProductID: originalCollabrixProductID, // Keep original product ID
            replicatedFromProductID: selectedReplicateOption.replicatedFromProductID // Set replica source
          };
          
          updateStepData("productDetails", updatedProductDetails);
          updateStepData(
            "otherConfiguration",
            replicatedData.otherConfiguration || {}
          );
          updateStepData(
            "processFlowConfiguration",
            replicatedData.processFlowConfiguration || {}
          );
          updateStepData(
            "shipmentConfiguration",
            replicatedData.shipmentConfiguration || {}
          );
          updateStepData(
            "documentDeliverables",
            replicatedData.documentDetailsList || []
          );
          updateStepData(
            "fgStatuses",
            replicatedData.productFGStatuses &&
              replicatedData.productFGStatuses.fgStatusesJSON
              ? replicatedData.productFGStatuses.fgStatusesJSON
              : {}
          );
          
          // Preserve current product's material details instead of using replicated product's materials
          // The material details entered in the Product Details step should be carried forward
          if (formData?.materialDetails) {
            // Keep the material details from the current product form
            updateStepData("materialDetails", formData.materialDetails);
          }
          
          // Replicate allocation configuration with proper status name to ID conversion
          if (replicatedData.allocationConfiguration) {
            const poStatuses = replicatedData?.productFGStatuses?.fgStatusesJSON?.poStatuses || [];
            const convertedAllocationConfig = convertAllocationConfigTriggerValues(
              replicatedData.allocationConfiguration,
              poStatuses
            );
            updateStepData("allocationConfiguration", convertedAllocationConfig);
          }
          
          // Set replicatedFromProductID in sessionStorage for review step conditional rendering
          if (updatedProductDetails.replicatedFromProductID) {
            sessionStorage.setItem(
              "selectedReplicatedFromProductID",
              updatedProductDetails.replicatedFromProductID
            );
          } else {
            sessionStorage.removeItem("selectedReplicatedFromProductID");
          }
          if (
            typeof handleNext === "function" &&
            typeof stepsLength === "number"
          ) {
            handleNext(stepsLength - 1); // Go to last step (review)
          }
        }
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to fetch product details.",
          severity: "error",
          key: Date.now(),
        });
      } finally {
        setIsReplicateLoading(false);
      }
      setOpenReplicateDialog(false);
    } else if (selectedReplicateValue === "none") {
      
      const cleanProductDetails = {
        ...stepData,
      };
      updateStepData("productDetails", cleanProductDetails);
      
      updateStepData("otherConfiguration", null);
      updateStepData("processFlowConfiguration", null);
      updateStepData("shipmentConfiguration", null);
      updateStepData("documentDeliverables", null);
      updateStepData("fgStatuses", null);
      updateStepData("allocationConfiguration", null);
      
      sessionStorage.removeItem("selectedReplicatedFromProductID");
      sessionStorage.removeItem("originalCollabrixProductID")
      
      setOpenReplicateDialog(false);
      handleNextWithValidation(true);
    } else {
      setOpenReplicateDialog(false);
    }
  };

  return (
    <div className={classes.formContainer}>
      {/* Full Page Loader */}
      {!isAllDataLoaded && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}

      <form>
        <Typography variant="h6" className={classes?.formTitle}>
          Product Details
        </Typography>
        <Grid className="two-col-forms-grid" container spacing={3}>
          {/* Product Code */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productCode">
                Product Code{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "productCode",
                readOnly,
                selectedProduct
              ) ? (
                <TextField
                  id="productCode"
                  value={stepData?.productCode || ""}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const { name, value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      const trimmedValue = value.trim();
                      const syntheticEvent = {
                        target: {
                          name: name,
                          value: trimmedValue,
                        },
                      };
                      handleChange(syntheticEvent);
                    }
                  }}
                  name="productCode"
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={readOnly}
                  InputProps={{
                    readOnly: readOnly,
                    style: commonInlineStyles.input,
                  }}
                  sx={inputFieldStyles}
                  inputProps={commonInlineStyles.inputProps}
                  style={commonInlineStyles.input}
                  error={Boolean(errors.productCode)}
                  helperText={errors.productCode}
                />
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.productCode || "N/A"
                    : stepData?.productCode || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Product Description */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productDescription">
                Product Description{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "productDescription",
                readOnly,
                selectedProduct
              ) ? (
                <TextField
                  id="productDescription"
                  value={stepData?.productDescription || ""}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const { name, value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      const trimmedValue = value.trim();
                      const syntheticEvent = {
                        target: {
                          name: name,
                          value: trimmedValue,
                        },
                      };
                      handleChange(syntheticEvent);
                    }
                  }}
                  name="productDescription"
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  disabled={readOnly}
                  InputProps={{
                    readOnly: readOnly,
                    style: commonInlineStyles.input,
                  }}
                  sx={inputFieldStyles}
                  inputProps={commonInlineStyles.inputProps}
                  style={commonInlineStyles.input}
                  error={Boolean(errors.productDescription)}
                  helperText={errors.productDescription}
                />
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.productDescription || "N/A"
                    : stepData?.productDescription || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Product Modality */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productModality">
                Product Modality{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "productModality",
                readOnly,
                selectedProduct
              ) ? (
                <>
                  <Select
                    id="productModality"
                    value={stepData?.productModality || ""}
                    onChange={handleChange}
                    name="productModality"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.productModalities}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.productModality)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return isLoading.productModalities
                          ? "Loading..."
                          : "Select from the list";
                      }
                      return selected;
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {isLoading.productModalities ? (
                      <MenuItem value="" disabled>
                        Loading...
                      </MenuItem>
                    ) : productModalities && productModalities.length > 0 ? (
                      productModalities.map((modality) => (
                        <MenuItem
                          key={modality.drugTypeID}
                          value={modality.drugTypeName}
                        >
                          {modality.drugTypeName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="no_data" disabled>
                        No modalities available
                      </MenuItem>
                    )}
                  </Select>
                  {errors.productModality && (
                    <FormHelperText error>
                      {errors.productModality}
                    </FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {formData?.productDetails?.drugTypeName ||
                    stepData?.productModality ||
                    "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Product Type */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="productTypeName">
                Product Type{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "productTypeName",
                readOnly,
                selectedProduct
              ) ? (
                <>
                  <Select
                    id="productTypeName"
                    value={stepData?.productTypeName || ""}
                    onChange={handleChange}
                    name="productTypeName"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.productTypes}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.productTypeName)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return isLoading.productTypes
                          ? "Loading..."
                          : "Select from the list";
                      }
                      return selected;
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {isLoading.productTypes ? (
                      <MenuItem value="" disabled>
                        Loading...
                      </MenuItem>
                    ) : productTypes && productTypes.length > 0 ? (
                      productTypes.map((type) => (
                        <MenuItem
                          key={type.productTypeID}
                          value={type.productTypeName}
                        >
                          {type.productTypeName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="no_data" disabled>
                        No product types available
                      </MenuItem>
                    )}
                  </Select>
                  {errors.productTypeName && (
                    <FormHelperText error>
                      {errors.productTypeName}
                    </FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {formData?.productDetails?.productTypeName ||
                    stepData?.productTypeName ||
                    "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Manufacturing Facility Type */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="manufacturingFacilityType">
                Manufacturing Facility Type{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "manufacturingFacilityType",
                readOnly,
                selectedProduct
              ) ? (
                <>
                  <Select
                    id="manufacturingFacilityType"
                    value={stepData?.manufacturingFacilityType || ""}
                    onChange={handleChange}
                    name="manufacturingFacilityType"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.facilityTypes}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.manufacturingFacilityType)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return isLoading.facilityTypes
                          ? "Loading..."
                          : "Select from the list";
                      }
                      return selected;
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {isLoading.facilityTypes ? (
                      <MenuItem value="" disabled>
                        Loading...
                      </MenuItem>
                    ) : facilityTypes && facilityTypes.length > 0 ? (
                      facilityTypes.map((type) => (
                        <MenuItem
                          key={type.facilityTypeID}
                          value={type.facilityTypeName}
                        >
                          {type.facilityTypeName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="no_data" disabled>
                        No facility types available
                      </MenuItem>
                    )}
                  </Select>
                  {errors.manufacturingFacilityType && (
                    <FormHelperText error>
                      {errors.manufacturingFacilityType}
                    </FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {formData?.productDetails?.facilityTypeName ||
                    stepData?.manufacturingFacilityType ||
                    "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Manufacturing Supply Type */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="manufactureSupplyType">
                Manufacturing Supply Type{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              <Typography>
                {formData.productDetails?.supplyTypeName ||
                  selectedProductType.supplyTypeName ||
                  "N/A"}
              </Typography>
            </FormControl>
          </Grid>
          {/* CMO Plant ID */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                component="label"
                htmlFor="cmoPlantId"
                className={classes.label}
              >
                CMO Name{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "cmoPlantId",
                readOnly,
                selectedProduct
              ) ? (
                <>
                  <Select
                    id="cmoPlantId"
                    name="cmoPlantId"
                    value={
                      stepData?.vendorMosymphonyID &&
                      stepData?.vendorName
                     ? `${stepData?.vendorMosymphonyID}|${stepData?.vendorName}|${stepData?.vendorPlant || ""}`
                        : ""
                    }
                    onChange={(e) => {
                      const [vendorMosymphonyID, vendorName, vendorPlant] =
                        e.target.value.split("|");
                      setStepData((prev) => ({
                        ...prev,
                        vendorMosymphonyID: Number(vendorMosymphonyID),
                        vendorName,
                        vendorPlant: vendorPlant || "",
                        cmoPlantId: e.target.value,
                      }));
                      updateStepData("productDetails", {
                        ...formData.productDetails,
                        vendorMosymphonyID: Number(vendorMosymphonyID),
                        vendorName,
                        vendorPlant: vendorPlant || "",
                        cmoPlantId: e.target.value,
                      });
                      setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        if (e.target.value) {
                          delete newErrors.cmoPlantId;
                        } else {
                          newErrors.cmoPlantId = "CMO is required";
                        }
                        return newErrors;
                      });
                    }}
                    variant="outlined"
                    displayEmpty
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.cmoPlantId)}
                    disabled={
                      readOnly 
                      ||
                      isLoading.vendorPlants ||
                      vendorPlants.length === 0
                    }
                    renderValue={(selected) => {
                      if (!selected) return "Select from the list";
                      const [vendorMosymphonyID, vendorName, vendorPlant] =
                        selected.split("|");
                      return `${vendorName}`;
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {isLoading.vendorPlants ? (
                      <MenuItem value="" disabled>
                        Loading vendor plants...
                      </MenuItem>
                    ) : vendorPlants.length > 0 ? (
                      vendorPlants
                        .filter((plant) => plant?.vendorMosymphonyID)
                        .map((plant) => (
                          <MenuItem
                            key={`${plant.vendorMosymphonyID}|${plant.vendorName}|${plant.vendorPlant}`}
                            value={`${plant.vendorMosymphonyID}|${plant.vendorName}|${plant.vendorPlant || ""}`}
                          >
                            {`${plant.vendorName}`}
                          </MenuItem>
                        ))
                    ) : (
                      <MenuItem value="" disabled>
                        No vendor plants available
                      </MenuItem>
                    )}
                  </Select>
                  {errors.cmoPlantId && (
                    <FormHelperText error>{errors.cmoPlantId}</FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? `${formData?.productDetails?.vendorName}`
                    : `${stepData?.vendorName}`}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* CMO Product Code - OLD IMPLEMENTATION - COMMENTED OUT */}
          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                component="label"
                htmlFor="cmoProductCode"
                className={classes.label}
              >
                CMO Product Code{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "cmoProductCode",
                readOnly,
                selectedProduct
              ) ? (
                <>
                  <TextField
                    id="cmoProductCode"
                    name="cmoProductCode"
                    value={stepData?.cmoProductCode || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const { name, value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        const trimmedValue = value.trim();
                        const syntheticEvent = {
                          target: {
                            name: name,
                            value: trimmedValue,
                          },
                        };
                        handleChange(syntheticEvent);
                      }
                    }}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.cmoProductCode)}
                    helperText={errors.cmoProductCode}
                  />
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.cmoProductCode || "N/A"
                    : stepData.cmoProductCode || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid> */}
          {/* CMO Product Description - OLD IMPLEMENTATION - COMMENTED OUT */}
          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                component="label"
                htmlFor="cmoProductDescription"
                className={classes.label}
              >
                CMO Product Description{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField(
                "cmoProductDescription",
                readOnly,
                selectedProduct
              ) ? (
                <>
                  <TextField
                    id="cmoProductDescription"
                    name="cmoProductDescription"
                    value={stepData?.cmoProductDescription || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const { name, value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        const trimmedValue = value.trim();
                        const syntheticEvent = {
                          target: {
                            name: name,
                            value: trimmedValue,
                          },
                        };
                        handleChange(syntheticEvent);
                      }
                    }}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.cmoProductDescription)}
                    helperText={errors.cmoProductDescription}
                  />
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.cmoProductDescription || "N/A"
                    : stepData.cmoProductDescription || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid> */}
          {/* CMO UOM - OLD IMPLEMENTATION - MOVED TO TABLE BELOW */}
          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                component="label"
                htmlFor="cmoUOM"
                className={classes.label}
              >
                CMO UOM {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("cmoUOM", readOnly, selectedProduct) ? (
                <>
                  <TextField
                    id="cmoUOM"
                    name="cmoUOM"
                    value={stepData?.cmoUOM || ""}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors?.cmoUOM)}
                  />
                  {errors.cmoUOM && (
                    <FormHelperText error>{errors.cmoUOM}</FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.cmoUOM || "N/A"
                    : stepData?.cmoUOM || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid> */}
          {/* Brand */}
          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="brand">
                Brand {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("brand", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="brand"
                    value={stepData?.brand || ""}
                    onChange={handleChange}
                    name="brand"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly || isLoading.brands}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.brand)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return isLoading.brands
                          ? "Loading..."
                          : "Select from the list";
                      }
                      return selected;
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {isLoading.brands ? (
                      <MenuItem value="" disabled>
                        Loading...
                      </MenuItem>
                    ) : brands && brands.length > 0 ? (
                      brands.map((brand, index) => {
                        // Handle both object and string formats
                        const brandValue =
                          typeof brand === "object" && brand !== null
                            ? brand.id || brand.name || ""
                            : brand;
                        const brandDisplay =
                          typeof brand === "object" && brand !== null
                            ? brand.name || brand.id || ""
                            : brand;

                        return (
                          <MenuItem key={index} value={brandValue}>
                            {brandDisplay}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem value="no_data" disabled>
                        No brands available
                      </MenuItem>
                    )}
                  </Select>
                  {errors.brand && (
                    <FormHelperText error>{errors.brand}</FormHelperText>
                  )}
                  <Box mt={1} className="line-height-initial">
                    <Typography
                      style={{ display: "inline-block" }}
                      className="fw-500 f-13px line-height-initial"
                    >
                      Brand not in the list?
                    </Typography>
                    <Button
                      onClick={handleOpenBrandDialog}
                      color="primary"
                      variant="text"
                      size="small"
                      className="fw-400 add-new-brand-btn line-height-initial"
                    >
                      Click here to add new brand
                    </Button>
                  </Box>
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.brand || "N/A"
                    : stepData.brand || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* UOM */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                component="label"
                htmlFor="sponsorUOM"
                className={classes.label}
              >
                UOM {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("sponsorUOM", readOnly, selectedProduct) ? (
                <>
                  <TextField
                    id="sponsorUOM"
                    name="sponsorUOM"
                    value={stepData?.sponsorUOM || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const { name, value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        const trimmedValue = value.trim();
                        const syntheticEvent = {
                          target: {
                            name: name,
                            value: trimmedValue,
                          },
                        };
                        handleChange(syntheticEvent);
                      }
                    }}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors?.sponsorUOM)}
                    // helperText={errors?.sponsorUOM}
                  />
                  {errors.sponsorUOM && (
                    <FormHelperText error>{errors.sponsorUOM}</FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.sponsorUOM || "N/A"
                    : stepData?.sponsorUOM || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* CMO UOM - OLD IMPLEMENTATION - COMMENTED OUT */}
          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                component="label"
                htmlFor="cmoUOM"
                className={classes.label}
              >
                CMO UOM {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("cmoUOM", readOnly, selectedProduct) ? (
                <>
                  <TextField
                    id="cmoUOM"
                    name="cmoUOM"
                    value={stepData?.cmoUOM || ""}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    style={commonInlineStyles.input}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors?.cmoUOM)}
                  />
                  {errors.cmoUOM && (
                    <FormHelperText error>{errors.cmoUOM}</FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.cmoUOM || "N/A"
                    : stepData?.cmoUOM || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid> */}

          {/* Product Status */}
          <Grid
            item
            xs={12}
            md={6}
            className={!readOnly ? "status-select-field" : ""}
          >
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="status">
                Status {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("status", readOnly, selectedProduct) ? (
                <>
                  <Select
                    id="status"
                    value={stepData?.status || ""}
                    onChange={handleChange}
                    name="status"
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                    fullWidth
                    disabled={readOnly}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.status)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return "Select from the list";
                      }
                      return selected;
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {/* <MenuItem value="" disabled>
                      Select Product Status
                    </MenuItem> */}
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                  {errors.status && (
                    <FormHelperText error>{errors.status}</FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {readOnly
                    ? formData?.productDetails?.status || "N/A"
                    : stepData.status || "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Plant ID */}

          {/* <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="manufactureSupplyType">
                Receiving Plant ID From Sponsor ERP{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              <Typography>
                {formData.productDetails?.receivingPlantIDFromSponsorERP ||
                  stepData?.receivingPlantIDFromSponsorERP || "N/A"}
              </Typography>
            </FormControl>
          </Grid> */}

          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography
                component="label"
                htmlFor="receivingPlantIDFromSponsorERP"
              >
                Receiving Plant ID From Sponsor ERP{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>

              {shouldShowEditableField(
                "receivingPlantIDFromSponsorERP",
                readOnly,
                selectedProductDetails
              ) ? (
                <>
                  <Select
                    id="receivingPlantIDFromSponsorERP"
                    name="receivingPlantIDFromSponsorERP"
                    value={stepData?.receivingPlantIDFromSponsorERP || ""}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    displayEmpty
                    className={classes.select}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    error={Boolean(errors.receivingPlantIDFromSponsorERP)}
                    disabled={
                      !(
                        formData?.productDetails?.vendorMosymphonyID ||
                        stepData?.vendorMosymphonyID
                      ) || receivingPlantIDs.length === 0
                    }
                    renderValue={(selected) =>
                      !selected
                        ? !(
                            formData?.productDetails?.vendorMosymphonyID ||
                            stepData?.vendorMosymphonyID
                          )
                          ? "Please select CMO - Plant ID first"
                          : "Select from the list"
                        : selected
                    }
                  >
                    {receivingPlantIDs.map((plantID, index) => (
                      <MenuItem
                        key={`plantID-${index}-${JSON.stringify(plantID)}`}
                        value={plantID}
                      >
                        {plantID}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.receivingPlantIDFromSponsorERP && (
                    <FormHelperText error>
                      {errors.receivingPlantIDFromSponsorERP}
                    </FormHelperText>
                  )}
                </>
              ) : (
                <Typography>
                  {formData.productDetails?.receivingPlantIDFromSponsorERP ||
                    stepData?.receivingPlantIDFromSponsorERP ||
                    "N/A"}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* storageLocationID */}
          {/* <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography component="label" htmlFor="manufactureSupplyType">
                Receiving Storage Location IDs From Sponsor ERP{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              <Typography>
                {formData.productDetails?.receivingStorageLocationIDsFromSponsorERP ||
                  stepData?.receivingStorageLocationIDsFromSponsorERP || "N/A"}
              </Typography>
            </FormControl>
          </Grid> */}

          <Grid item xs={6}>
            <FormControl className={classes.formField}>
              <Typography
                component="label"
                htmlFor="receivingStorageLocationIDsFromSponsorERP"
              >
                Receiving Storage Location IDs From Sponsor ERP{" "}
                {/* {!readOnly && <span style={{ color: "red" }}>*</span>} */}
              </Typography>

              {shouldShowEditableField(
                "receivingStorageLocationIDsFromSponsorERP",
                readOnly,
                selectedProductDetails
              ) ? (
                <>
                  <Select
                    id="receivingStorageLocationIDsFromSponsorERP"
                    multiple
                    name="receivingStorageLocationIDsFromSponsorERP"
                    value={(() => {
                      const storedValue =
                        stepData?.receivingStorageLocationIDsFromSponsorERP;
                      if (typeof storedValue === "string" && storedValue) {
                        return storedValue
                          .split(",")
                          .map((item) => item.trim())
                          .filter((item) => item !== "");
                      }
                      return Array.isArray(storedValue) ? storedValue : [];
                    })()}
                    onChange={handleChange}
                    variant="outlined"
                    renderValue={(selected) => {
                      if (
                        !(
                          formData?.productDetails?.vendorMosymphonyID ||
                          stepData?.vendorMosymphonyID
                        )
                      ) {
                        return "Please select CMO - Plant ID first";
                      }
                      // Handle both string and array cases
                      if (typeof selected === "string") {
                        return selected || "";
                      }
                      return Array.isArray(selected) && selected.length > 0
                        ? selected.join(", ")
                        : "";
                    }}
                    className={classes.select}
                    style={commonInlineStyles.select}
                    inputProps={commonInlineStyles.inputProps}
                    displayEmpty
                    disabled={
                      !(
                        formData?.productDetails?.vendorMosymphonyID ||
                        stepData?.vendorMosymphonyID
                      ) || receivingStorageLocationIDs.length === 0
                    }
                  >
                    {receivingStorageLocationIDs.map((locationID, index) => (
                      <MenuItem
                        key={`locationID-${index}-${JSON.stringify(
                          locationID
                        )}`}
                        value={locationID}
                      >
                        <Checkbox
                          checked={(() => {
                            const storedValue =
                              stepData?.receivingStorageLocationIDsFromSponsorERP;
                            if (
                              typeof storedValue === "string" &&
                              storedValue
                            ) {
                              const valuesArray = storedValue
                                .split(",")
                                .map((item) => item.trim());
                              return valuesArray.includes(locationID);
                            }
                            return (
                              Array.isArray(storedValue) &&
                              storedValue.includes(locationID)
                            );
                          })()}
                        />
                        <ListItemText primary={locationID} />
                      </MenuItem>
                    ))}
                  </Select>
                  {/* {errors.receivingStorageLocationIDsFromSponsorERP && (
                    <FormHelperText error>
                      {errors.receivingStorageLocationIDsFromSponsorERP}
                    </FormHelperText>
                  )} */}
                </>
              ) : (
                <Typography>
                  {(() => {
                    const value =
                      formData.productDetails
                        ?.receivingStorageLocationIDsFromSponsorERP ||
                      stepData?.receivingStorageLocationIDsFromSponsorERP;
                    if (typeof value === "string" && value) {
                      return value; // Already a comma-separated string
                    }
                    if (Array.isArray(value) && value.length > 0) {
                      return value.join(", ");
                    }
                    return "N/A";
                  })()}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* <Grid item xs={6}>
  <FormControl className={classes.formField}>
    <Typography component="label" htmlFor="QACheckRequired">
     {" "}
      {!readOnly && <span style={{ color: "red" }}>*</span>}
    </Typography>

    {shouldShowEditableField("QACheckRequired", readOnly, selectedProductDetails) ? (
      <>
        <Select
          id="QACheckRequired"
          name="QACheckRequired"
          value={stepData?.QACheckRequired || ""}
          onChange={handleChange}
          fullWidth
          variant="outlined"
          displayEmpty
          className={classes.select}
          style={commonInlineStyles.select}
          inputProps={commonInlineStyles.inputProps}
          error={Boolean(errors.QACheckRequired)}
          // disabled={!(formData?.productDetails?.vendorMosymphonyID || stepData?.vendorMosymphonyID) || receivingPlantIDs.length === 0}
          renderValue={"Select from the list"}
        >
        </Select>
        {errors.QACheckRequired && (
          <FormHelperText error>
            {errors.QACheckRequired}
          </FormHelperText>
        )}
      </>
    ) : (
      <Typography>
        {formData.productDetails?.QACheckRequired ? "Yes" : "No"}
      </Typography>
    )}
  </FormControl>
</Grid> */}

{/* <Grid item xs={12} md={6}>
  <FormControl className={classes.formField}>
    <Typography component="label">
      QA check required{" "}
      {!readOnly && <span style={{ color: "red" }}>*</span>}
    </Typography>

    {shouldShowEditableField("sponsorCQAApprovalReq", readOnly, selectedProduct) ? (
      <>
        <Select
          key={`qa-select-${stepData?.sponsorCQAApprovalReq}`}
          value={
            stepData?.sponsorCQAApprovalReq !== undefined && stepData?.sponsorCQAApprovalReq !== null
              ? stepData?.sponsorCQAApprovalReq
              : (formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? "")
          }
          onChange={handleChange}
          name="sponsorCQAApprovalReq"
          variant="outlined"
          displayEmpty
          className={classes.select}
          style={commonInlineStyles.select}
          inputProps={commonInlineStyles.inputProps}
          error={Boolean(errors.sponsorCQAApprovalReq)}
          renderValue={(selected) => {
            if (selected === "") {
              return "Select from the list";
            }
            return selected === true ? "YES" : "NO";
          }}
        >
          <MenuItem value={true}>YES</MenuItem>
          <MenuItem value={false}>NO</MenuItem>
        </Select>
        {errors.sponsorCQAApprovalReq && (
          <FormHelperText error>{errors.sponsorCQAApprovalReq}</FormHelperText>
        )}
      </>
    ) : (
      <Typography>
        {formData?.processFlowConfiguration?.sponsorCQAApprovalReq === true ||
        stepData?.sponsorCQAApprovalReq === true
          ? "YES"
          : "NO"}
      </Typography>
    )}
  </FormControl>
</Grid> */}

          {/* <Grid item xs={12} md={6}>
            <FormControl className={classes.formField}>
              <Typography component="label"
              //  className={classes.label}
                // htmlFor="receivingStorageLocationIDsFromSponsorERP"
               >
                QA check required{" "}
                {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {shouldShowEditableField("QACheckReq", readOnly, selectedProduct)
              // !readOnly
               ? (
                <Select
                  value={stepData?.QACheckReq}
                  onChange={handleChange}
                  name="QACheckReq"
                  variant="outlined"
                  className={classes.select}
                  style={commonInlineStyles.select}
                  inputProps={commonInlineStyles.inputProps}
                >
                  <MenuItem value={true}>YES</MenuItem>
                  <MenuItem value={false}>NO</MenuItem>
                </Select>
              ) : (
                <Typography>
                  {(formData?.processFlowConfiguration?.sponsorCQAApprovalReq
                    ? "YES"
                    : "NO" )|| (stepData?.QACheckReq
                    ? "YES"
                    : "NO")}
                </Typography>
              )}
            </FormControl>
          </Grid> */}
        </Grid>

         <Divider style={{ margin: "60px 0 24px 0" }} />
        <Typography variant="h6">Product Details (CMO)</Typography>
        <TableContainer
          component={Paper}
          style={{
            border: "1px solid #E0E0E0",
            borderRadius: 8,
            marginBottom: 16,
            boxShadow: "none",
          }}
        >
          <Table size="small" style={{ width: "100%" }}>
            <TableHead>
              <TableRow style={{ background: "#F4F6F8" }}>
                {[
                  {
                    key: "cmo-product-code",
                    content: "CMO Product Code",
                    className: "cmo-product-code-col",
                    width: readOnly ? "30%" : "25%",
                  },
                  {
                    key: "cmo-product-description",
                    content: "CMO Product Description",
                    className: "cmo-product-description-col",
                    width: readOnly ? "50%" : "40%",
                  },
                  {
                    key: "cmo-product-uom",
                    content: "CMO Product UOM",
                    className: "cmo-product-uom-col",
                    width: readOnly ? "20%" : "20%",
                  },
                  {
                    key: "action",
                    content: "Action",
                    className: "cmo-product-action-col",
                    width: "15%",
                    hidden: readOnly,
                  },
                ]
                  .filter(header => !header.hidden)
                  .map((header) => (
                  <TableCell
                    key={header.key}
                    className={header.className}
                    style={{
                      fontWeight: 700,
                      background: "#F4F6F8",
                      borderBottom: "2px solid #E0E0E0",
                      color: "#222",
                      fontSize: 16,
                      padding: "12px 16px",
                      width: header.width,
                    }}
                  >
                    {header.content}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {readOnly ? (
                // Read-only mode - display from formData
                formData?.productDetails?.cmoProductDetailsList && formData.productDetails.cmoProductDetailsList.length > 0 ? (
                  formData.productDetails.cmoProductDetailsList.map((row, idx) => (
                    <TableRow key={`cmo-product-readonly-${idx}`}>
                      <TableCell className="cmo-product-code-col" style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.cmoProductCode || "N/A"}
                      </TableCell>
                      <TableCell className="cmo-product-description-col" style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.cmoProductDescription || "N/A"}
                      </TableCell>
                      <TableCell className="cmo-product-uom-col" style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.cmoUOM || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key="cmo-product-no-data">
                    <TableCell
                      colSpan={3}
                      style={{
                        padding: "12px 16px",
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    >
                      No CMO product details available
                    </TableCell>
                  </TableRow>
                )
              ) : (
                // Editable mode
                cmoProductDetailsList.map((row, idx) => (
                  <TableRow key={`cmo-product-edit-${idx}`}>
                    <TableCell className="cmo-product-code-col" style={{ verticalAlign: "top", padding: "8px" }}>
                      <TextField
                        value={row.cmoProductCode}
                        onChange={(e) => handleCmoProductChange(idx, 'cmoProductCode', e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleCmoProductChange(idx, 'cmoProductCode', value.trim());
                          }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter CMO Product Code"
                        error={Boolean(cmoProductErrors[`cmoProductCode-${idx}`])}
                        helperText={cmoProductErrors[`cmoProductCode-${idx}`]}
                        style={{ backgroundColor: "#fff" }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="cmo-product-description-col" style={{ verticalAlign: "top", padding: "8px" }}>
                      <TextField
                        value={row.cmoProductDescription}
                        onChange={(e) => handleCmoProductChange(idx, 'cmoProductDescription', e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleCmoProductChange(idx, 'cmoProductDescription', value.trim());
                          }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter CMO Product Description"
                        error={Boolean(cmoProductErrors[`cmoProductDescription-${idx}`])}
                        helperText={cmoProductErrors[`cmoProductDescription-${idx}`]}
                        style={{ backgroundColor: "#fff" }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="cmo-product-uom-col" style={{ verticalAlign: "top", padding: "8px" }}>
                      <TextField
                        value={row.cmoUOM}
                        onChange={(e) => handleCmoProductChange(idx, 'cmoUOM', e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleCmoProductChange(idx, 'cmoUOM', value.trim());
                          }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter UOM"
                        error={Boolean(cmoProductErrors[`cmoUOM-${idx}`])}
                        helperText={cmoProductErrors[`cmoUOM-${idx}`]}
                        style={{ backgroundColor: "#fff", height: "auto" }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="cmo-product-action-col" style={{ verticalAlign: "top", padding: "8px" }}>
                      <IconButton
                        onClick={() => handleDeleteCmoProductRow(idx)}
                        style={{ padding: "6px 0 0 0", color: "#f44336" }}
                      >
                        {/* <CloseIcon /> */}
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {cmoTableError && (
          <Typography
            color="error"
            variant="body2"
            style={{
              marginTop: readOnly ? "16px" : "0",
              marginBottom: "16px",
              fontSize: "0.875rem",
            }}
          >
            {cmoTableError}
          </Typography>
        )}
        {!readOnly && (
          <Box display="flex" alignItems="center" mt={3}>
            <Button
              onClick={handleAddCmoProductRow}
              variant="outlined"
              disabled={hasEmptyRow()}
              startIcon={<AddCircleOutlineIcon style={{ fontSize: 21 }} />}
              style={{
        
                    fontSize: "16px",
  minWidth: "162px",
  height: "40px",
  padding: "5px 12px",
  borderColor: "#000000",
  fontWeight: "600",
  borderRadius: "20px"
              }}
            >
              Add New Row
            </Button>
          </Box>
        )}
        

        <Divider style={{ margin: "60px 0 24px 0" }} />
        {/* <Typography variant="h6">Material Details</Typography> */}
        <Typography
           variant="h6"
        >
          Material Details (Sponsor)
        </Typography>
        <TableContainer
          component={Paper}
          style={{
            border: "1px solid #E0E0E0",
            borderRadius: 8,
            marginBottom: 36,
            boxShadow: "none",
            overflowX: "auto",
          }}
        >
          <Table size="small" style={{ width: "100%", minWidth: "1600px" }}>
            <TableHead>
              <TableRow style={{ background: "#F4F6F8" }}>
                {[
                  {
                    key: "sponsor-supplying-material",
                    content: (
                      <>
                        Sponsor supplying <br /> Material
                      </>
                    ),
                    width: readOnly ? "10%" : "8%",
                  },
                  {
                    key: "material-brand",
                    content: (
                      <>
                        Material <br /> Brand
                      </>
                    ),
                    width: "12%",
                  },
                  {
                    key: "material-type",
                    content: "Material Type",
                    width: "12%",
                  },
                  {
                    key: "material-code",
                    content: (
                      <>
                        Material <br /> Code
                      </>
                    ),
                    width: "12%",
                  },
                  {
                    key: "material-description",
                    content: "Material Description",
                    width: "15%",
                  },
                  {
                    key: "material-uom",
                    content: (
                      <>
                        Material <br /> UOM
                      </>
                    ),
                    width: "8%",
                  },
                  {
                    key: "material-plant-id",
                    content: (
                      <>
                        Material Plant ID <br /> From Sponsor ERP
                        {/* {!readOnly && <span style={{ color: "red" }}> *</span>} */}
                      </>
                    ),
                    width: "15%",
                  },
                  {
                    key: "material-storage-location",
                    content: (
                      <>
                        Material Storage Location <br /> ID From Sponsor ERP
                        {/* {!readOnly && <span style={{ color: "red" }}> *</span>} */}
                      </>
                    ),
                    width: "15%",
                  },
                  {
                    key: "action",
                    content: "Action",
                    width: "5%",
                    hidden: readOnly,
                  },
                ]
                  .filter(header => !header.hidden)
                  .map((header) => (
                  <TableCell
                    key={header.key}
                    style={{
                      fontWeight: 700,
                      background: "#F4F6F8",
                      borderBottom: "2px solid #E0E0E0",
                      color: "#222",
                      fontSize: 16,
                      padding: "12px 16px",
                      width: header.width,
                    }}
                  >
                    {header.content}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {readOnly ? (
                // Read-only mode - display only sponsor-linked materials (first occurrence of each bo_material_code)
                // Filters by _isSponsorLinked flag set by sortMaterials to handle duplicates correctly
                (formData?.materialDetails?.productMaterialsList &&
                formData.materialDetails.productMaterialsList.length > 0) ? (
                  formData.materialDetails.productMaterialsList
                    .filter(row => row._isSponsorLinked && row.is_material_active !== 1)
                    .map((row, idx) => (
                    <TableRow
                      key={`sponsor-material-readonly-${idx}-${
                        row?.bo_material_code || ""
                      }-${row?.material_type_id || ""}`}
                    >
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.sponsorSupplyingMaterial ? "Yes" : "No"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.material_brand || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.materialTypeName || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.bo_material_code || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.material_description || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.bo_material_UOM || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.materialPlantIDFromSponsorERP || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row?.materialStorageLocationIDsFromSponsorERP || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key="sponsor-no-data">
                    <TableCell
                      colSpan={8}
                      style={{
                        padding: "12px 16px",
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    >
                      No material details available
                    </TableCell>
                  </TableRow>
                )
              ) : (
                // Editable mode
                materialDetailsList.map((row, idx) => (
                  <TableRow key={`sponsor-material-edit-${idx}`}>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <Select
                        value={row.sponsorSupplyingMaterial ? "Yes" : "No"}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'sponsorSupplyingMaterial', e.target.value === "Yes")}
                        variant="outlined"
                        size="small"
                        fullWidth
                        style={{ backgroundColor: "#fff" }}
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <Select
                        value={row.material_brand || ""}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'material_brand', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        displayEmpty
                        error={Boolean(materialDetailsErrors[`material_brand-${idx}`])}
                        style={{ backgroundColor: "#fff", fontSize: '14px' }}
                        renderValue={(selected) => {
                          if (!selected || selected === "") {
                            return <span style={{ color: '#999' }}>Select</span>;
                          }
                          return selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Brand
                        </MenuItem>
                        {brands && brands.length > 0 && brands.map((brand, bIdx) => (
                          <MenuItem key={`brand-${bIdx}`} value={typeof brand === 'object' ? brand.name : brand}>
                            {typeof brand === 'object' ? brand.name : brand}
                          </MenuItem>
                        ))}
                        {materialBrandOptions.filter(b => !brands.some(br => (typeof br === 'object' ? br.name : br) === b)).map((brand, bIdx) => (
                          <MenuItem key={`brand-extra-${bIdx}`} value={brand}>
                            {brand}
                          </MenuItem>
                        ))}
                      </Select>
                      {materialDetailsErrors[`material_brand-${idx}`] && (
                        <Typography variant="caption" color="error" style={{ fontSize: "0.75rem", marginTop: "3px", display: "block" }}>
                          {materialDetailsErrors[`material_brand-${idx}`]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <Select
                        value={row.material_type_id || ""}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'materialTypeName', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        displayEmpty
                        error={Boolean(materialDetailsErrors[`materialTypeName-${idx}`])}
                        style={{ backgroundColor: "#fff", fontSize: '14px' }}
                        renderValue={(selected) => {
                          if (!selected || selected === "") {
                            return <span style={{ color: '#999' }}>Select</span>;
                          }
                          // Find the material type name for the selected ID
                          const selectedType = availableMaterialTypes.find(mt => mt.materialTypeID === selected);
                          return selectedType ? selectedType.materialTypeName : (row.materialTypeName || selected);
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Material Type
                        </MenuItem>
                        {availableMaterialTypes && availableMaterialTypes.length > 0 && availableMaterialTypes.map((type, tIdx) => (
                          <MenuItem key={`material-type-${type.materialTypeID}-${tIdx}`} value={type.materialTypeID}>
                            {type.materialTypeName}
                          </MenuItem>
                        ))}
                      </Select>
                      {materialDetailsErrors[`materialTypeName-${idx}`] && (
                        <Typography variant="caption" color="error" style={{ fontSize: "0.75rem", marginTop: "3px", display: "block" }}>
                          {materialDetailsErrors[`materialTypeName-${idx}`]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <TextField
                        value={row.bo_material_code || ""}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'bo_material_code', e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleMaterialDetailsChange(idx, 'bo_material_code', value.trim());
                          }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter Material Code"
                        error={Boolean(materialDetailsErrors[`bo_material_code-${idx}`])}
                        helperText={materialDetailsErrors[`bo_material_code-${idx}`]}
                        style={{ backgroundColor: "#fff" }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <TextField
                        value={row.material_description || ""}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'material_description', e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleMaterialDetailsChange(idx, 'material_description', value.trim());
                          }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter Material Description"
                        error={Boolean(materialDetailsErrors[`material_description-${idx}`])}
                        helperText={materialDetailsErrors[`material_description-${idx}`]}
                        style={{ backgroundColor: "#fff" }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <TextField
                        value={row.bo_material_UOM || ""}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'bo_material_UOM', e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleMaterialDetailsChange(idx, 'bo_material_UOM', value.trim());
                          }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter UOM"
                        error={Boolean(materialDetailsErrors[`bo_material_UOM-${idx}`])}
                        helperText={materialDetailsErrors[`bo_material_UOM-${idx}`]}
                        style={{ backgroundColor: "#fff" }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <Select
                        value={row.materialPlantIDFromSponsorERP || ""}
                        onChange={(e) => handleMaterialDetailsChange(idx, 'materialPlantIDFromSponsorERP', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        displayEmpty
                        error={Boolean(materialDetailsErrors[`materialPlantIDFromSponsorERP-${idx}`])}
                        style={{ backgroundColor: "#fff", fontSize: '14px' }}
                        renderValue={(selected) => {
                          if (!selected || selected === "") {
                            return <span style={{ color: '#999' }}>Select</span>;
                          }
                          return selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Receiving Plant ID
                        </MenuItem>
                        {receivingPlantIDs && receivingPlantIDs.length > 0 && receivingPlantIDs.map((plantID, pIdx) => (
                          <MenuItem key={`plant-id-${pIdx}`} value={plantID}>
                            {plantID}
                          </MenuItem>
                        ))}
                        {materialPlantIDOptions.filter(p => !receivingPlantIDs.includes(p)).map((plantID, pIdx) => (
                          <MenuItem key={`plant-id-extra-${pIdx}`} value={plantID}>
                            {plantID}
                          </MenuItem>
                        ))}
                      </Select>
                      {materialDetailsErrors[`materialPlantIDFromSponsorERP-${idx}`] && (
                        <Typography variant="caption" color="error" style={{ fontSize: "0.75rem", marginTop: "3px", display: "block" }}>
                          {materialDetailsErrors[`materialPlantIDFromSponsorERP-${idx}`]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <Select
                        value={(() => {
                          const val = row.materialStorageLocationIDsFromSponsorERP;
                          if (typeof val === 'string' && val) {
                            return val.split(',').map(s => s.trim()).filter(Boolean);
                          }
                          return Array.isArray(val) ? val : [];
                        })()}
                        onChange={(e) => {
                          const selectedValues = e.target.value;
                          const stringValue = Array.isArray(selectedValues) ? selectedValues.join(',') : selectedValues;
                          handleMaterialDetailsChange(idx, 'materialStorageLocationIDsFromSponsorERP', stringValue);
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiple
                        displayEmpty
                        error={Boolean(materialDetailsErrors[`materialStorageLocationIDsFromSponsorERP-${idx}`])}
                        style={{ backgroundColor: "#fff", fontSize: '14px' }}
                        renderValue={(selected) => {
                          if (!selected || selected.length === 0) {
                            return <span style={{ color: '#999' }}>Select</span>;
                          }
                          return Array.isArray(selected) ? selected.join(', ') : selected;
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 224,
                              width: 250,
                            },
                          },
                        }}
                      >
                        {receivingStorageLocationIDs && receivingStorageLocationIDs.length > 0 && receivingStorageLocationIDs.map((storageID, sIdx) => (
                          <MenuItem key={`storage-id-${sIdx}`} value={storageID}>
                            <Checkbox 
                              checked={(() => {
                                const val = row.materialStorageLocationIDsFromSponsorERP;
                                if (typeof val === 'string' && val) {
                                  return val.split(',').map(s => s.trim()).includes(storageID);
                                }
                                return Array.isArray(val) && val.includes(storageID);
                              })()}
                              style={{ padding: '4px' }}
                            />
                            <ListItemText primary={storageID} />
                          </MenuItem>
                        ))}
                        {materialStorageLocationIDOptions.filter(s => !receivingStorageLocationIDs.includes(s)).map((storageID, sIdx) => (
                          <MenuItem key={`storage-id-extra-${sIdx}`} value={storageID}>
                            <Checkbox 
                              checked={(() => {
                                const val = row.materialStorageLocationIDsFromSponsorERP;
                                if (typeof val === 'string' && val) {
                                  return val.split(',').map(s => s.trim()).includes(storageID);
                                }
                                return Array.isArray(val) && val.includes(storageID);
                              })()}
                              style={{ padding: '4px' }}
                            />
                            <ListItemText primary={storageID} />
                          </MenuItem>
                        ))}
                      </Select>
                      {materialDetailsErrors[`materialStorageLocationIDsFromSponsorERP-${idx}`] && (
                        <Typography variant="caption" color="error" style={{ fontSize: "0.75rem", marginTop: "3px", display: "block" }}>
                          {materialDetailsErrors[`materialStorageLocationIDsFromSponsorERP-${idx}`]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                      <IconButton
                        onClick={() => handleDeleteMaterialRow(idx)}
                        style={{ padding: "6px 0 0 0", color: "#f44336" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {materialTableError && (
          <Typography
            color="error"
            variant="body2"
            style={{
              marginTop: readOnly ? "16px" : "0",
              marginBottom: "16px",
              fontSize: "0.875rem",
            }}
          >
            {materialTableError}
          </Typography>
        )}
        {!readOnly && (
          <Box display="flex" alignItems="center" mt={2} mb={4}>
            <Button
              onClick={handleAddMaterialRow}
              variant="outlined"
              disabled={hasMaterialEmptyRow()}
              startIcon={<AddCircleOutlineIcon style={{ fontSize: 21 }} />}
              style={{
                fontSize: "16px",
                minWidth: "162px",
                height: "40px",
                padding: "5px 12px",
                borderColor: "#000000",
                fontWeight: "600",
                borderRadius: "20px"
              }}
            >
              Add New Row
            </Button>
          </Box>
        )}

        <Divider style={{ margin: "60px 0 24px 0" }} />


        {/* CMO Material Details Table */}
        <Typography
           variant="h6"
        >
          Material Details (CMO)
        </Typography>
        <TableContainer
          component={Paper}
          style={{
            border: "1px solid #E0E0E0",
            borderRadius: 8,
            boxShadow: "none",
          }}
        >
          <Table size="small" style={{ width: "100%" }}>
            <TableHead>
              <TableRow style={{ background: "#F4F6F8" }}>
                {[
                  {
                    key: "cmo-product-code",
                    content: "CMO Product Code",
                    width: readOnly ? "20%" : "18%",
                  },
                  {
                    key: "cmo-material-code",
                    content: "Sponsor Material Code",
                    width: readOnly ? "20%" : "18%",
                  },
                  // {
                  //   key: "cmo-material-description",
                  //   content: "Material Description",
                  //   width: readOnly ? "25%" : "22%",
                  // },
                  {
                    key: "cmo-material-code-cmo",
                    content: "CMO Material Code",
                    width: readOnly ? "20%" : "18%",
                  },
                  {
                    key: "cmo-material-description-cmo",
                    content: "CMO Material Description",
                    width: readOnly ? "25%" : "22%",
                  },
                  {
                    key: "cmo-material-uom",
                    content: "CMO Material UOM",
                    width: readOnly ? "10%" : "15%",
                  },
                  {
                    key: "action",
                    content: "Action",
                    width: "5%",
                    hidden: readOnly,
                  },
                ].filter(header => !header.hidden).map((header) => (
                  <TableCell
                    key={header.key}
                    style={{
                      fontWeight: 700,
                      background: "#F4F6F8",
                      borderBottom: "2px solid #E0E0E0",
                      color: "#222",
                      fontSize: 15,
                      padding: "12px 16px",
                      width: header.width,
                    }}
                  >
                    {header.content}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {readOnly ? (
                // Read-only mode - display from formData, excluding deleted materials
                (formData?.materialDetails?.productMaterialsList &&
                formData.materialDetails.productMaterialsList.length > 0) ? (
                  formData.materialDetails.productMaterialsList
                    .filter(row => row.is_material_active !== 1)
                    .map((row, idx) => (
                    <TableRow
                      key={`cmo-material-readonly-${idx}-${row?.bo_material_code || ""}-${
                        row?.cmo_material_code || ""
                      }`}
                    >
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {formData?.productDetails?.cmoProductDetailsList?.[0]?.cmoProductCode || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.bo_material_code || "N/A"}
                      </TableCell>
                      {/* <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.material_description || "N/A"}
                      </TableCell> */}
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.cmo_material_code || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.cmo_material_description || "N/A"}
                      </TableCell>
                      <TableCell style={{ padding: "12px 16px", fontSize: 14 }}>
                        {row.cmo_material_UOM || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key="cmo-no-data">
                    <TableCell
                      colSpan={5}
                      style={{
                        padding: "12px 16px",
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    >
                      No material details available
                    </TableCell>
                  </TableRow>
                )
              ) : (
                // Editable mode - display from cmoMaterialDetailsList
                cmoMaterialDetailsList.length > 0 ? (
                  cmoMaterialDetailsList.map((row, idx) => {
                    const isLinkedToSponsor = idx < materialDetailsList.length;
                    // Use index for linked rows (stable), use ID for additional rows (unique)
                    const rowKey = isLinkedToSponsor ? `cmo-material-linked-${idx}` : (row.id || `cmo-material-${idx}`);
                    
                  
                    
                    return (
                      <TableRow key={rowKey}>
                        <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          <Select
                            value={row.cmo_product_code || ""}
                            onChange={(e) => handleCmoMaterialDetailsChange(idx, 'cmo_product_code', e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            displayEmpty
                            error={Boolean(cmoMaterialDetailsErrors[`cmo_product_code-${idx}`])}
                            style={{ backgroundColor: "#fff", fontSize: '14px' }}
                            renderValue={(selected) => {
                              if (!selected || selected === "") {
                                return <span style={{ color: '#999' }}>Select</span>;
                              }
                              return selected;
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select CMO Product Code
                            </MenuItem>
                            {cmoProductDetailsList && cmoProductDetailsList.length > 0 && cmoProductDetailsList.map((product, pIdx) => (
                              <MenuItem key={`cmo-product-${pIdx}`} value={product.cmoProductCode}>
                                {product.cmoProductCode}
                              </MenuItem>
                            ))}
                          </Select>
                          {cmoMaterialDetailsErrors[`cmo_product_code-${idx}`] && (
                            <Typography variant="caption" color="error" style={{ fontSize: "0.75rem", marginTop: "3px", display: "block" }}>
                              {cmoMaterialDetailsErrors[`cmo_product_code-${idx}`]}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          {isLinkedToSponsor ? (
                            <TextField
                              value={row.bo_material_code || ""}
                              variant="outlined"
                              size="small"
                              fullWidth
                              disabled
                              placeholder="Sponsor Material Code"
                              style={{ backgroundColor: "#f5f5f5" }}
                              InputProps={{
                                style: {
                                  fontSize: '14px',
                                  lineHeight: '1.4',
                                  // padding: '8px 12px'
                                }
                              }}
                            />
                          ) : (
                            <Select
                              value={row.bo_material_code || ""}
                              onChange={(e) => handleCmoMaterialDetailsChange(idx, 'bo_material_code', e.target.value)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              displayEmpty
                              style={{ backgroundColor: "#fff" }}
                              error={Boolean(cmoMaterialDetailsErrors[`bo_material_code-${idx}`])}
                              renderValue={(selected) => {
                                if (!selected || selected === "") {
                                  return <span style={{ color: '#999' }}>Select Material Code</span>;
                                }
                                // Check if selected value still exists in materialDetailsList
                                const exists = materialDetailsList.some(m => m.bo_material_code === selected);
                                if (!exists) {
                                  return <span style={{ color: theme.palette.error.main }}>{selected} (Deleted)</span>;
                                }
                                return selected;
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select Material Code
                              </MenuItem>
                              {materialDetailsList
                                .filter(m => m.bo_material_code && m.bo_material_code.trim())
                                .map((material, mIdx) => (
                                  <MenuItem key={`material-opt-${mIdx}`} value={material.bo_material_code}>
                                    {material.bo_material_code}
                                  </MenuItem>
                                ))}
                            </Select>
                          )}
                          {cmoMaterialDetailsErrors[`bo_material_code-${idx}`] && (
                            <Typography variant="caption" color="error" style={{ fontSize: "0.75rem" }}>
                              {cmoMaterialDetailsErrors[`bo_material_code-${idx}`]}
                            </Typography>
                          )}
                        </TableCell>
                        {/* <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          {isLinkedToSponsor ? (
                            <TextField
                              value={row.material_description || ""}
                              variant="outlined"
                              size="small"
                              fullWidth
                              disabled
                              placeholder="Sponsor Material Description"
                              style={{ backgroundColor: "#f5f5f5" }}
                              multiline
                              minRows={1}
                              InputProps={{
                                style: {
                                  fontSize: '14px',
                                  lineHeight: '1.4',
                                  // padding: '8px 12px'
                                }
                              }}
                            />
                          ) : (
                            <TextField
                              value={row.material_description || ""}
                              onChange={(e) => handleCmoMaterialDetailsChange(idx, 'material_description', e.target.value)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              multiline
                              minRows={1}
                              placeholder="Enter Material Description"
                              style={{ backgroundColor: "#fff" }}
                              InputProps={{
                                style: {
                                  fontSize: '14px',
                                  lineHeight: '1.4',
                                  padding: '8px 12px'
                                }
                              }}
                            />
                          )}
                        </TableCell> */}
                        <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          <TextField
                            value={row.cmo_material_code || ""}
                            onChange={(e) => handleCmoMaterialDetailsChange(idx, 'cmo_material_code', e.target.value)}
                            onBlur={(e) => {
                              const { value } = e.target;
                              // Trim leading and trailing spaces for string values
                              if (typeof value === "string" && value.trim() !== value) {
                                handleCmoMaterialDetailsChange(idx, 'cmo_material_code', value.trim());
                              }
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={1}
                            placeholder="Enter CMO Material Code"
                            error={Boolean(cmoMaterialDetailsErrors[`cmo_material_code-${idx}`])}
                            helperText={cmoMaterialDetailsErrors[`cmo_material_code-${idx}`]}
                            style={{ backgroundColor: "#fff" }}
                            InputProps={{
                              style: {
                                fontSize: '14px',
                                lineHeight: '1.4',
                                padding: '8px 12px'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          <TextField
                            value={row.cmo_material_description || ""}
                            onChange={(e) => handleCmoMaterialDetailsChange(idx, 'cmo_material_description', e.target.value)}
                            onBlur={(e) => {
                              const { value } = e.target;
                              // Trim leading and trailing spaces for string values
                              if (typeof value === "string" && value.trim() !== value) {
                                handleCmoMaterialDetailsChange(idx, 'cmo_material_description', value.trim());
                              }
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={1}
                            placeholder="Enter CMO Material Description"
                            error={Boolean(cmoMaterialDetailsErrors[`cmo_material_description-${idx}`])}
                            helperText={cmoMaterialDetailsErrors[`cmo_material_description-${idx}`]}
                            style={{ backgroundColor: "#fff" }}
                            InputProps={{
                              style: {
                                fontSize: '14px',
                                lineHeight: '1.4',
                                padding: '8px 12px'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          <TextField
                            value={row.cmo_material_UOM || ""}
                            onChange={(e) => handleCmoMaterialDetailsChange(idx, 'cmo_material_UOM', e.target.value)}
                            onBlur={(e) => {
                              const { value } = e.target;
                              // Trim leading and trailing spaces for string values
                              if (typeof value === "string" && value.trim() !== value) {
                                handleCmoMaterialDetailsChange(idx, 'cmo_material_UOM', value.trim());
                              }
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={1}
                            placeholder="Enter UOM"
                            error={Boolean(cmoMaterialDetailsErrors[`cmo_material_UOM-${idx}`])}
                            helperText={cmoMaterialDetailsErrors[`cmo_material_UOM-${idx}`]}
                            style={{ backgroundColor: "#fff" }}
                            InputProps={{
                              style: {
                                fontSize: '14px',
                                lineHeight: '1.4',
                                padding: '8px 12px'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                          {!isLinkedToSponsor && (
                            <IconButton
                              onClick={() => handleDeleteCmoMaterialRow(idx)}
                              style={{ padding: "6px 0 0 0", color: "#f44336" }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow key="cmo-no-data">
                    <TableCell
                      colSpan={6}
                      style={{
                        padding: "12px 16px",
                        fontSize: 14,
                        textAlign: "center",
                      }}
                    >
                      No material details available
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!readOnly && (
          <Box display="flex" alignItems="center" mt={2} mb={4}>
            <Button
              onClick={handleAddCmoMaterialRow}
              variant="outlined"
              disabled={materialDetailsList.length === 0 || !materialDetailsList.some(m => m.bo_material_code)}
              startIcon={<AddCircleOutlineIcon style={{ fontSize: 21 }} />}
              style={{
                fontSize: "16px",
                minWidth: "162px",
                height: "40px",
                padding: "5px 12px",
                borderColor: "#000000",
                fontWeight: "600",
                borderRadius: "20px"
              }}
            >
              Add New Row
            </Button>
          </Box>
        )}

       
      </form>

      {/* Replicate Config Dialog */}
      <Dialog
        open={openReplicateDialog}
        className="replicate-product-dialog"
        onClose={() => setOpenReplicateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ textAlign: "center" }}>
          Replicate Product Configurations
        </DialogTitle>
        <DialogContent>
          {isReplicateLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : replicateOptions.length > 0 ? (
            <>
              <Typography gutterBottom>
                {`Following is the list of existing products (of the same product type) with ${
                  selectedProductDetails?.vendorName || stepData?.vendorName
                }, grouped based on the configurations.`}
              </Typography>
              <Typography gutterBottom>
                Please select a group if you want to apply the same set of
                configurations to this new product:
              </Typography>
              <RadioGroup
                value={selectedReplicateValue}
                onChange={(e) => {
                  setSelectedReplicateValue(e.target.value);
                  if (e.target.value === "none") {
                    setSelectedReplicateOption(null);
                    setStepData((prev) => ({
                      ...prev,
                      replicatedFromProductID: editPendingStatus
                        ? stepData.replicatedFromProductID
                        : null,
                      collabrixProductID: editPendingStatus
                        ? stepData.collabrixProductID
                        : null,
                    }));
                  } else {
                    const selected = replicateOptions.find(
                      (opt) =>
                        String(opt.replicatedFromProductID) === e.target.value
                    );
                    setSelectedReplicateOption(selected);
                    if (selected) {
                      setStepData((prev) => ({
                        ...prev,
                        replicatedFromProductID:
                          selected.replicatedFromProductID || null,
                        collabrixProductID: selected.collabrixProductID || null,
                      }));
                    }
                  }
                }}
              >
                {replicateOptions.map((option) => {
                  const fullProductList = option.productsList
                    .map((p) => `${p.productCode} / ${p.productDescription}`)
                    .join(", ");
                  const isTruncated = option.productsList.length > 2;

                  return (
                    <Tooltip
                      key={option.replicatedFromProductID}
                      title={isTruncated ? fullProductList : ""}
                      placement="top"
                      // disableHoverListener={!isTruncated}
                      disableHoverListener={true}
                    >
                      <FormControlLabel
                        value={String(option.replicatedFromProductID)}
                        control={<Radio className={classesLocal.orangeRadio} />}
                        label={option.label}
                        color="primary"
                      />
                    </Tooltip>
                  );
                })}
                <FormControlLabel
                  value="none"
                  control={<Radio className={classesLocal.orangeRadio} />}
                  label="None of the above (add new configurations)"
                />
              </RadioGroup>
            </>
          ) : (
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {JSON.stringify(replicateConfigResponse, null, 2)}
            </pre>
          )}
        </DialogContent>
        <DialogActions className="replicate-product-dialog-actions">
          {replicateOptions.length > 0 ? (
            <>
              {/* <Button
                onClick={handleReplicateProductConfigurations}
                color="primary"
                disabled={!selectedReplicateValue}
              >
                Continue
              </Button> */}

              <Button
                variant="contained"
                onClick={handleReplicateProductConfigurations}
                disabled={!selectedReplicateValue}
                endIcon={<ArrowForwardIosIcon style={{ fontSize: 18 }} />}
                color="primary"
                className={`${classes.button} update-changes-btn`}
              >
                Continue
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setOpenReplicateDialog(false)}
              color="primary"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          style={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add Brand Dialog */}
      <Dialog
        className="add-new-brand-popup"
        open={openBrandDialog}
        onClose={handleCloseBrandDialog}
      >
        <DialogTitle id="brand-dialog-title">Add New Brand</DialogTitle>
        <DialogContent>
          <Typography component="label" htmlFor="newBrandInput">
            Brand {!readOnly && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <TextField
            id="newBrandInput"
            value={newBrand}
            // onChange={(e) => {
            //   setNewBrand(e.target.value);
            //   // Clear error when user types
            //   if (brandError) {
            //     setBrandError("");
            //   }
            // }}
            onChange={(e) => {
              const value = e.target.value;
              setNewBrand(value);

              // Validate brand name format as user types
              const brandNameRegex = /^[A-Za-z0-9 \-&_'\.]+$/;

              if (!value.trim()) {
                setBrandError("Brand name cannot be empty");
              } else if (!brandNameRegex.test(value)) {
                setBrandError(
                  "Brand name can only contain alphabets, digits, spaces, hyphens (-), ampersands (&), underscores (_), apostrophes ('), and periods (.)"
                );
              } else {
                setBrandError("");
              }
            }}
            onBlur={(e) => {
              const { value } = e.target;
              // Trim leading and trailing spaces for string values
              if (typeof value === "string" && value.trim() !== value) {
                setNewBrand(value.trim());
              }
            }}
            variant="outlined"
            fullWidth
            autoFocus
            InputProps={{
              readOnly: readOnly,
              style: commonInlineStyles.input,
            }}
            sx={inputFieldStyles}
            inputProps={commonInlineStyles.inputProps}
            style={commonInlineStyles.input}
            error={Boolean(brandError)}
            helperText={brandError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBrandDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleAddNewBrand}
            color="primary"
            disabled={!newBrand.trim()}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {/* Navigation buttons - Only show if stepper props are provided */}
      {typeof handleNext === "function" &&
        typeof handleBack === "function" &&
        typeof activeStep === "number" &&
        typeof stepsLength === "number" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: activeStep === 0 ? "center" : "space-between",
            }}
            className="stepper-navigation-btns"
            mt={4}
          >
            {/* Back button - hide on first step */}
            {activeStep !== 0 && (
              <>
                <Button
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                >
                  Back
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
              </>
            )}
            {/* Next/Submit button - changes text based on step */}
            {!readOnly && (
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  if (activeStep === stepsLength - 1) {
                    handleSubmit();
                  } else {
                    handleNextWithValidation();
                  }
                }}
                disabled={isLoading.savingData}
                className={classes.button}
              >
                {isLoading.savingData ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Saving...
                  </>
                ) : activeStep === stepsLength - 1 ? (
                  "Save"
                ) : (
                  "Save & Next"
                )}
              </Button>
            )}
          </Box>
        )}
    </div>
  );
});

// Export with React.memo to prevent unnecessary re-renders
export default React.memo(ProductDetails);
